import { useState, useEffect, useMemo, useRef } from 'react';
import { AlertTriangle, Archive, BarChart3, CheckCircle2, ChevronDown, ChevronUp, Cloud, Copy, Eye, FileText, GripVertical, Heading1, Image, Layers3, LayoutTemplate, Minus, Palette, Pilcrow, RefreshCw, Save, Send, Server, ShieldCheck, Sparkles, Trash2, Type, UploadCloud, XCircle } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { DEFAULT_PORTAL_FONT, PORTAL_FONT_OPTIONS, fontTokensForBranding, normalizePortalFont } from '../../tokens/fonts';
import { useMspClients } from '../../hooks/useMspData';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import ColorPicker from '../../components/ui/ColorPicker';
import PageError from '../../components/ui/PageError';
import BrandLogo from '../../components/ui/BrandLogo';
import AwsOnboardingPanel from '../../components/aws/AwsOnboardingPanel';
import { ApiKeysPanel } from '../account/ApiKeys';
import { useApiClient } from '../../api/apiClient';
import { usePlatformAdmin } from '../../context/AuthContext';
import { useUpdateBranding } from '../../context/BrandingContext';
import { useUser } from '../../context/UserContext';
import { seededCloudResult } from '../../utils/mspPolish';
import {
  FALLBACK_INTEGRATION_CATALOG,
  INTEGRATION_KIND,
  buildIntegrationRows,
  formDefinitionsForKind,
  normalizeIntegrationCatalog,
  providerLabel,
  providerValue,
} from '../../utils/integrationHealth';

const TABS = ['Branding', 'Report Templates', 'Domain & SSO', 'Cloud Access', 'Integrations', 'Notifications', 'API Keys'];

// Function instead of const — re-evaluates on each render to pick up theme changes from branding
const getInputStyle = () => ({
  width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
  padding: '8px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none',
});

function isProblemPayload(payload) {
  if (!payload || Array.isArray(payload) || typeof payload !== 'object') return false;
  const status = Number(payload.status ?? payload.Status);
  const hasProblemShape = payload.type || payload.Type || payload.title || payload.Title || payload.detail || payload.Detail;
  return Boolean(hasProblemShape && Number.isFinite(status) && status >= 400);
}

/* ═══════════════════════════════════════════════════════════════
   BRANDING TAB
   ═══════════════════════════════════════════════════════════════ */
function BrandingTab() {
  const api = useApiClient();
  const isPlatformAdmin = usePlatformAdmin();
  const updateBranding = useUpdateBranding();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [saveError, setSaveError] = useState('');

  // Identity fields
  const [portalName, setPortalName] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [logoLetter, setLogoLetter] = useState('');

  // Colors
  const [primaryColor, setPrimaryColor] = useState('#1a1a2e');
  const [secondaryColor, setSecondaryColor] = useState('#e94560');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [surfaceColor, setSurfaceColor] = useState('#f8f9fb');
  const [textColor, setTextColor] = useState('#1a1a2e');
  const [fontFamily, setFontFamily] = useState(DEFAULT_PORTAL_FONT);
  const previewFonts = fontTokensForBranding(fontFamily);

  // Logo
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoStatus, setLogoStatus] = useState('');
  const fileRef = useRef(null);

  // Custom Domain
  const [customDomain, setCustomDomain] = useState('');
  const savedDomain = useRef('');

  // OIDC
  const [oidcOpen, setOidcOpen] = useState(false);
  const [oidcAuthority, setOidcAuthority] = useState('');
  const [oidcClientId, setOidcClientId] = useState('');
  const [oidcScopes, setOidcScopes] = useState('openid profile email');
  const [oidcError, setOidcError] = useState('');

  // Load branding on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get('/msp/branding');
        if (cancelled) return;
        if (data) {
          setPortalName(data.portalName || '');
          setTagline(data.tagline || '');
          setDescription(data.description || '');
          setLogoLetter(data.logoLetter || '');
          setPrimaryColor(data.primaryColor || '#1a1a2e');
          setSecondaryColor(data.secondaryColor || '#e94560');
          setBackgroundColor(data.backgroundColor || '#ffffff');
          setSurfaceColor(data.surfaceColor || '#f8f9fb');
          setTextColor(data.textColor || '#1a1a2e');
          setFontFamily(normalizePortalFont(data.fontFamily));
          setLogoUrl(data.logoUrl || '');
          setCustomDomain(data.customDomain || '');
          savedDomain.current = data.customDomain || '';
          setOidcAuthority(data.oidcAuthority || '');
          setOidcClientId(data.oidcClientId || '');
          setOidcScopes(data.oidcScopes || 'openid profile email');
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || 'Failed to load branding settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const showToast = (msg, isError) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  // Post-save verification state
  const [verified, setVerified] = useState(null);

  // Validate before saving
  const validate = () => {
    if (!portalName.trim()) return 'Portal Name is required.';
    if (primaryColor && !/^#[0-9a-fA-F]{6}$/.test(primaryColor)) return 'Primary color must be a valid hex (e.g. #ef4444).';
    if (secondaryColor && !/^#[0-9a-fA-F]{6}$/.test(secondaryColor)) return 'Secondary color must be a valid hex.';
    if (backgroundColor && !/^#[0-9a-fA-F]{6}$/.test(backgroundColor)) return 'Background color must be a valid hex.';
    if (surfaceColor && !/^#[0-9a-fA-F]{6}$/.test(surfaceColor)) return 'Surface color must be a valid hex.';
    if (textColor && !/^#[0-9a-fA-F]{6}$/.test(textColor)) return 'Text color must be a valid hex.';
    if (isPlatformAdmin && oidcAuthority && !oidcAuthority.startsWith('https://')) return 'OIDC Authority must start with https://';
    return null;
  };

  // Save branding with validation + post-save verification
  const handleSave = async () => {
    setOidcError('');
    setSaveError('');
    setVerified(null);

    const validationError = validate();
    if (validationError) {
      setSaveError(validationError);
      if (validationError.includes('OIDC')) { setOidcError(validationError); setOidcOpen(true); }
      return;
    }

    setSaving(true);
    try {
      // 1. Save appearance
      const appearancePayload = {
        portalName, tagline, description, logoLetter, logoUrl,
        primaryColor, secondaryColor, backgroundColor, surfaceColor, textColor, fontFamily,
      };
      await api.put('/msp/branding/appearance', appearancePayload);

      // 2. Save custom domain if set
      if (isPlatformAdmin && customDomain !== savedDomain.current) {
        await api.put('/msp/branding/domain', { customDomain });
        savedDomain.current = customDomain;
      }

      // 3. Save OIDC (PlatformAdmin only)
      if (isPlatformAdmin && (oidcAuthority || oidcClientId)) {
        await api.put('/msp/branding/oidc', {
          oidcAuthority, oidcClientId, oidcScopes,
        });
      }

      // 4. Post-save verification — re-fetch via authenticated endpoint
      let check;
      try {
        check = await api.get('/msp/branding');
        const matches = check &&
          check.portalName === portalName &&
          check.primaryColor === primaryColor &&
          check.secondaryColor === secondaryColor &&
          check.fontFamily === fontFamily;
        setVerified(matches ? 'pass' : 'mismatch');
      } catch {
        setVerified('unknown');
        throw new Error('Portal font could not be verified. Reload branding settings before trying again.');
      }
      if (check?.fontFamily !== fontFamily) {
        throw new Error('Portal font was not saved. Reload branding settings before trying again.');
      }

      // 5. Apply branding to context directly — do NOT refetch via the
      //    domain-based endpoint which returns different branding for
      //    the Azure Static Apps hosting domain.
      updateBranding({ ...appearancePayload, customDomain });

      showToast('Branding saved successfully.', false);
    } catch (err) {
      console.error('[BrandingSave] Error:', err);
      const msg = err.message || 'Failed to save branding.';
      setSaveError(msg);
      showToast(msg, true);
    } finally {
      setSaving(false);
    }
  };

  // Logo upload — uses apiClient so X-Act-As-Msp header is injected during impersonation
  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setLogoUploading(true);
    setLogoStatus('');
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      const data = await api.post('/msp/branding/logo', formData);
      setLogoUrl(data.logoUrl || '');
      setLogoFile(null);
      setLogoStatus('Logo uploaded successfully.');
    } catch (err) {
      console.error('[LogoUpload] Error:', err);
      setLogoStatus(err.message || 'Upload failed.');
    } finally {
      setLogoUploading(false);
    }
  };

  // Logo remove
  const handleLogoRemove = async () => {
    try {
      await api.delete('/msp/branding/logo');
      setLogoUrl('');
      setLogoStatus('Logo removed.');
    } catch (err) {
      setLogoStatus(err.message || 'Failed to remove logo.');
    }
  };

  if (loading) {
    return (
      <Card style={{ padding: 22 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Skeleton width="40%" height={18} />
          <Skeleton height={14} />
          <Skeleton height={14} />
          <Skeleton width="60%" height={14} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 8 }}>
            <Skeleton height={120} />
            <Skeleton height={120} />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      {loadError && <PageError error={loadError} />}
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8,
          background: toast.isError ? C.redBg : C.grnBg,
          border: `1px solid ${toast.isError ? C.red : C.grn}33`,
          color: toast.isError ? C.red : C.grn,
          fontSize: 12, fontFamily: FONTS.mono, fontWeight: 600,
          animation: 'fadeIn 0.2s ease',
        }}>{toast.msg}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        {/* ── SECTION A: Portal Identity ── */}
        <Card style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 18 }}>Portal Identity</div>
          <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14 }}>
            These values appear on the login page and browser tab for your white-label portal.
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Portal Name</div>
            <input value={portalName} onChange={e => setPortalName(e.target.value)} placeholder="Sentinel Command" style={getInputStyle()} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Tagline</div>
            <input value={tagline} onChange={e => setTagline(e.target.value)} placeholder="Cybersecurity Command Portal" style={getInputStyle()} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Description</div>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe your portal..."
              rows={4} style={{ ...getInputStyle(), resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Logo Letter</div>
            <input value={logoLetter} onChange={e => setLogoLetter(e.target.value.slice(0, 1))} placeholder="S" maxLength={1}
              style={{ ...getInputStyle(), width: 60, textAlign: 'center', fontSize: 18, fontWeight: 800 }} />
            <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>Single character fallback when no logo is uploaded</div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Custom Domain</div>
            <input value={customDomain} onChange={e => setCustomDomain(e.target.value.toLowerCase().trim())} placeholder="portal.yourdomain.com" style={getInputStyle()} />
            <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>
              The domain where your portal is hosted. Login page branding is loaded based on this domain.
            </div>
          </div>
        </Card>

        {/* ── SECTION B: Brand Colors + Live Preview ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 18 }}>Brand Colors</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <ColorPicker label="Primary Color" value={primaryColor} onChange={setPrimaryColor} />
              <ColorPicker label="Secondary Color" value={secondaryColor} onChange={setSecondaryColor} />
              <ColorPicker label="Background Color" value={backgroundColor} onChange={setBackgroundColor} />
              <ColorPicker label="Surface Color" value={surfaceColor} onChange={setSurfaceColor} />
              <ColorPicker label="Text Color" value={textColor} onChange={setTextColor} />
            </div>
          </Card>

          <Card style={{ padding: 22 }}>
            <label htmlFor="partner-portal-font" style={{ display: 'block', fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10 }}>Portal Font</label>
            <select id="partner-portal-font" aria-describedby="partner-portal-font-help" value={fontFamily} onChange={e => setFontFamily(e.target.value)} style={getInputStyle()}>
              {PORTAL_FONT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <p id="partner-portal-font-help" style={{ fontSize: 11, color: C.t2, marginTop: 10, lineHeight: 1.6 }}>
              Applies to this partner’s portal and all of their clients. Other partners keep their own fonts. Report and email fonts are configured separately.
            </p>
            <div aria-label="Font preview" style={{ fontFamily: previewFonts.sans, color: textColor, background: backgroundColor, border: `1px solid ${C.b1}`, borderRadius: 6, padding: 14, marginTop: 12 }}>
              <strong style={{ display: 'block', fontSize: 18, marginBottom: 6 }}>{portalName || 'Partner Portal'}</strong>
              <span style={{ fontSize: 13 }}>Security findings, assets, and reports — Aa Bb 0123456789</span>
            </div>
          </Card>

          {/* Live Preview — sidebar mockup */}
          <Card style={{ padding: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: previewFonts.sans, marginBottom: 12, color: C.t2 }}>LIVE PREVIEW</div>
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.b1}`, height: 230, marginBottom: 12 }}>
              {/* Mini sidebar */}
              <div style={{ width: 160, background: surfaceColor, borderRight: `1px solid ${backgroundColor}22`, display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '12px 10px', borderBottom: `1px solid ${primaryColor}22`, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {logoUrl ? (
                    <BrandLogo
                      src={logoUrl}
                      alt={`${portalName || 'Portal'} logo`}
                      letter={logoLetter || 'S'}
                      primaryColor={primaryColor}
                      secondaryColor={secondaryColor}
                      width={130}
                      height={48}
                      maxWidth={130}
                      maxHeight={48}
                    />
                  ) : (
                    <>
                      <div style={{
                        width: 20, height: 20, borderRadius: 4,
                        background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: 900, color: '#fff', flexShrink: 0,
                      }}>{logoLetter || 'S'}</div>
                      <span style={{ fontSize: 10, fontWeight: 800, color: textColor, fontFamily: previewFonts.sans }}>{portalName || 'Portal'}</span>
                    </>
                  )}
                </div>
                <div style={{ padding: '6px 6px', flex: 1 }}>
                  {['Dashboard', 'Findings', 'Assets', 'Reports'].map((item, i) => (
                    <div key={item} style={{
                      padding: '5px 8px', borderRadius: 4, marginBottom: 2,
                      fontSize: 9, fontFamily: previewFonts.sans, cursor: 'default',
                      background: i === 0 ? `${primaryColor}18` : 'transparent',
                      color: i === 0 ? textColor : (textColor + '99'),
                      fontWeight: i === 0 ? 600 : 400,
                      borderLeft: i === 0 ? `2px solid ${secondaryColor}` : '2px solid transparent',
                    }}>{item}</div>
                  ))}
                </div>
              </div>
              {/* Mini main area */}
              <div style={{ flex: 1, background: backgroundColor, display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: 32, background: surfaceColor, borderBottom: `1px solid ${primaryColor}11`, display: 'flex', alignItems: 'center', padding: '0 12px' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: textColor, fontFamily: previewFonts.sans }}>Dashboard</span>
                </div>
                <div style={{ padding: 12, flex: 1 }}>
                  <div style={{ fontSize: 9, color: textColor, fontFamily: previewFonts.mono, opacity: 0.6, marginBottom: 8 }}>
                    {tagline || 'Your tagline here'}
                  </div>
                  <button style={{
                    padding: '5px 14px', borderRadius: 4, border: 'none', cursor: 'default',
                    background: secondaryColor, color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: previewFonts.sans,
                  }}>Primary Action</button>
                  <button style={{
                    padding: '5px 14px', borderRadius: 4, border: `1px solid ${primaryColor}33`, cursor: 'default',
                    background: 'transparent', color: textColor, fontSize: 9, fontWeight: 600, fontFamily: previewFonts.sans, marginLeft: 6,
                  }}>Secondary</button>
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 8, overflow: 'hidden', background: backgroundColor }}>
                <div style={{ padding: 14, minHeight: 112, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>{logoLetter || 'S'}</div>
                  <div style={{ fontSize: 12, color: textColor, fontWeight: 800 }}>{portalName || 'Portal'}</div>
                  <div style={{ width: '72%', height: 22, borderRadius: 5, background: surfaceColor, border: `1px solid ${primaryColor}22` }} />
                  <div style={{ width: '72%', height: 22, borderRadius: 5, background: secondaryColor }} />
                </div>
                <div style={{ padding: '6px 10px', borderTop: `1px solid ${primaryColor}22`, fontSize: 8, color: textColor, opacity: 0.7, fontFamily: FONTS.mono }}>Login screen</div>
              </div>
              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <div style={{ height: 112, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 8, color: primaryColor, fontFamily: FONTS.mono, fontWeight: 800 }}>MONTHLY SECURITY REPORT</div>
                    <div style={{ fontSize: 16, color: '#111827', fontWeight: 900, marginTop: 6 }}>{portalName || 'Partner Portal'}</div>
                    <div style={{ fontSize: 9, color: '#64748b', marginTop: 4 }}>{tagline || 'Security posture and remediation priorities'}</div>
                  </div>
                  <div style={{ height: 5, width: '100%', background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`, borderRadius: 999 }} />
                </div>
                <div style={{ padding: '6px 10px', borderTop: '1px solid #e5e7eb', fontSize: 8, color: '#64748b', fontFamily: FONTS.mono }}>Report cover</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* ── SECTION C: Logo Upload (Drag & Drop) ── */}
      <Card style={{ padding: 22, marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 16 }}>Logo</div>
        <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14 }}>
          Upload your organization logo. Accepted formats: PNG, JPEG, SVG, WebP. Max height displayed: 80px.
        </div>

        {logoUrl && (
          <div style={{ marginBottom: 14, padding: 14, background: C.s2, borderRadius: 7, border: `1px solid ${C.b1}`, display: 'inline-block' }}>
            <BrandLogo
              src={logoUrl}
              alt="Current logo"
              letter={logoLetter || 'S'}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              width={220}
              height={86}
              maxWidth={220}
              maxHeight={86}
            />
          </div>
        )}

        <div
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.sky; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = C.b1; }}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.b1; const f = e.dataTransfer.files[0]; if (f && f.type.startsWith('image/')) { setLogoFile(f); setLogoStatus(''); } }}
          style={{
            border: `2px dashed ${C.b1}`, borderRadius: 8, padding: '20px 16px',
            textAlign: 'center', transition: 'border-color 0.15s', marginBottom: 12, cursor: 'pointer',
          }}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={e => { setLogoFile(e.target.files[0] || null); setLogoStatus(''); }}
            style={{ display: 'none' }}
          />
          <div style={{ fontSize: 20, marginBottom: 6 }}>{'\u2B06'}</div>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>
            {logoFile ? logoFile.name : 'Drag & drop a logo here, or click to browse'}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {logoFile && (
            <Button variant="pri" onClick={handleLogoUpload} style={{ opacity: logoUploading ? 0.6 : 1 }}>
              {logoUploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
          {logoUrl && (
            <Button variant="ghost" onClick={handleLogoRemove} style={{ color: C.red, fontSize: 11 }}>
              Remove Logo
            </Button>
          )}
        </div>
        {logoStatus && (
          <div style={{ marginTop: 8, fontSize: 11, color: logoStatus.includes('fail') || logoStatus.includes('Failed') ? C.red : C.grn, fontFamily: FONTS.mono }}>
            {logoStatus}
          </div>
        )}
      </Card>

      {/* ── SECTION D: SSO / OIDC Configuration ── */}
      {isPlatformAdmin ? (
      <Card style={{ padding: 22, marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: oidcOpen ? 16 : 0 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans }}>SSO Configuration (OIDC)</div>
            {!oidcOpen && (
              <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 4 }}>
                {oidcAuthority ? 'Configured' : 'Not configured'} — click to expand
              </div>
            )}
          </div>
          <Button variant="sec" onClick={() => setOidcOpen(o => !o)} style={{ fontSize: 10 }}>
            {oidcOpen ? 'Collapse' : 'Expand'}
          </Button>
        </div>

        {oidcOpen && (
          <div>
            <div style={{
              padding: '12px 14px', borderRadius: 7, background: C.skyBg, border: `1px solid ${C.sky}33`,
              fontSize: 11, color: C.sky, fontFamily: FONTS.mono, lineHeight: 1.6, marginBottom: 16,
            }}>
              Configure your organization's identity provider so your portal users authenticate with their own
              corporate credentials instead of Sentinel Command accounts.
            </div>

            {oidcError && (
              <div style={{
                padding: '10px 14px', borderRadius: 7, background: C.redBg, border: `1px solid ${C.red}33`,
                fontSize: 11, color: C.red, fontFamily: FONTS.mono, marginBottom: 14,
              }}>{oidcError}</div>
            )}

            {oidcAuthority && !oidcAuthority.startsWith('https://') && (
              <div style={{
                padding: '10px 14px', borderRadius: 7, background: C.ambBg, border: `1px solid ${C.amb}33`,
                fontSize: 11, color: C.amb, fontFamily: FONTS.mono, marginBottom: 14,
              }}>OIDC Authority URL must use HTTPS for security.</div>
            )}

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>OIDC Authority URL</div>
              <input value={oidcAuthority} onChange={e => { setOidcAuthority(e.target.value); setOidcError(''); }}
                placeholder="https://login.microsoftonline.com/{tenant}/v2.0" style={getInputStyle()} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>OIDC Client ID</div>
              <input value={oidcClientId} onChange={e => setOidcClientId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000" style={getInputStyle()} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>OIDC Scopes</div>
              <input value={oidcScopes} onChange={e => setOidcScopes(e.target.value)}
                placeholder="openid profile email" style={getInputStyle()} />
              <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>Space-separated list of scopes</div>
            </div>
          </div>
        )}
      </Card>
      ) : (
      <Card style={{ padding: 22, marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12 }}>SSO Configuration (OIDC)</div>
        <div style={{ padding: 16, background: C.s2, borderRadius: 8, border: `1px solid ${C.b1}` }}>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6 }}>
            Request white-glove SSO setup for this partner portal. Southern Sentinel Security will validate your identity provider details and schedule the cutover.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
            <input placeholder="Identity provider" style={getInputStyle()} />
            <input placeholder="Admin contact email" style={getInputStyle()} />
          </div>
          <Button variant="pri" style={{ marginTop: 12 }} disabled title="Request capture is prepared for demo; workflow delivery is handled by support.">Request SSO setup</Button>
        </div>
      </Card>
      )}

      {/* Save error banner */}
      {saveError && (
        <div style={{
          marginTop: 14, padding: '12px 16px', borderRadius: 7,
          background: C.redBg, border: `1px solid ${C.red}33`,
          color: C.red, fontSize: 12, fontFamily: FONTS.mono, lineHeight: 1.5,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>Save failed: {saveError}</span>
          <button onClick={() => setSaveError('')} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14 }}>{'\u2715'}</button>
        </div>
      )}

      {/* Post-save verification */}
      {verified && (
        <div style={{
          marginTop: 14, padding: '10px 14px', borderRadius: 7, fontSize: 11, fontFamily: FONTS.mono,
          background: verified === 'pass' ? `${C.grn}10` : `${C.amb}10`,
          border: `1px solid ${verified === 'pass' ? C.grn : C.amb}33`,
          color: verified === 'pass' ? C.grn : C.amb,
        }}>
          {verified === 'pass' && '\u2713 Verified — saved values match what was submitted.'}
          {verified === 'mismatch' && '\u26A0 Warning — some saved values may not match. Try refreshing the page.'}
          {verified === 'unknown' && '\u26A0 Could not verify saved values. Changes were submitted but verification failed.'}
        </div>
      )}

      {/* Save button — sticky at bottom */}
      <div style={{ position: 'sticky', bottom: 0, marginTop: 16, padding: '12px 0', background: C.bg, borderTop: `1px solid ${C.b1}`, display: 'flex', justifyContent: 'flex-end', zIndex: 10 }}>
        <Button variant="pri" onClick={handleSave} style={{
          padding: '10px 28px', fontSize: 13, opacity: saving ? 0.6 : 1,
        }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </>
  );
}

const REPORT_TEMPLATE_FALLBACK_SLOTS = [
  { reportType: 'monthly', outputFormat: 'docx', label: 'Monthly Security DOCX' },
  { reportType: 'monthly', outputFormat: 'md', label: 'Monthly Security Markdown' },
  { reportType: 'compliance', outputFormat: 'docx', label: 'Monthly Compliance DOCX' },
  { reportType: 'compliance', outputFormat: 'md', label: 'Monthly Compliance Markdown' },
  { reportType: 'qbr', outputFormat: 'pptx', label: 'QBR PPTX' },
  { reportType: 'qbr', outputFormat: 'md', label: 'QBR Markdown' },
];

const DEFAULT_REPORT_THEME = {
  primaryColor: '#0f4c81',
  secondaryColor: '#102a43',
  accentColor: '#2d9cdb',
  backgroundColor: '#f8fafc',
  surfaceColor: '#ffffff',
  textColor: '#172033',
  fontFamily: 'Inter',
  headingFontFamily: 'Inter',
  coverStyle: 'executive',
};

const DEFAULT_TEMPLATE_FIELD_MAPPINGS = {
  Company: '{{client.name}}',
  Client: '{{client.name}}',
  Manager: '{{msp.name}}',
  MSP: '{{msp.name}}',
  ReportPeriod: '{{report.period}}',
  ReportYear: '{{report.year}}',
  ReportDate: '{{report.generatedAt}}',
  ReportType: '{{report.type}}',
};

const EMPTY_TEMPLATE_DEFINITION = {
  coverTitle: '',
  coverSubtitle: '',
  executiveSummaryHeading: '',
  executiveSummaryIntro: '',
  closingNote: '',
  markdownBody: '',
  theme: DEFAULT_REPORT_THEME,
  fieldMappings: DEFAULT_TEMPLATE_FIELD_MAPPINGS,
  blocks: [],
  assets: [],
};

const REPORT_DESIGNER_SAMPLE = {
  clientName: 'Demo Environment',
  mspName: 'Southern Sentinel Security',
  period: 'May 2026',
  quarter: 'Q2 2026',
  securityScore: '83',
  totalOpen: '27',
  criticalCount: '4',
  highCount: '9',
  mediumCount: '11',
  lowCount: '3',
  resolvedCount: '18',
  newCount: '7',
  dastCriticalCount: '2',
  dastHighCount: '3',
  sddCriticalCount: '1',
  sddHighCount: '4',
  vulnerabilityCriticalCount: '3',
  vulnerabilityHighCount: '8',
  attackPathCount: '5',
  poamOpenCount: '14',
  poamCriticalCount: '3',
  poamHighCount: '6',
  findings: [
    { severity: 'Critical', title: 'Critical AWS S3 bucket allows public access', age: 9, tags: ['cloud', 'aws', 'storage'] },
    { severity: 'Critical', title: 'Critical Entra admin account missing MFA enforcement', age: 14, tags: ['cloud', 'identity'] },
    { severity: 'High', title: 'External web application exposes vulnerable framework version', age: 22, tags: ['web', 'vulnerability'] },
    { severity: 'High', title: 'Azure security group allows broad management access', age: 17, tags: ['cloud', 'network'] },
    { severity: 'Medium', title: 'Endpoint patch baseline is outside operating target', age: 31, tags: ['endpoint', 'vulnerability'] },
    { severity: 'Critical', title: 'DAST scan found SQL injection on customer portal login', age: 6, tags: ['dast', 'web', 'application', 'sqli'] },
    { severity: 'High', title: 'DAST scan found missing security headers on admin portal', age: 12, tags: ['dast', 'web', 'headers'] },
    { severity: 'Critical', title: 'Sensitive data discovery found credentials in SharePoint finance folder', age: 4, tags: ['sdd', 'sensitive', 'credential', 'sharepoint'] },
    { severity: 'High', title: 'Sensitive data discovery found payroll export in public S3 bucket', age: 11, tags: ['sdd', 'sensitive', 'pii', 'aws'] },
    { severity: 'Critical', title: 'Nessus detected exploitable VPN CVE on edge appliance', age: 8, tags: ['vulnerability', 'nessus', 'cve', 'edge'] },
  ],
  severity: [
    { label: 'Critical', value: 4, color: '#dc2626' },
    { label: 'High', value: 9, color: '#f97316' },
    { label: 'Medium', value: 11, color: '#facc15' },
    { label: 'Low', value: 3, color: '#22c55e' },
  ],
  scoreTrend: [71, 74, 78, 76, 81, 83],
};

const REPORT_FONT_OPTIONS = [
  'Inter',
  'Arial',
  'Arial Black',
  'Aptos',
  'Aptos Display',
  'Helvetica Neue',
  'Neue Haas Grotesk Text Pro',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Segoe UI',
  'Open Sans',
  'Roboto',
  'Lato',
  'Montserrat',
  'Poppins',
  'Nunito Sans',
  'Source Sans 3',
  'Work Sans',
  'IBM Plex Sans',
  'IBM Plex Mono',
  'PT Sans Caption',
  'Teko Light',
  'Roboto Slab',
  'Georgia',
  'Times New Roman',
  'Cambria',
  'Garamond',
  'Baskerville',
  'Palatino Linotype',
  'Merriweather',
  'Lora',
  'Playfair Display',
  'Futura',
  'Avenir',
  'Gill Sans',
  'Franklin Gothic Medium',
  'Century Gothic',
  'Optima',
  'Didot',
  'Bodoni 72',
  'Courier New',
];
const REPORT_COVER_STYLES = ['executive', 'minimal', 'board'];
const REPORT_CHART_TYPES = [
  { value: 'severity', label: 'Severity bars' },
  { value: 'bar', label: 'Bar chart' },
  { value: 'stackedBar', label: 'Stacked bar' },
  { value: 'line', label: 'Line chart' },
  { value: 'dualLine', label: 'Dual line' },
  { value: 'pie', label: 'Pie chart' },
  { value: 'donut', label: 'Donut chart' },
  { value: 'scoreTrend', label: 'Score trend' },
  { value: 'riskMix', label: 'Risk mix' },
];
const REPORT_CHART_COLOR_FIELDS = [
  ['Primary Series', 'chartPrimaryColor'],
  ['Secondary Series', 'chartSecondaryColor'],
  ['Third Series', 'chartTertiaryColor'],
  ['Fourth Series', 'chartQuaternaryColor'],
];
const REPORT_KPI_PRESETS = [
  { label: 'Security Score', token: '{{security.score}}', color: 'primary' },
  { label: 'Open Findings', token: '{{findings.totalOpen}}', color: 'secondary' },
  { label: 'Critical Findings', token: '{{findings.criticalCount}}', color: '#dc2626' },
  { label: 'High Findings', token: '{{findings.highCount}}', color: '#f97316' },
  { label: 'Medium Findings', token: '{{findings.mediumCount}}', color: '#facc15' },
  { label: 'Low Findings', token: '{{findings.lowCount}}', color: '#22c55e' },
  { label: 'Resolved Findings', token: '{{findings.resolvedCount}}', color: '#16a34a' },
  { label: 'New Findings', token: '{{findings.newCount}}', color: 'accent' },
  { label: 'Critical DAST', token: '{{dast.criticalCount}}', color: '#be123c' },
  { label: 'High DAST', token: '{{dast.highCount}}', color: '#ea580c' },
  { label: 'Critical SDD', token: '{{sdd.criticalCount}}', color: '#7c3aed' },
  { label: 'High SDD', token: '{{sdd.highCount}}', color: '#9333ea' },
  { label: 'Critical Vulnerabilities', token: '{{vulnerabilities.criticalCount}}', color: '#b91c1c' },
  { label: 'High Vulnerabilities', token: '{{vulnerabilities.highCount}}', color: '#c2410c' },
  { label: 'Open POA&M', token: '{{compliance.openPoamCount}}', color: '#0f766e' },
  { label: 'Critical POA&M', token: '{{compliance.criticalPoamCount}}', color: '#dc2626' },
  { label: 'Attack Paths', token: '{{attackPaths.count}}', color: '#2563eb' },
];
const REPORT_TOKEN_LIBRARY = [
  '{{client.name}}',
  '{{msp.name}}',
  '{{report.period}}',
  '{{report.year}}',
  '{{report.generatedAt}}',
  '{{report.type}}',
  '{{security.score}}',
  '{{findings.totalOpen}}',
  '{{findings.criticalCount}}',
  '{{findings.highCount}}',
  '{{findings.mediumCount}}',
  '{{findings.lowCount}}',
  '{{findings.resolvedCount}}',
  '{{findings.newCount}}',
  '{{critical.cloud.finding}}',
  '{{critical.cloud.findings}}',
  '{{high.cloud.findings}}',
  '{{dast.findings}}',
  '{{critical.dast.finding}}',
  '{{critical.dast.findings}}',
  '{{high.dast.findings}}',
  '{{dast.criticalCount}}',
  '{{dast.highCount}}',
  '{{sdd.findings}}',
  '{{critical.sdd.finding}}',
  '{{critical.sdd.findings}}',
  '{{high.sdd.findings}}',
  '{{sdd.criticalCount}}',
  '{{sdd.highCount}}',
  '{{vulnerabilities.findings}}',
  '{{critical.vulnerability.finding}}',
  '{{critical.vulnerability.findings}}',
  '{{vulnerabilities.criticalCount}}',
  '{{vulnerabilities.highCount}}',
  '{{attackPaths.count}}',
  '{{attackPaths.summary}}',
  '{{compliance.openPoamCount}}',
  '{{compliance.criticalPoamCount}}',
  '{{compliance.highPoamCount}}',
  '{{compliance.cmmcScore}}',
  '{{qbr.quarter}}',
  '{{qbr.resolvedThisQuarter}}',
  '{{finding.title}}',
  '{{finding.severity}}',
  '{{finding.status}}',
  '{{finding.ageDays}}',
  '{{finding.cve}}',
  '{{finding.cvss}}',
  '{{finding.fingerprint}}',
];

const REPORT_BLOCK_LIBRARY = [
  { type: 'cover', label: 'Cover', icon: LayoutTemplate },
  { type: 'toc', label: 'TOC', icon: FileText },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'table', label: 'Table', icon: Layers3 },
  { type: 'kpi', label: 'KPI', icon: Layers3 },
  { type: 'chart', label: 'Chart', icon: BarChart3 },
  { type: 'findings', label: 'Findings', icon: FileText },
  { type: 'findingCard', label: 'Finding Card', icon: FileText },
  { type: 'findingRepeater', label: 'Finding Repeater', icon: Layers3 },
  { type: 'tableRepeater', label: 'Table Repeater', icon: Layers3 },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'pageBreak', label: 'Page Break', icon: LayoutTemplate },
  { type: 'appendix', label: 'Appendix', icon: FileText },
];

const REPORT_ELEMENT_LIBRARY = [
  { type: 'heading', label: 'Heading', icon: Heading1 },
  { type: 'paragraph', label: 'Paragraph', icon: Pilcrow },
  { type: 'metric', label: 'Metric', icon: Layers3 },
  { type: 'chart', label: 'Chart', icon: BarChart3 },
  { type: 'findings', label: 'Findings', icon: FileText },
  { type: 'findingRepeater', label: 'Finding Repeater', icon: Layers3 },
  { type: 'tableRepeater', label: 'Table Repeater', icon: Layers3 },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'table', label: 'Table', icon: Layers3 },
  { type: 'divider', label: 'Divider', icon: Minus },
];

const COVER_COMPONENT_TYPES = [
  { type: 'accentLine', label: 'Accent Line' },
  { type: 'logo', label: 'Logo' },
  { type: 'title', label: 'Title' },
  { type: 'subtitle', label: 'Subtitle' },
  { type: 'metadata', label: 'Client / Period / Score' },
];

const REPORT_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';
const REPORT_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

function readReportImageFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Image upload could not be read.'));
    reader.readAsDataURL(file);
  });
}

function clampNumber(value, min, max) {
  const number = Number(value);
  if (Number.isNaN(number)) return min;
  return Math.min(max, Math.max(min, number));
}

function normalizeLayout(layout = {}, fallback = {}) {
  return {
    x: clampNumber(layout.x ?? fallback.x ?? 0, 0, 100),
    y: clampNumber(layout.y ?? fallback.y ?? 0, 0, 100),
    width: clampNumber(layout.width ?? fallback.width ?? 20, 1, 100),
    height: clampNumber(layout.height ?? fallback.height ?? 8, 1, 100),
    direction: layout.direction || fallback.direction || '',
    align: layout.align || fallback.align || '',
  };
}

function normalizeStyle(style = {}, fallback = {}) {
  return Object.fromEntries(Object.entries({ ...fallback, ...(style || {}) }).map(([key, value]) => [key, String(value)]));
}

function defaultFrameLayout() {
  return { x: 0, y: 0, width: 100, height: 8, direction: '', align: 'left' };
}

function defaultElementLayout(type = 'paragraph', index = 0) {
  const normalizedType = String(type || '').toLowerCase();
  if (normalizedType === 'metric' || normalizedType === 'kpi') {
    return {
      x: (index % 3) * 34,
      y: Math.floor(index / 3) * 118,
      width: 31,
      height: 16,
      direction: '',
      align: 'left',
    };
  }

  return {
    x: 0,
    y: index * 180,
    width: 100,
    height: normalizedType === 'chart' || normalizedType === 'image' || normalizedType === 'table' || normalizedType === 'findingrepeater' || normalizedType === 'tablerepeater' ? 28 : 12,
    direction: '',
    align: 'left',
  };
}

function hasIntentionalLayout(layout = {}) {
  if (!layout || typeof layout !== 'object') return false;
  const x = Number(layout.x ?? 0);
  const y = Number(layout.y ?? 0);
  const width = Number(layout.width ?? 0);
  const height = Number(layout.height ?? 0);
  return x !== 0
    || y !== 0
    || (width !== 0 && width !== 20 && width !== 100)
    || (height !== 0 && height !== 8)
    || Boolean(layout.align)
    || Boolean(layout.direction);
}

function normalizeFrameLayout(layout = {}) {
  // Older import previews serialize unspecified frame dimensions as zero.
  // Use frame defaults for those dimensions only; retain intentional sizes,
  // positioning and the separate cover-component layout contract.
  const frame = layout || {};
  const defaults = defaultFrameLayout();
  const normalized = normalizeLayout({
    ...frame,
    width: Number(frame.width) === 0 ? defaults.width : frame.width,
    height: Number(frame.height) === 0 ? defaults.height : frame.height,
  }, defaults);
  const looksLikeLegacyDefault = Number(layout?.x ?? 0) === 0
    && Number(layout?.y ?? 0) === 0
    && Number(layout?.width ?? 100) === 20
    && Number(layout?.height ?? 8) === 8
    && !layout?.align
    && !layout?.direction;
  return looksLikeLegacyDefault ? { ...normalized, width: 100, align: 'left' } : normalized;
}

function createCoverComponents(block = {}) {
  const prefix = block.id || uid('cover');
  return [
    {
      id: `${prefix}-accent`,
      type: 'accentLine',
      label: 'Accent Line',
      layout: { x: 8, y: 16, width: 7, height: 2, direction: '', align: '' },
      style: { lineThickness: '5', color: 'accent' },
    },
    {
      id: `${prefix}-title`,
      type: 'title',
      label: 'Title',
      content: '',
      layout: { x: 8, y: 31, width: 65, height: 16, direction: '', align: 'left' },
      style: { fontSize: '38', fontWeight: '900' },
    },
    {
      id: `${prefix}-subtitle`,
      type: 'subtitle',
      label: 'Subtitle',
      content: '',
      layout: { x: 8, y: 53, width: 62, height: 10, direction: '', align: 'left' },
      style: { fontSize: '14' },
    },
    {
      id: `${prefix}-metadata`,
      type: 'metadata',
      label: 'Client / Period / Score',
      layout: { x: 8, y: 72, width: 64, height: 18, direction: 'row', align: 'left' },
      style: { lineThickness: '1', gap: '10' },
    },
    {
      id: `${prefix}-logo`,
      type: 'logo',
      label: 'Logo',
      layout: { x: 80, y: 9, width: 14, height: 28, direction: '', align: 'center' },
      style: { size: '92', shape: 'rounded' },
    },
  ];
}

function normalizeCoverComponent(component, index, fallbackComponent) {
  const fallback = fallbackComponent || createCoverComponents({ id: `component-${index}` })[0];
  return {
    id: component.id || fallback.id || uid(`component-${index}`),
    type: component.type || fallback.type || 'text',
    label: component.label || fallback.label || '',
    content: component.content || '',
    query: component.query || '',
    imageUrl: component.imageUrl || '',
    altText: component.altText || '',
    layout: normalizeLayout(component.layout, fallback.layout),
    style: normalizeStyle(component.style, fallback.style),
  };
}

function templateKey(item) {
  return `${String(item?.reportType || '').toLowerCase()}:${String(item?.outputFormat || '').toLowerCase()}`;
}

function formatTemplateDate(value) {
  if (!value) return 'Not published';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleString();
}

function uid(prefix = 'block') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function tokenPreview(text = '') {
  return String(text || '')
    .replaceAll('{{client.name}}', REPORT_DESIGNER_SAMPLE.clientName)
    .replaceAll('{{msp.name}}', REPORT_DESIGNER_SAMPLE.mspName)
    .replaceAll('{{report.period}}', REPORT_DESIGNER_SAMPLE.period)
    .replaceAll('{{report.year}}', '2026')
    .replaceAll('{{report.generatedAt}}', new Date().toLocaleDateString())
    .replaceAll('{{report.type}}', 'Monthly Security Report')
    .replaceAll('{{security.score}}', REPORT_DESIGNER_SAMPLE.securityScore)
    .replaceAll('{{findings.totalOpen}}', REPORT_DESIGNER_SAMPLE.totalOpen)
    .replaceAll('{{findings.criticalCount}}', REPORT_DESIGNER_SAMPLE.criticalCount)
    .replaceAll('{{findings.highCount}}', REPORT_DESIGNER_SAMPLE.highCount)
    .replaceAll('{{findings.mediumCount}}', REPORT_DESIGNER_SAMPLE.mediumCount)
    .replaceAll('{{findings.lowCount}}', REPORT_DESIGNER_SAMPLE.lowCount)
    .replaceAll('{{findings.resolvedCount}}', REPORT_DESIGNER_SAMPLE.resolvedCount)
    .replaceAll('{{findings.newCount}}', REPORT_DESIGNER_SAMPLE.newCount)
    .replaceAll('{{critical.cloud.finding}}', 'Critical AWS S3 bucket allows public access')
    .replaceAll('{{critical.cloud.findings}}', 'Critical AWS S3 bucket allows public access; Critical Entra admin account missing MFA enforcement')
    .replaceAll('{{high.cloud.findings}}', 'Azure security group allows broad management access')
    .replaceAll('{{dast.findings}}', 'DAST scan found SQL injection on customer portal login; DAST scan found missing security headers on admin portal')
    .replaceAll('{{critical.dast.finding}}', 'DAST scan found SQL injection on customer portal login')
    .replaceAll('{{critical.dast.findings}}', 'DAST scan found SQL injection on customer portal login')
    .replaceAll('{{high.dast.findings}}', 'DAST scan found missing security headers on admin portal')
    .replaceAll('{{dast.criticalCount}}', REPORT_DESIGNER_SAMPLE.dastCriticalCount)
    .replaceAll('{{dast.highCount}}', REPORT_DESIGNER_SAMPLE.dastHighCount)
    .replaceAll('{{sdd.findings}}', 'Sensitive data discovery found credentials in SharePoint finance folder; Sensitive data discovery found payroll export in public S3 bucket')
    .replaceAll('{{critical.sdd.finding}}', 'Sensitive data discovery found credentials in SharePoint finance folder')
    .replaceAll('{{critical.sdd.findings}}', 'Sensitive data discovery found credentials in SharePoint finance folder')
    .replaceAll('{{high.sdd.findings}}', 'Sensitive data discovery found payroll export in public S3 bucket')
    .replaceAll('{{sdd.criticalCount}}', REPORT_DESIGNER_SAMPLE.sddCriticalCount)
    .replaceAll('{{sdd.highCount}}', REPORT_DESIGNER_SAMPLE.sddHighCount)
    .replaceAll('{{vulnerabilities.findings}}', 'Nessus detected exploitable VPN CVE on edge appliance; Endpoint patch baseline is outside operating target')
    .replaceAll('{{critical.vulnerability.finding}}', 'Nessus detected exploitable VPN CVE on edge appliance')
    .replaceAll('{{critical.vulnerability.findings}}', 'Nessus detected exploitable VPN CVE on edge appliance')
    .replaceAll('{{vulnerabilities.criticalCount}}', REPORT_DESIGNER_SAMPLE.vulnerabilityCriticalCount)
    .replaceAll('{{vulnerabilities.highCount}}', REPORT_DESIGNER_SAMPLE.vulnerabilityHighCount)
    .replaceAll('{{attackPaths.count}}', REPORT_DESIGNER_SAMPLE.attackPathCount)
    .replaceAll('{{attackPaths.summary}}', 'Five active attack paths connect exposed edge services, identity gaps, and sensitive data stores.')
    .replaceAll('{{compliance.openPoamCount}}', REPORT_DESIGNER_SAMPLE.poamOpenCount)
    .replaceAll('{{compliance.criticalPoamCount}}', REPORT_DESIGNER_SAMPLE.poamCriticalCount)
    .replaceAll('{{compliance.highPoamCount}}', REPORT_DESIGNER_SAMPLE.poamHighCount)
    .replaceAll('{{compliance.cmmcScore}}', '74')
    .replaceAll('{{qbr.resolvedThisQuarter}}', REPORT_DESIGNER_SAMPLE.resolvedCount)
    .replaceAll('{{qbr.quarter}}', REPORT_DESIGNER_SAMPLE.quarter)
    .replaceAll('{{finding.title}}', REPORT_DESIGNER_SAMPLE.findings[0]?.title || 'Sample finding')
    .replaceAll('{{finding.severity}}', REPORT_DESIGNER_SAMPLE.findings[0]?.severity || 'Critical')
    .replaceAll('{{finding.status}}', 'Open')
    .replaceAll('{{finding.ageDays}}', String(REPORT_DESIGNER_SAMPLE.findings[0]?.age || 0))
    .replaceAll('{{finding.cve}}', 'CVE-2026-0001')
    .replaceAll('{{finding.cvss}}', '9.1')
    .replaceAll('{{finding.fingerprint}}', 'sample:finding');
}

function containsTemplateToken(text = '') {
  return /{{[^}]+}}/.test(String(text || ''));
}

function replaceLineAtIndex(text = '', index = 0, nextLine = '') {
  const lines = String(text || '').split('\n');
  lines[index] = nextLine;
  return lines.join('\n');
}

function updateKpiMetricLabel(content = '', index = 0, nextLabel = '') {
  const lines = String(content || '').split('\n');
  const line = lines[index] || '';
  const parts = line.split('|').map(part => part.trim());
  parts[0] = nextLabel;
  lines[index] = parts.join(' | ');
  return lines.join('\n');
}

function InlineEditableText({
  value,
  fallback = '',
  onCommit,
  as: TagName = 'span',
  style,
  title,
  multiline = false,
  disabled = false,
  children,
}) {
  const text = String(value || fallback || '');
  const editable = !disabled && text.trim().length > 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const ref = useRef(null);
  const draftRef = useRef(text);

  useEffect(() => {
    if (!editing) {
      draftRef.current = text;
      setDraft(text);
    }
  }, [editing, text]);

  useEffect(() => {
    if (!editing || !ref.current) return;
    draftRef.current = text;
    ref.current.textContent = text;
    ref.current.focus();
    const range = document.createRange();
    range.selectNodeContents(ref.current);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [editing]);

  const commit = () => {
    const liveDraft = ref.current
      ? (multiline ? ref.current.innerText : ref.current.textContent)
      : draftRef.current;
    const next = String(liveDraft || '').trim();
    draftRef.current = next;
    setEditing(false);
    if (!next || next === text) return;
    onCommit?.(next);
  };

  if (!editable) {
    return <TagName style={style}>{children ?? tokenPreview(text)}</TagName>;
  }

  if (editing) {
    return (
      <TagName
        ref={ref}
        data-report-inline-edit="active"
        contentEditable
        suppressContentEditableWarning
        onClick={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
        onInput={event => {
          draftRef.current = multiline ? event.currentTarget.innerText : event.currentTarget.textContent;
        }}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            draftRef.current = text;
            if (ref.current) ref.current.textContent = text;
            setDraft(text);
            setEditing(false);
          }
          if (event.key === 'Enter' && !multiline) {
            event.preventDefault();
            event.stopPropagation();
            commit();
          }
        }}
        style={{
          ...style,
          cursor: 'text',
          outline: `2px solid ${C.sky}`,
          outlineOffset: 2,
          borderRadius: 4,
          background: '#ffffff',
          color: C.t1,
        }}
      >
        {draft}
      </TagName>
    );
  }

  return (
    <TagName
      data-report-inline-edit="ready"
      title={title || 'Double-click to edit'}
      onDoubleClick={event => {
        event.preventDefault();
        event.stopPropagation();
        setDraft(text);
        setEditing(true);
      }}
      style={{ ...style, cursor: 'text' }}
    >
      {children ?? tokenPreview(text)}
    </TagName>
  );
}

function resolveThemeColor(value, theme, fallback) {
  if (value === 'primary') return theme.primaryColor;
  if (value === 'secondary') return theme.secondaryColor;
  if (value === 'accent') return theme.accentColor;
  if (value === 'text') return theme.textColor;
  return /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;
}

function colorInputValue(value, fallback = '#0f4c81') {
  return /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;
}

function parseKpiMetrics(content, theme, max = 6) {
  const source = parseKpiMetricDefinitions(content);
  return source.slice(0, max).map((metric, index) => ({
    label: metric.label,
    value: tokenPreview(metric.token || metric.value || ''),
    token: metric.token || '',
    color: resolveThemeColor(metric.color, theme, [theme.primaryColor, theme.secondaryColor, '#dc2626', '#16a34a'][index % 4]),
  }));
}

function parseKpiMetricDefinitions(content) {
  const lines = String(content || '').split('\n').map(line => line.trim()).filter(Boolean);
  if (lines.length === 0) return REPORT_KPI_PRESETS.slice(0, 4);

  return lines.map(line => {
    const parts = line.split('|').map(part => part.trim());
    return {
      label: parts[0] || 'Metric',
      token: parts[1] || '{{security.score}}',
      color: parts[2] || '',
    };
  });
}

function createKpiMetricElement(metric, index = 0, id = '') {
  return {
    id: id || uid('element-metric'),
    type: 'metric',
    label: metric.label || 'Metric',
    content: metric.token || metric.value || '{{security.score}}',
    query: '',
    chartType: '',
    imageUrl: '',
    altText: '',
    layout: defaultElementLayout('metric', index),
    style: { color: metric.color || 'primary' },
  };
}

function kpiMetricElementsFromContent(content, blockId = 'kpi') {
  return parseKpiMetricDefinitions(content)
    .slice(0, 12)
    .map((metric, index) => createKpiMetricElement(metric, index, `${blockId}-metric-${index}`));
}

function axisTitle(item, axis, fallback) {
  return String(item?.style?.[axis] || fallback || '').trim();
}

function chartColors(item, theme) {
  const style = item?.style || {};
  return [
    resolveThemeColor(style.chartPrimaryColor || 'primary', theme, theme.primaryColor),
    resolveThemeColor(style.chartSecondaryColor || 'secondary', theme, theme.secondaryColor),
    resolveThemeColor(style.chartTertiaryColor || 'accent', theme, theme.accentColor),
    resolveThemeColor(style.chartQuaternaryColor || '#f97316', theme, '#f97316'),
  ];
}

function chartTitleFor(item, fallback) {
  return tokenPreview(item?.title || item?.label || fallback || 'Chart');
}

function chartAxisDefaults(chartType) {
  const type = String(chartType || 'severity').toLowerCase();
  if (type === 'scoretrend' || type === 'line' || type === 'dualline') return ['Reporting Period', 'Security Score', 'Open Findings'];
  if (type === 'riskmix' || type === 'stackedbar') return ['Risk Category', 'Finding Count', ''];
  return ['Severity', 'Finding Count', ''];
}

function reportQueryFindings(query = '') {
  const parts = String(query || '').toLowerCase()
    .replaceAll('{{', '')
    .replaceAll('}}', '')
    .replaceAll('findings', '')
    .replaceAll('finding', '')
    .replaceAll('where', '')
    .replaceAll('and', '')
    .replaceAll('=', ' ')
    .replaceAll('"', ' ')
    .replaceAll("'", ' ')
    .split(/[\s.,:;|/\\]+/)
    .filter(Boolean);
  const severities = parts.filter(part => ['critical', 'high', 'medium', 'low'].includes(part));
  const tags = parts.filter(part => !['critical', 'high', 'medium', 'low', 'open', 'top', 'tag', 'tags', 'filter'].includes(part));
  return REPORT_DESIGNER_SAMPLE.findings.filter(finding => {
    const severityMatch = severities.length === 0 || severities.includes(finding.severity.toLowerCase());
    const haystack = `${finding.title} ${finding.tags.join(' ')}`.toLowerCase();
    const tagMatch = tags.length === 0 || tags.every(tag => haystack.includes(tag));
    return severityMatch && tagMatch;
  });
}

function applyFindingTokens(template = '', finding = {}) {
  return tokenPreview(String(template || '')
    .replaceAll('{{finding.title}}', finding.title || 'Sample finding')
    .replaceAll('{{finding.severity}}', finding.severity || 'Critical')
    .replaceAll('{{finding.status}}', finding.status || 'Open')
    .replaceAll('{{finding.ageDays}}', String(finding.age ?? 0))
    .replaceAll('{{finding.cve}}', finding.cve || 'CVE-2026-0001')
    .replaceAll('{{finding.cvss}}', finding.cvss || '9.1')
    .replaceAll('{{finding.fingerprint}}', finding.fingerprint || 'sample:finding'));
}

function splitRepeaterTableLine(line = '') {
  return String(line || '').trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(part => part.trim()).filter(Boolean);
}

function parseRepeaterTableTemplate(content = '') {
  const lines = String(content || '').split('\n').map(line => line.trim()).filter(line => line && line.includes('|') && !line.includes('---'));
  if (lines.length >= 2) {
    const headers = splitRepeaterTableLine(lines[0]);
    const cells = splitRepeaterTableLine(lines[1]);
    if (headers.length && cells.length === headers.length) return { headers, cells };
  }
  return {
    headers: ['Severity', 'Finding', 'Age', 'Status'],
    cells: ['{{finding.severity}}', '{{finding.title}}', '{{finding.ageDays}}d', '{{finding.status}}'],
  };
}

function repeaterLimit(item, fallback = 6) {
  return clampNumber(item?.style?.limit || fallback, 1, 50);
}

function createReportBlock(type, slot) {
  const reportType = String(slot?.reportType || 'monthly').toLowerCase();
  const base = { id: uid(type), type, title: '', content: '', query: '', chartType: '', imageUrl: '', altText: '', layout: defaultFrameLayout(), style: {}, components: [], elements: [] };
  if (type === 'cover') {
    const cover = {
      ...base,
      title: reportType === 'qbr' ? 'QUARTERLY BUSINESS REVIEW' : reportType === 'compliance' ? 'MONTHLY COMPLIANCE REPORT' : 'Monthly Security Report',
      content: reportType === 'qbr'
        ? 'Executive posture, security outcomes, business risk, hard priorities, and next-quarter operating plan.'
        : 'Prepared for {{client.name}} for {{report.period}}.',
    };
    return { ...cover, components: createCoverComponents(cover) };
  }
  if (type === 'kpi') {
    const content = REPORT_KPI_PRESETS.slice(0, 4).map(metric => `${metric.label} | ${metric.token} | ${metric.color}`).join('\n');
    return { ...base, title: 'Security KPIs', content, elements: kpiMetricElementsFromContent(content, base.id) };
  }
  if (type === 'chart') return { ...base, title: 'Finding Severity Distribution', chartType: 'severity', style: { axisTitleX: 'Severity', axisTitleY: 'Finding Count' } };
  if (type === 'findings') return { ...base, title: 'Critical Cloud Findings', query: 'critical cloud' };
  if (type === 'findingCard') return { ...base, title: 'Priority Remediation Card', query: 'critical vulnerability', content: 'Recommended action: assign an owner, validate exposure, and confirm remediation evidence.' };
  if (type === 'findingRepeater') return {
    ...base,
    title: 'Detailed Findings',
    query: 'critical high vulnerability',
    content: '{{finding.title}}\nSeverity: {{finding.severity}}\nStatus: {{finding.status}}\nAge: {{finding.ageDays}} days\nRecommended action: assign an owner, validate exposure, capture evidence, and confirm remediation.',
    style: { limit: '8', layout: 'cards', emptyText: 'No matching findings were available for this report period.' },
  };
  if (type === 'tableRepeater') return {
    ...base,
    title: 'Finding Summary Table',
    query: 'critical high vulnerability',
    content: 'Severity | Finding | Age | Status\n{{finding.severity}} | {{finding.title}} | {{finding.ageDays}}d | {{finding.status}}',
    style: { limit: '12', emptyText: 'No matching findings were available for this report period.' },
  };
  if (type === 'table') return { ...base, title: 'Data at a Glance', content: '| Metric | Value |\n| --- | ---: |\n| Security score | {{security.score}} |\n| Open findings | {{findings.totalOpen}} |\n| Critical findings | {{findings.criticalCount}} |' };
  if (type === 'toc') return { ...base, title: 'Table of Contents', content: '' };
  if (type === 'pageBreak') return { ...base, title: 'Page Break', content: '' };
  if (type === 'appendix') return { ...base, title: 'Appendix', content: 'Supporting evidence, scope notes, and detailed finding exports for {{report.period}}.' };
  if (type === 'image') return { ...base, title: 'Program Infographic', content: 'Risk posture context for {{report.period}}.', altText: 'Program infographic' };
  return {
    ...base,
    title: reportType === 'qbr' ? 'Executive Summary' : 'Business Risk Summary',
    content: '{{client.name}} has {{findings.totalOpen}} open findings for {{report.period}}. The cloud-only findings table highlight is {{critical.cloud.finding}}. The overall priority remediation is shown separately on the Priority Remediation Card.',
  };
}

function createReportElement(type, index = 0) {
  const base = { id: uid(`element-${type}`), type, label: '', content: '', query: '', chartType: '', imageUrl: '', altText: '', layout: defaultElementLayout(type, index), style: {} };
  if (type === 'heading') return { ...base, content: 'Section heading for {{client.name}}' };
  if (type === 'paragraph') return { ...base, content: '{{client.name}} has {{findings.totalOpen}} open findings for {{report.period}}.' };
  if (type === 'metric') return { ...base, label: 'Open findings', content: '{{findings.totalOpen}}' };
  if (type === 'chart') return { ...base, label: 'Finding Severity Distribution', chartType: 'severity', style: { axisTitleX: 'Severity', axisTitleY: 'Finding Count' } };
  if (type === 'findings') return { ...base, label: 'Critical Cloud Findings', query: 'critical cloud' };
  if (type === 'findingRepeater') return { ...base, label: 'Detailed Findings', query: 'critical high vulnerability', content: '{{finding.title}}\nSeverity: {{finding.severity}}\nAge: {{finding.ageDays}} days', style: { limit: '4', layout: 'cards' } };
  if (type === 'tableRepeater') return { ...base, label: 'Finding Summary Table', query: 'critical high vulnerability', content: 'Severity | Finding | Age\n{{finding.severity}} | {{finding.title}} | {{finding.ageDays}}d', style: { limit: '6' } };
  if (type === 'image') return { ...base, label: 'Program Infographic', content: 'Risk posture context for {{report.period}}.', altText: 'Program infographic' };
  if (type === 'table') return { ...base, label: 'Data at a Glance', content: '| Metric | Value |\n| --- | ---: |\n| Security score | {{security.score}} |\n| Open findings | {{findings.totalOpen}} |' };
  if (type === 'divider') return base;
  return base;
}

function defaultDefinition(slot) {
  const type = String(slot?.reportType || '').toLowerCase();
  const blocks = [
    createReportBlock('cover', slot),
    createReportBlock('toc', slot),
    createReportBlock('text', slot),
    createReportBlock('kpi', slot),
    createReportBlock('chart', slot),
    createReportBlock('findings', slot),
    createReportBlock('findingCard', slot),
  ];
  if (type === 'qbr') {
    return {
      ...EMPTY_TEMPLATE_DEFINITION,
      coverTitle: 'QUARTERLY BUSINESS REVIEW',
      coverSubtitle: 'Executive posture, security outcomes, business risk, hard priorities, and next-quarter operating plan.',
      executiveSummaryHeading: 'Security Posture at a Glance',
      executiveSummaryIntro: '{{client.name}} security posture and executive risk movement for {{qbr.quarter}}.',
      blocks,
    };
  }
  if (type === 'compliance') {
    return {
      ...EMPTY_TEMPLATE_DEFINITION,
      coverTitle: 'MONTHLY COMPLIANCE REPORT',
      coverSubtitle: 'Compliance gap overview, POA&M status, control coverage, evidence context, and remediation tracking.',
      executiveSummaryHeading: 'Executive Summary',
      executiveSummaryIntro: 'This monthly compliance report presents the current control posture for {{client.name}} for {{report.period}}.',
      blocks,
    };
  }
  return {
    ...EMPTY_TEMPLATE_DEFINITION,
    coverTitle: 'Monthly Security Report',
    coverSubtitle: 'A client-facing briefing of current exposure, remediation priority, score movement, attack-path context, and next-month focus.',
    executiveSummaryHeading: 'Executive · Business Risk Summary',
    executiveSummaryIntro: 'This monthly security briefing summarizes the current risk posture for {{client.name}} for {{report.period}}.',
    blocks,
  };
}

function normalizeDefinition(slot, raw) {
  const fallback = defaultDefinition(slot);
  const incoming = raw && typeof raw === 'object' ? raw : {};
  const blocks = Array.isArray(incoming.blocks) && incoming.blocks.length
    ? incoming.blocks.map((block, index) => {
      const normalizedBlock = {
        id: block.id || uid(`block-${index}`),
        type: block.type || 'text',
        title: block.title || '',
        content: block.content || '',
        query: block.query || '',
        chartType: block.chartType || '',
        imageUrl: block.imageUrl || '',
        altText: block.altText || '',
        layout: normalizeFrameLayout(block.layout),
        style: normalizeStyle(block.style, {}),
        components: [],
        elements: Array.isArray(block.elements) ? block.elements.map((element, elementIndex) => {
          const elementType = element.type || 'paragraph';
          return {
            id: element.id || uid(`element-${index}-${elementIndex}`),
            type: elementType,
            label: element.label || '',
            content: element.content || '',
            query: element.query || '',
            chartType: element.chartType || '',
            imageUrl: element.imageUrl || '',
            altText: element.altText || '',
            layout: hasIntentionalLayout(element.layout)
              ? normalizeFrameLayout(element.layout)
              : defaultElementLayout(elementType, elementIndex),
            style: normalizeStyle(element.style, {}),
          };
        }) : [],
      };
      if (String(normalizedBlock.type || '').toLowerCase() === 'kpi' && normalizedBlock.elements.length === 0) {
        normalizedBlock.elements = kpiMetricElementsFromContent(normalizedBlock.content, normalizedBlock.id);
      }
      const defaultComponents = String(normalizedBlock.type || '').toLowerCase() === 'cover'
        ? createCoverComponents(normalizedBlock)
        : [];
      const incomingComponents = Array.isArray(block.components) && block.components.length ? block.components : defaultComponents;
      normalizedBlock.components = incomingComponents.map((component, componentIndex) => normalizeCoverComponent(component, componentIndex, defaultComponents[componentIndex]));
      return normalizedBlock;
    })
    : fallback.blocks;

  return {
    ...fallback,
    ...incoming,
    theme: { ...DEFAULT_REPORT_THEME, ...(fallback.theme || {}), ...(incoming.theme || {}) },
    fieldMappings: { ...DEFAULT_TEMPLATE_FIELD_MAPPINGS, ...(fallback.fieldMappings || {}), ...(incoming.fieldMappings || {}) },
    blocks,
    assets: Array.isArray(incoming.assets) ? incoming.assets : [],
  };
}

function ReportTemplatesTab() {
  const api = useApiClient();
  const fileRef = useRef(null);
  const importFileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [slots, setSlots] = useState(REPORT_TEMPLATE_FALLBACK_SLOTS);
  const [items, setItems] = useState([]);
  const [selectedKey, setSelectedKey] = useState(templateKey(REPORT_TEMPLATE_FALLBACK_SLOTS[0]));
  const [selectedFile, setSelectedFile] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [definition, setDefinition] = useState(() => normalizeDefinition(REPORT_TEMPLATE_FALLBACK_SLOTS[0]));
  const [selectedBlockId, setSelectedBlockId] = useState('');
  const [selectedElementId, setSelectedElementId] = useState('');
  const [selectedComponentId, setSelectedComponentId] = useState('');

  const loadTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/msp/report-templates');
      const nextSlots = Array.isArray(data?.supported) && data.supported.length ? data.supported : REPORT_TEMPLATE_FALLBACK_SLOTS;
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setSlots(nextSlots);
      setItems(nextItems);
      if (!nextSlots.some(slot => templateKey(slot) === selectedKey)) {
        setSelectedKey(templateKey(nextSlots[0] || REPORT_TEMPLATE_FALLBACK_SLOTS[0]));
      }
    } catch (err) {
      setError(err?.message || 'Failed to load report templates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTemplates(); }, []);

  const selectedSlot = slots.find(slot => templateKey(slot) === selectedKey) || slots[0] || REPORT_TEMPLATE_FALLBACK_SLOTS[0];
  const selectedItems = items.filter(item => templateKey(item) === selectedKey);
  const activeTemplate = selectedItems.find(item => String(item.status).toLowerCase() === 'active') || null;
  const draftTemplate = selectedItems.find(item => String(item.status).toLowerCase() === 'draft') || null;
  const editableTemplate = draftTemplate || activeTemplate;
  const supportsUpload = ['docx', 'pptx'].includes(String(selectedSlot.outputFormat).toLowerCase());
  const isWordOutput = String(selectedSlot.outputFormat).toLowerCase() === 'docx';
  const supportsImportWizard = isWordOutput;
  const usesDefault = !activeTemplate;
  const blocks = Array.isArray(definition.blocks) ? definition.blocks : [];
  const theme = { ...DEFAULT_REPORT_THEME, ...(definition.theme || {}) };
  const fieldMappings = { ...DEFAULT_TEMPLATE_FIELD_MAPPINGS, ...(definition.fieldMappings || {}) };
  const selectedBlock = blocks.find(block => block.id === selectedBlockId) || blocks[0] || null;
  const selectedComponents = Array.isArray(selectedBlock?.components) ? selectedBlock.components : [];
  const selectedComponent = selectedComponents.find(component => component.id === selectedComponentId) || selectedComponents[0] || null;
  const selectedElements = Array.isArray(selectedBlock?.elements) ? selectedBlock.elements : [];
  const selectedElement = selectedElements.find(element => element.id === selectedElementId) || selectedElements[0] || null;

  useEffect(() => {
    const nextDefinition = normalizeDefinition(selectedSlot, editableTemplate?.definition);
    setDefinition(nextDefinition);
    setSelectedBlockId(nextDefinition.blocks?.[0]?.id || '');
    setSelectedElementId(nextDefinition.blocks?.[0]?.elements?.[0]?.id || '');
    setSelectedComponentId(nextDefinition.blocks?.[0]?.components?.[0]?.id || '');
    setSelectedFile(null);
    setImportFile(null);
    setImportPreview(null);
    if (fileRef.current) fileRef.current.value = '';
    if (importFileRef.current) importFileRef.current.value = '';
  }, [selectedKey, editableTemplate?.templateId]);

  const updateDefinition = (field, value) => setDefinition(prev => ({ ...prev, [field]: value }));
  const updateTheme = (field, value) => {
    setDefinition(prev => ({ ...prev, theme: { ...DEFAULT_REPORT_THEME, ...(prev.theme || {}), [field]: value } }));
  };
  const updateFieldMapping = (field, value) => {
    setDefinition(prev => ({
      ...prev,
      fieldMappings: { ...DEFAULT_TEMPLATE_FIELD_MAPPINGS, ...(prev.fieldMappings || {}), [field]: value },
    }));
  };
  const updateBlocks = updater => {
    setDefinition(prev => {
      const current = Array.isArray(prev.blocks) ? prev.blocks : [];
      return { ...prev, blocks: typeof updater === 'function' ? updater(current) : updater };
    });
  };
  const updateBlock = (id, patch) => {
    updateBlocks(current => current.map(block => (block.id === id
      ? {
        ...block,
        ...patch,
        layout: patch.layout ? { ...(block.layout || defaultFrameLayout()), ...patch.layout } : block.layout,
        style: patch.style ? { ...(block.style || {}), ...patch.style } : block.style,
      }
      : block)));
  };
  const updateBlockLayout = (id, key, value) => {
    updateBlock(id, { layout: { [key]: ['x', 'y', 'width', 'height'].includes(key) ? Number(value) : value } });
  };
  const updateBlockStyle = (id, key, value) => {
    updateBlock(id, { style: { [key]: String(value) } });
  };
  const addBlock = type => {
    const block = createReportBlock(type, selectedSlot);
    updateBlocks(current => [...current, block]);
    setSelectedBlockId(block.id);
    setSelectedElementId(block.elements?.[0]?.id || '');
    setSelectedComponentId(block.components?.[0]?.id || '');
  };
  const duplicateBlock = id => {
    const block = blocks.find(item => item.id === id);
    if (!block) return;
    const clone = { ...block, id: uid(block.type), title: `${block.title || 'Block'} copy` };
    updateBlocks(current => {
      const index = current.findIndex(item => item.id === id);
      const next = [...current];
      next.splice(index + 1, 0, clone);
      return next;
    });
    setSelectedBlockId(clone.id);
    setSelectedElementId(clone.elements?.[0]?.id || '');
    setSelectedComponentId(clone.components?.[0]?.id || '');
  };
  const removeBlock = id => {
    updateBlocks(current => {
      const next = current.filter(block => block.id !== id);
      if (selectedBlockId === id) setSelectedBlockId(next[0]?.id || '');
      return next;
    });
  };
  const moveBlock = (id, direction) => {
    updateBlocks(current => {
      const index = current.findIndex(block => block.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      const [block] = next.splice(index, 1);
      next.splice(target, 0, block);
      return next;
    });
  };
  const moveBlockToIndex = (id, targetIndex) => {
    updateBlocks(current => {
      const index = current.findIndex(block => block.id === id);
      if (index < 0 || targetIndex < 0 || targetIndex >= current.length || index === targetIndex) return current;
      const next = [...current];
      const [block] = next.splice(index, 1);
      next.splice(targetIndex, 0, block);
      return next;
    });
  };
  const updateComponents = updater => {
    if (!selectedBlock) return;
    updateBlock(selectedBlock.id, {
      components: typeof updater === 'function' ? updater(selectedComponents) : updater,
    });
  };
  const updateComponent = (id, patch) => {
    updateComponents(current => current.map(component => (
      component.id === id
        ? {
          ...component,
          ...patch,
          layout: patch.layout ? { ...(component.layout || {}), ...patch.layout } : component.layout,
          style: patch.style ? { ...(component.style || {}), ...patch.style } : component.style,
        }
        : component
    )));
  };
  const updateComponentLayout = (id, key, value) => {
    updateComponent(id, { layout: { [key]: ['x', 'y', 'width', 'height'].includes(key) ? Number(value) : value } });
  };
  const updateComponentStyle = (id, key, value) => {
    updateComponent(id, { style: { [key]: String(value) } });
  };
  const moveComponentToPoint = (id, point) => {
    const component = selectedComponents.find(item => item.id === id);
    if (!component) return;
    const width = Number(component.layout?.width) || 12;
    const height = Number(component.layout?.height) || 8;
    updateComponent(id, {
      layout: {
        x: clampNumber(point.x - width / 2, 0, Math.max(0, 100 - width)),
        y: clampNumber(point.y - height / 2, 0, Math.max(0, 100 - height)),
      },
    });
    setSelectedComponentId(id);
  };
  const updateElements = updater => {
    if (!selectedBlock) return;
    updateBlock(selectedBlock.id, {
      elements: typeof updater === 'function' ? updater(selectedElements) : updater,
    });
  };
  const addElement = type => {
    if (!selectedBlock) return;
    const element = createReportElement(type, selectedElements.length);
    updateElements(current => [...current, element]);
    setSelectedElementId(element.id);
  };
  const updateElement = (id, patch) => {
    updateElements(current => current.map(element => (element.id === id
      ? {
        ...element,
        ...patch,
        layout: patch.layout ? { ...(element.layout || defaultFrameLayout()), ...patch.layout } : element.layout,
        style: patch.style ? { ...(element.style || {}), ...patch.style } : element.style,
      }
      : element)));
  };
  const updateElementLayout = (id, key, value) => {
    updateElement(id, { layout: { [key]: ['x', 'y', 'width', 'height'].includes(key) ? Number(value) : value } });
  };
  const updateElementStyle = (id, key, value) => {
    updateElement(id, { style: { [key]: String(value) } });
  };
  const moveElementLayoutInBlockToPoint = (blockId, elementId, point) => {
    updateBlocks(current => current.map(block => {
      if (block.id !== blockId) return block;
      const nextElements = Array.isArray(block.elements) ? block.elements.map(element => {
        if (element.id !== elementId) return element;
        const layout = normalizeLayout(element.layout, defaultFrameLayout());
        return {
          ...element,
          layout: {
            ...(element.layout || defaultFrameLayout()),
            x: clampNumber(point.x - layout.width / 2, 0, Math.max(0, 100 - layout.width)),
            y: clampNumber(point.y, 0, 700),
          },
        };
      }) : [];
      return { ...block, elements: nextElements };
    }));
  };
  const moveElement = (id, direction) => {
    updateElements(current => {
      const index = current.findIndex(element => element.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      const [element] = next.splice(index, 1);
      next.splice(target, 0, element);
      return next;
    });
  };
  const moveElementToIndex = (id, targetIndex) => {
    updateElements(current => {
      const index = current.findIndex(element => element.id === id);
      if (index < 0 || targetIndex < 0 || targetIndex >= current.length || index === targetIndex) return current;
      const next = [...current];
      const [element] = next.splice(index, 1);
      next.splice(targetIndex, 0, element);
      return next;
    });
  };
  const duplicateElement = id => {
    const element = selectedElements.find(item => item.id === id);
    if (!element) return;
    const clone = { ...element, id: uid(`element-${element.type}`), label: `${element.label || element.type} copy` };
    updateElements(current => {
      const index = current.findIndex(item => item.id === id);
      const next = [...current];
      next.splice(index + 1, 0, clone);
      return next;
    });
    setSelectedElementId(clone.id);
  };
  const removeElement = id => {
    updateElements(current => {
      const next = current.filter(element => element.id !== id);
      if (selectedElementId === id) setSelectedElementId(next[0]?.id || '');
      return next;
    });
  };
  const appendToken = token => {
    if (selectedElement) {
      updateElement(selectedElement.id, { content: `${selectedElement.content || ''}${selectedElement.content ? ' ' : ''}${token}` });
      return;
    }
    if (!selectedBlock) {
      updateDefinition('executiveSummaryIntro', `${definition.executiveSummaryIntro || ''}${definition.executiveSummaryIntro ? ' ' : ''}${token}`);
      return;
    }
    updateBlock(selectedBlock.id, { content: `${selectedBlock.content || ''}${selectedBlock.content ? ' ' : ''}${token}` });
  };
  const appendKpiMetric = (preset, target = 'block') => {
    const line = `${preset.label} | ${preset.token} | ${preset.color}`;
    if (target === 'element' && selectedElement) {
      updateElement(selectedElement.id, {
        content: `${selectedElement.content || ''}${selectedElement.content ? '\n' : ''}${line}`,
      });
      return;
    }
    if (selectedBlock && String(selectedBlock.type || '').toLowerCase() === 'kpi') {
      const element = createKpiMetricElement(preset, selectedElements.length);
      updateBlock(selectedBlock.id, {
        content: `${selectedBlock.content || ''}${selectedBlock.content ? '\n' : ''}${line}`,
        elements: [...selectedElements, element],
      });
      setSelectedElementId(element.id);
      setSelectedComponentId('');
      return;
    }
    if (selectedBlock) {
      updateBlock(selectedBlock.id, {
        content: `${selectedBlock.content || ''}${selectedBlock.content ? '\n' : ''}${line}`,
      });
    }
  };

  const saveDraft = async ({ quiet = false } = {}) => {
    setSaving(true);
    setError('');
    if (!quiet) setMessage('');
    try {
      const saved = await api.post('/msp/report-templates', {
        templateId: draftTemplate?.templateId || null,
        reportType: selectedSlot.reportType,
        outputFormat: selectedSlot.outputFormat,
        name: editableTemplate?.name || selectedSlot.label,
        definition,
      });
      if (!quiet) setMessage('Draft saved.');
      await loadTemplates();
      return saved;
    } catch (err) {
      setError(err?.message || 'Failed to save template draft.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Choose a DOCX or PPTX template file first.');
      return;
    }
    const draft = draftTemplate || await saveDraft({ quiet: true });
    if (!draft?.templateId) return;

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const form = new FormData();
      form.append('template', selectedFile);
      await api.post(`/msp/report-templates/${draft.templateId}/upload`, form);
      setMessage('Template file uploaded and validated.');
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await loadTemplates();
    } catch (err) {
      setError(err?.message || 'Failed to upload template file.');
    } finally {
      setSaving(false);
    }
  };

  const handleImportPreview = async () => {
    if (!importFile) {
      setError('Choose a DOCX reference report first.');
      return;
    }
    if (!importFile.name.toLowerCase().endsWith('.docx')) {
      setError('The import wizard currently accepts DOCX reference reports.');
      return;
    }

    setImporting(true);
    setError('');
    setMessage('');
    setImportPreview(null);
    try {
      const form = new FormData();
      form.append('template', importFile);
      form.append('reportType', selectedSlot.reportType);
      form.append('outputFormat', selectedSlot.outputFormat);
      const preview = await api.post('/msp/report-templates/import-preview', form);
      setImportPreview(preview);
      setMessage(`Import preview ready for ${preview?.fileName || importFile.name}.`);
    } catch (err) {
      setError(err?.message || 'Failed to inspect reference report.');
    } finally {
      setImporting(false);
    }
  };

  const applyImportPreview = () => {
    if (!importPreview?.definition) {
      setError('Run the import wizard before applying a draft.');
      return;
    }
    const nextDefinition = normalizeDefinition(selectedSlot, importPreview.definition);
    setDefinition(nextDefinition);
    setSelectedBlockId(nextDefinition.blocks?.[0]?.id || '');
    setSelectedElementId(nextDefinition.blocks?.[0]?.elements?.[0]?.id || '');
    setSelectedComponentId(nextDefinition.blocks?.[0]?.components?.[0]?.id || '');
    setMessage('Suggested editable template applied. Review the pages, then Save Draft.');
  };

  const handleTemplateImageUpload = async (file, applyImage) => {
    if (!file) return;
    setError('');
    setMessage('');
    if (!REPORT_IMAGE_ACCEPT.split(',').includes(file.type)) {
      setError('Use a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > REPORT_IMAGE_MAX_BYTES) {
      setError('Template images must be 2 MB or smaller.');
      return;
    }
    try {
      const dataUrl = await readReportImageFile(file);
      applyImage(dataUrl, file.name);
      setMessage(`Image "${file.name}" added to the template. Save Draft to keep it.`);
    } catch (err) {
      setError(err?.message || 'Failed to load image.');
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const draft = await saveDraft({ quiet: true });
      if (!draft?.templateId) {
        setError('Save failed, so the template was not published.');
        return;
      }
      await api.post(`/msp/report-templates/${draft.templateId}/publish`);
      setMessage('Template published. New report downloads will use the MSP override.');
      await loadTemplates();
    } catch (err) {
      setError(err?.message || 'Failed to publish template.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveActive = async () => {
    if (!activeTemplate) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.post(`/msp/report-templates/${activeTemplate.templateId}/archive`);
      setMessage('Active override archived. Downloads will fall back to the current default.');
      await loadTemplates();
    } catch (err) {
      setError(err?.message || 'Failed to archive template.');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = { ...getInputStyle(), fontFamily: FONTS.sans, minHeight: 34 };
  const textAreaStyle = { ...fieldStyle, minHeight: 88, resize: 'vertical', lineHeight: 1.45 };
  const imageUploadInputStyle = { ...fieldStyle, padding: '7px 8px', fontSize: 11, fontFamily: FONTS.mono };
  const renderImageUploadInput = applyImage => (
    <div style={{ display: 'block', marginTop: 8 }}>
      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Upload Image</div>
      <input
        type="file"
        accept={REPORT_IMAGE_ACCEPT}
        onChange={event => {
          const file = event.target.files?.[0];
          void handleTemplateImageUpload(file, applyImage);
          event.target.value = '';
        }}
        style={imageUploadInputStyle}
      />
      <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 4 }}>PNG, JPEG, or WebP up to 2 MB.</div>
    </div>
  );
  const statusLabel = usesDefault
    ? (draftTemplate ? 'Draft only - exports still use default' : 'Default renderer')
    : `MSP override v${activeTemplate.version}`;
  const selectedBlockType = String(selectedBlock?.type || 'text').toLowerCase();
  const selectedElementType = String(selectedElement?.type || 'paragraph').toLowerCase();
  const selectedBlockLayout = selectedBlock ? normalizeLayout(selectedBlock.layout, defaultFrameLayout()) : defaultFrameLayout();
  const selectedElementLayout = selectedElement ? normalizeLayout(selectedElement.layout, defaultFrameLayout()) : defaultFrameLayout();

  return (
    <div>
      <Card style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 5 }}>
              <LayoutTemplate size={16} color={C.sky} />
              <div style={{ fontSize: 14, fontWeight: 800, fontFamily: FONTS.sans, color: C.t1 }}>Report Designer</div>
            </div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
              {selectedSlot.label} · {statusLabel} · {activeTemplate ? `Published ${formatTemplateDate(activeTemplate.publishedAt)}` : 'Current default fallback'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Tag label={usesDefault ? 'Default' : 'Override'} color={usesDefault ? C.t2 : C.grn} />
            {draftTemplate && <Tag label="Draft" color={C.amb} />}
            <Button variant="ghost" onClick={loadTemplates} disabled={loading || saving} style={{ fontSize: 10 }}>
              <RefreshCw size={13} style={{ marginRight: 6 }} /> Refresh
            </Button>
            <Button variant="pri" onClick={() => saveDraft()} disabled={saving || loading}>
              <Save size={13} style={{ marginRight: 6 }} /> Save Draft
            </Button>
            <Button variant="sec" onClick={handlePublish} disabled={saving || loading}>
              <Send size={13} style={{ marginRight: 6 }} /> Save & Publish
            </Button>
          </div>
        </div>
        {error && <div style={{ marginTop: 12, color: C.red, fontSize: 11, fontFamily: FONTS.mono }}>{error}</div>}
        {message && <div style={{ marginTop: 12, color: C.grn, fontSize: 11, fontFamily: FONTS.mono }}>{message}</div>}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(230px, 270px) minmax(520px, 1fr) minmax(280px, 340px)', gap: 14, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase' }}>Outputs</div>
            {slots.map(slot => {
              const key = templateKey(slot);
              const slotItems = items.filter(item => templateKey(item) === key);
              const active = slotItems.find(item => String(item.status).toLowerCase() === 'active');
              const draft = slotItems.find(item => String(item.status).toLowerCase() === 'draft');
              const selected = key === selectedKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedKey(key)}
                  style={{
                    width: '100%', textAlign: 'left', border: `1px solid ${selected ? C.sky : C.b1}`,
                    background: selected ? `${C.sky}12` : C.s2, borderRadius: 7, padding: '10px 11px',
                    marginBottom: 8, cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileText size={14} color={selected ? C.sky : C.t2} />
                    <span style={{ fontSize: 11, color: C.t1, fontWeight: 700, fontFamily: FONTS.sans }}>{slot.label}</span>
                  </div>
                  <div style={{ marginTop: 7, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 9, color: active ? C.grn : C.t2, fontFamily: FONTS.mono }}>{active ? `Active v${active.version}` : 'Default'}</span>
                    {draft && <span style={{ fontSize: 9, color: C.amb, fontFamily: FONTS.mono }}>Draft v{draft.version}</span>}
                  </div>
                </button>
              );
            })}
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase' }}>Outline</div>
            {blocks.map((block, index) => {
              const selected = block.id === selectedBlock?.id;
              return (
                <button
                  key={block.id}
                  type="button"
                  draggable
                  onDragStart={e => e.dataTransfer.setData('text/report-block-id', block.id)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    moveBlockToIndex(e.dataTransfer.getData('text/report-block-id'), index);
                  }}
                  onClick={() => {
                    setSelectedBlockId(block.id);
                    setSelectedElementId(block.elements?.[0]?.id || '');
                    setSelectedComponentId(block.components?.[0]?.id || '');
                  }}
                  style={{
                    width: '100%', display: 'grid', gridTemplateColumns: '18px 12px minmax(0, 1fr)', gap: 8,
                    alignItems: 'center', textAlign: 'left', border: `1px solid ${selected ? C.sky : C.b1}`,
                    background: selected ? '#fff' : C.s2, borderRadius: 7, padding: '9px 10px',
                    marginBottom: 7, cursor: 'pointer',
                  }}
                >
                  <span style={{
                    width: 18, height: 18, borderRadius: 5, background: selected ? C.sky : C.s3,
                    color: selected ? '#fff' : C.t2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 800, fontFamily: FONTS.mono,
                  }}>{index + 1}</span>
                  <GripVertical size={12} color={C.t3} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 11, color: C.t1, fontWeight: 700, fontFamily: FONTS.sans, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {block.title || block.type}
                    </span>
                    <span style={{ display: 'block', fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', marginTop: 2 }}>{block.type}</span>
                  </span>
                </button>
              );
            })}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginTop: 10 }}>
              {REPORT_BLOCK_LIBRARY.filter(item => item.type !== 'cover').map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.type}
                    type="button"
                    onClick={() => addBlock(item.type)}
                    style={{
                      border: `1px solid ${C.b1}`, background: '#fff', borderRadius: 7, padding: '8px 6px',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                      color: C.t1, fontSize: 10, fontWeight: 700, fontFamily: FONTS.sans,
                    }}
                  >
                    <Icon size={12} /> {item.label}
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', borderBottom: `1px solid ${C.b1}`, background: C.s2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.t1, fontSize: 12, fontWeight: 800, fontFamily: FONTS.sans }}>
              <Eye size={15} color={C.sky} /> Document Preview
            </div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {REPORT_BLOCK_LIBRARY.map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.type}
                    type="button"
                    onClick={() => addBlock(item.type)}
                    style={{
                      width: 32, height: 30, borderRadius: 6, border: `1px solid ${C.b1}`, background: '#fff',
                      color: C.t2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    title={`Add ${item.label}`}
                    aria-label={`Add ${item.label}`}
                  >
                    <Icon size={14} />
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ background: '#e8edf3', padding: 24, maxHeight: 920, overflow: 'auto' }}>
            <div style={{ width: 'min(100%, clamp(620px, 56vw, 980px))', margin: '0 auto', display: 'grid', gap: 22, fontFamily: theme.fontFamily }}>
              {blocks.map((block, index) => (
                <div key={block.id} style={{ display: 'grid', gap: 12 }}>
                  {index > 0 && <ReportPageBreak pageNumber={index + 1} theme={theme} />}
                  <div
                    data-report-paper-page="true"
                    style={{
                      width: '100%',
                      aspectRatio: '8.5 / 11',
                      minHeight: 0,
                      position: 'relative',
                      background: theme.surfaceColor,
                      color: theme.textColor,
                      borderRadius: 6,
                      boxShadow: '0 22px 60px rgba(15, 23, 42, 0.18)',
                      overflow: 'hidden',
                    }}
                  >
                    <ReportPreviewBlock
                      block={block}
                      index={index}
                      blockCount={blocks.length}
                      theme={theme}
                      selected={block.id === selectedBlock?.id}
                      selectedElementId={selectedBlock?.id === block.id ? selectedElementId : ''}
                      onSelect={() => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId(block.elements?.[0]?.id || '');
                        setSelectedComponentId(block.components?.[0]?.id || '');
                      }}
                      onSelectElement={elementId => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId(elementId);
                        setSelectedComponentId('');
                      }}
                      selectedComponentId={selectedBlock?.id === block.id ? selectedComponentId : ''}
                      onSelectComponent={componentId => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId('');
                        setSelectedComponentId(componentId);
                      }}
                      onMoveComponent={(componentId, point) => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId('');
                        setSelectedComponentId(componentId);
                        moveComponentToPoint(componentId, point);
                      }}
                      onMoveElement={(elementId, targetIndex) => {
                        setSelectedBlockId(block.id);
                        moveElementToIndex(elementId, targetIndex);
                      }}
                      onMoveElementLayout={(elementId, point) => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId(elementId);
                        setSelectedComponentId('');
                        moveElementLayoutInBlockToPoint(block.id, elementId, point);
                      }}
                      onMoveBlock={targetIndex => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId('');
                        setSelectedComponentId(block.components?.[0]?.id || '');
                        moveBlockToIndex(block.id, targetIndex);
                      }}
                      onDropBlock={(draggedBlockId, targetIndex) => {
                        setSelectedBlockId(draggedBlockId);
                        setSelectedElementId('');
                        setSelectedComponentId('');
                        moveBlockToIndex(draggedBlockId, targetIndex);
                      }}
                      onEditBlockField={(field, value) => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId('');
                        setSelectedComponentId('');
                        updateBlock(block.id, { [field]: value });
                      }}
                      onEditComponentField={(componentId, field, value) => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId('');
                        setSelectedComponentId(componentId);
                        const sourceComponents = Array.isArray(block.components) && block.components.length
                          ? block.components
                          : createCoverComponents(block);
                        updateBlock(block.id, {
                          components: sourceComponents.map(component => (
                            component.id === componentId ? { ...component, [field]: value } : component
                          )),
                        });
                      }}
                      onEditElementField={(elementId, field, value) => {
                        setSelectedBlockId(block.id);
                        setSelectedElementId(elementId);
                        setSelectedComponentId('');
                        updateBlock(block.id, {
                          elements: (Array.isArray(block.elements) ? block.elements : []).map(element => (
                            element.id === elementId ? { ...element, [field]: value } : element
                          )),
                        });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Palette size={15} color={C.sky} />
              <div style={{ fontSize: 12, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans }}>Theme</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
              {[
                ['Primary', 'primaryColor'],
                ['Secondary', 'secondaryColor'],
                ['Accent', 'accentColor'],
                ['Background', 'backgroundColor'],
                ['Surface', 'surfaceColor'],
                ['Text', 'textColor'],
              ].map(([label, key]) => (
                <label key={key} style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{label}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 6 }}>
                    <input type="color" value={theme[key] || DEFAULT_REPORT_THEME[key]} onChange={e => updateTheme(key, e.target.value)} style={{ width: 34, height: 32, padding: 0, border: `1px solid ${C.b1}`, borderRadius: 6, background: '#fff' }} />
                    <input value={theme[key] || ''} onChange={e => updateTheme(key, e.target.value)} style={{ ...fieldStyle, fontFamily: FONTS.mono, minWidth: 0 }} />
                  </div>
                </label>
              ))}
            </div>
            <label style={{ display: 'block', marginTop: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Body Font</div>
              <select value={theme.fontFamily || DEFAULT_REPORT_THEME.fontFamily} onChange={e => updateTheme('fontFamily', e.target.value)} style={fieldStyle}>
                {REPORT_FONT_OPTIONS.map(font => <option key={font} value={font}>{font}</option>)}
              </select>
            </label>
            <label style={{ display: 'block', marginTop: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Heading Font</div>
              <select value={theme.headingFontFamily || DEFAULT_REPORT_THEME.headingFontFamily} onChange={e => updateTheme('headingFontFamily', e.target.value)} style={fieldStyle}>
                {REPORT_FONT_OPTIONS.map(font => <option key={font} value={font}>{font}</option>)}
              </select>
            </label>
            <label style={{ display: 'block', marginTop: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Cover Style</div>
              <select value={theme.coverStyle || DEFAULT_REPORT_THEME.coverStyle} onChange={e => updateTheme('coverStyle', e.target.value)} style={fieldStyle}>
                {REPORT_COVER_STYLES.map(style => <option key={style} value={style}>{style}</option>)}
              </select>
            </label>
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Layers3 size={15} color={C.sky} />
                <div style={{ fontSize: 12, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans }}>Block</div>
              </div>
              {selectedBlock && (
                <div style={{ display: 'flex', gap: 5 }}>
                  <button type="button" onClick={() => moveBlock(selectedBlock.id, -1)} title="Move up" style={iconButtonStyle()}><ChevronUp size={13} /></button>
                  <button type="button" onClick={() => moveBlock(selectedBlock.id, 1)} title="Move down" style={iconButtonStyle()}><ChevronDown size={13} /></button>
                  <button type="button" onClick={() => duplicateBlock(selectedBlock.id)} title="Duplicate" style={iconButtonStyle()}><Copy size={13} /></button>
                  <button type="button" onClick={() => removeBlock(selectedBlock.id)} title="Delete" style={{ ...iconButtonStyle(), color: C.red }}><Trash2 size={13} /></button>
                </div>
              )}
            </div>
            {selectedBlock ? (
              <>
                <label style={{ display: 'block', marginBottom: 10 }}>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Type</div>
                  <select value={selectedBlock.type || 'text'} onChange={e => updateBlock(selectedBlock.id, { type: e.target.value })} style={fieldStyle}>
                    {REPORT_BLOCK_LIBRARY.map(item => <option key={item.type} value={item.type}>{item.label}</option>)}
                  </select>
                </label>
                <label style={{ display: 'block', marginBottom: 10 }}>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Title</div>
                  <input value={selectedBlock.title || ''} onChange={e => updateBlock(selectedBlock.id, { title: e.target.value })} style={fieldStyle} />
                </label>
                <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, marginBottom: 10, background: '#fff' }}>
                  <div style={{ fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 8 }}>Block Layout</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      ['X Offset %', 'x', 0, 95],
                      ['Top Gap px', 'y', 0, 700],
                      ['Width %', 'width', 10, 100],
                      ['Min Height', 'height', 1, 100],
                    ].map(([label, key, min, max]) => (
                      <label key={key} style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{label}</div>
                        <input
                          type="number"
                          min={min}
                          max={max}
                          value={Math.round(selectedBlockLayout[key] ?? 0)}
                          onChange={e => updateBlockLayout(selectedBlock.id, key, clampNumber(e.target.value, min, max))}
                          style={{ ...fieldStyle, fontFamily: FONTS.mono }}
                        />
                      </label>
                    ))}
                  </div>
                  <label style={{ display: 'block', marginTop: 8 }}>
                    <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Alignment</div>
                    <select value={selectedBlockLayout.align || 'left'} onChange={e => updateBlockLayout(selectedBlock.id, 'align', e.target.value)} style={fieldStyle}>
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                </div>
                {['findings', 'findingcard', 'findingrepeater', 'tablerepeater'].includes(selectedBlockType) && (
                  <label style={{ display: 'block', marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Finding Filter</div>
                    <input value={selectedBlock.query || ''} onChange={e => updateBlock(selectedBlock.id, { query: e.target.value })} placeholder="critical cloud" style={{ ...fieldStyle, fontFamily: FONTS.mono }} />
                  </label>
                )}
                {['findingrepeater', 'tablerepeater'].includes(selectedBlockType) && (
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, marginBottom: 10, background: '#fff' }}>
                    <div style={{ fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 8 }}>Repeater Controls</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Max Items</div>
                        <input type="number" min="1" max="50" value={selectedBlock.style?.limit || '10'} onChange={e => updateBlockStyle(selectedBlock.id, 'limit', clampNumber(e.target.value, 1, 50))} style={{ ...fieldStyle, fontFamily: FONTS.mono }} />
                      </label>
                      <label>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Layout</div>
                        <select value={selectedBlock.style?.layout || (selectedBlockType === 'tablerepeater' ? 'table' : 'cards')} onChange={e => updateBlockStyle(selectedBlock.id, 'layout', e.target.value)} style={fieldStyle}>
                          <option value="cards">Cards</option>
                          <option value="table">Table</option>
                          <option value="compact">Compact</option>
                        </select>
                      </label>
                    </div>
                    <label style={{ display: 'block', marginTop: 8 }}>
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Empty State</div>
                      <input value={selectedBlock.style?.emptyText || ''} onChange={e => updateBlockStyle(selectedBlock.id, 'emptyText', e.target.value)} placeholder="No matching findings were available." style={fieldStyle} />
                    </label>
                  </div>
                )}
                {selectedBlockType === 'chart' && (
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, marginBottom: 10, background: '#fff' }}>
                    <label style={{ display: 'block', marginBottom: 8 }}>
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Chart Format</div>
                      <select value={selectedBlock.chartType || 'severity'} onChange={e => updateBlock(selectedBlock.id, { chartType: e.target.value })} style={fieldStyle}>
                        {REPORT_CHART_TYPES.map(chart => <option key={chart.value} value={chart.value}>{chart.label}</option>)}
                      </select>
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>X Axis Title</div>
                        <input value={selectedBlock.style?.axisTitleX || chartAxisDefaults(selectedBlock.chartType)[0]} onChange={e => updateBlockStyle(selectedBlock.id, 'axisTitleX', e.target.value)} style={fieldStyle} />
                      </label>
                      <label>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Y Axis Title</div>
                        <input value={selectedBlock.style?.axisTitleY || chartAxisDefaults(selectedBlock.chartType)[1]} onChange={e => updateBlockStyle(selectedBlock.id, 'axisTitleY', e.target.value)} style={fieldStyle} />
                      </label>
                    </div>
                    {String(selectedBlock.chartType || '').toLowerCase() === 'dualline' && (
                      <label style={{ display: 'block', marginTop: 8 }}>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Secondary Y Axis Title</div>
                        <input value={selectedBlock.style?.axisTitleY2 || chartAxisDefaults(selectedBlock.chartType)[2]} onChange={e => updateBlockStyle(selectedBlock.id, 'axisTitleY2', e.target.value)} style={fieldStyle} />
                      </label>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                      {REPORT_CHART_COLOR_FIELDS.map(([label, key], index) => (
                        <label key={key} style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{label}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 6 }}>
                            <input
                              type="color"
                              value={colorInputValue(selectedBlock.style?.[key], [theme.primaryColor, theme.secondaryColor, theme.accentColor, '#f97316'][index])}
                              onChange={e => updateBlockStyle(selectedBlock.id, key, e.target.value)}
                              style={{ width: 34, height: 32, padding: 0, border: `1px solid ${C.b1}`, borderRadius: 6, background: '#fff' }}
                            />
                            <input value={selectedBlock.style?.[key] || ''} onChange={e => updateBlockStyle(selectedBlock.id, key, e.target.value)} placeholder={['primary', 'secondary', 'accent', '#f97316'][index]} style={{ ...fieldStyle, minWidth: 0, fontFamily: FONTS.mono }} />
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {selectedBlockType === 'kpi' && (
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, marginBottom: 10, background: '#fff' }}>
                    <div style={{ fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 8 }}>KPI Metrics</div>
                    <textarea
                      value={selectedBlock.content || ''}
                      onChange={e => updateBlock(selectedBlock.id, { content: e.target.value })}
                      placeholder="Security Score | {{security.score}} | primary"
                      style={{ ...textAreaStyle, minHeight: 110, fontFamily: FONTS.mono }}
                    />
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {REPORT_KPI_PRESETS.map(preset => (
                        <button
                          key={preset.token}
                          type="button"
                          onClick={() => appendKpiMetric(preset)}
                          style={{ border: `1px solid ${C.b1}`, background: C.s2, borderRadius: 6, padding: '5px 6px', fontSize: 9, color: C.t1, fontFamily: FONTS.mono, cursor: 'pointer' }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {['image', 'infographic'].includes(selectedBlockType) && (
                  <>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Image URL</div>
                      <input value={selectedBlock.imageUrl || ''} onChange={e => updateBlock(selectedBlock.id, { imageUrl: e.target.value })} style={fieldStyle} />
                      {renderImageUploadInput((imageUrl, fileName) => updateBlock(selectedBlock.id, {
                        imageUrl,
                        altText: selectedBlock.altText || fileName,
                      }))}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Alt Text</div>
                      <input value={selectedBlock.altText || ''} onChange={e => updateBlock(selectedBlock.id, { altText: e.target.value })} style={fieldStyle} />
                    </label>
                  </>
                )}
                {!['kpi', 'chart', 'findings', 'toc', 'pagebreak'].includes(selectedBlockType) && (
                  <label style={{ display: 'block', marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Content</div>
                    <textarea value={selectedBlock.content || ''} onChange={e => updateBlock(selectedBlock.id, { content: e.target.value })} style={{ ...textAreaStyle, minHeight: 120 }} />
                  </label>
                )}
                {selectedBlockType === 'cover' && (
                  <div style={{ borderTop: `1px solid ${C.b1}`, marginTop: 12, paddingTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 9 }}>
                      <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, fontWeight: 800, textTransform: 'uppercase' }}>Cover Components</div>
                      <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{selectedComponents.length} editable</span>
                    </div>
                    {selectedComponents.map(component => {
                      const selected = component.id === selectedComponent?.id;
                      return (
                        <button
                          key={component.id}
                          type="button"
                          onClick={() => {
                            setSelectedComponentId(component.id);
                            setSelectedElementId('');
                          }}
                          style={{
                            width: '100%', display: 'grid', gridTemplateColumns: '14px minmax(0, 1fr) auto', gap: 7,
                            alignItems: 'center', textAlign: 'left', border: `1px solid ${selected ? C.sky : C.b1}`,
                            background: selected ? `${C.sky}10` : C.s2, borderRadius: 7, padding: '8px 9px',
                            marginBottom: 7, cursor: 'pointer',
                          }}
                        >
                          <GripVertical size={12} color={selected ? C.sky : C.t3} />
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {component.label || component.type}
                            </span>
                            <span style={{ display: 'block', fontSize: 8, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', marginTop: 2 }}>{component.type}</span>
                          </span>
                          <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{Math.round(component.layout?.x ?? 0)}%, {Math.round(component.layout?.y ?? 0)}%</span>
                        </button>
                      );
                    })}
                    {selectedComponent && (
                      <div style={{ marginTop: 10, padding: 10, border: `1px solid ${C.b1}`, borderRadius: 7, background: '#fff' }}>
                        <div style={{ fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 10 }}>Selected Component</div>
                        <label style={{ display: 'block', marginBottom: 8 }}>
                          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Component</div>
                          <select value={selectedComponent.type || 'text'} onChange={e => updateComponent(selectedComponent.id, { type: e.target.value })} style={fieldStyle}>
                            {COVER_COMPONENT_TYPES.map(item => <option key={item.type} value={item.type}>{item.label}</option>)}
                          </select>
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                          {[
                            ['X', 'x', 0, 95],
                            ['Y', 'y', 0, 95],
                            ['Width', 'width', 5, 100],
                            ['Height', 'height', 5, 60],
                          ].map(([label, key, min, max]) => (
                            <label key={key} style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{label} %</div>
                              <input
                                type="number"
                                min={min}
                                max={max}
                                value={Math.round(selectedComponent.layout?.[key] ?? 0)}
                                onChange={e => updateComponentLayout(selectedComponent.id, key, clampNumber(e.target.value, min, max))}
                                style={{ ...fieldStyle, fontFamily: FONTS.mono }}
                              />
                            </label>
                          ))}
                        </div>
                        {['title', 'subtitle'].includes(String(selectedComponent.type || '').toLowerCase()) && (
                          <>
                            <label style={{ display: 'block', marginBottom: 8 }}>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Text Override</div>
                              <textarea value={selectedComponent.content || ''} onChange={e => updateComponent(selectedComponent.id, { content: e.target.value })} placeholder={selectedComponent.type === 'title' ? selectedBlock.title : selectedBlock.content} style={{ ...textAreaStyle, minHeight: 70 }} />
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <label>
                                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Font Size</div>
                                <input type="number" min="8" max="72" value={selectedComponent.style?.fontSize || (selectedComponent.type === 'title' ? '38' : '14')} onChange={e => updateComponentStyle(selectedComponent.id, 'fontSize', e.target.value)} style={{ ...fieldStyle, fontFamily: FONTS.mono }} />
                              </label>
                              <label>
                                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Align</div>
                                <select value={selectedComponent.layout?.align || 'left'} onChange={e => updateComponentLayout(selectedComponent.id, 'align', e.target.value)} style={fieldStyle}>
                                  <option value="left">Left</option>
                                  <option value="center">Center</option>
                                  <option value="right">Right</option>
                                </select>
                              </label>
                            </div>
                          </>
                        )}
                        {String(selectedComponent.type || '').toLowerCase() === 'logo' && (
                          <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <label>
                                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Size px</div>
                                <input type="number" min="32" max="180" value={selectedComponent.style?.size || '92'} onChange={e => updateComponentStyle(selectedComponent.id, 'size', e.target.value)} style={{ ...fieldStyle, fontFamily: FONTS.mono }} />
                              </label>
                              <label>
                                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Shape</div>
                                <select value={selectedComponent.style?.shape || 'rounded'} onChange={e => updateComponentStyle(selectedComponent.id, 'shape', e.target.value)} style={fieldStyle}>
                                  <option value="rounded">Rounded</option>
                                  <option value="circle">Circle</option>
                                  <option value="square">Square</option>
                                </select>
                              </label>
                            </div>
                            <label style={{ display: 'block', marginTop: 8 }}>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Image URL</div>
                              <input value={selectedComponent.imageUrl || ''} onChange={e => updateComponent(selectedComponent.id, { imageUrl: e.target.value })} style={fieldStyle} />
                              {renderImageUploadInput((imageUrl, fileName) => updateComponent(selectedComponent.id, {
                                imageUrl,
                                altText: selectedComponent.altText || fileName,
                              }))}
                            </label>
                          </>
                        )}
                        {String(selectedComponent.type || '').toLowerCase() === 'metadata' && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Layout</div>
                              <select value={selectedComponent.layout?.direction || 'row'} onChange={e => updateComponentLayout(selectedComponent.id, 'direction', e.target.value)} style={fieldStyle}>
                                <option value="row">Side by side</option>
                                <option value="stack">Stacked</option>
                              </select>
                            </label>
                            <label>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Line px</div>
                              <input type="number" min="0" max="8" value={selectedComponent.style?.lineThickness || '1'} onChange={e => updateComponentStyle(selectedComponent.id, 'lineThickness', e.target.value)} style={{ ...fieldStyle, fontFamily: FONTS.mono }} />
                            </label>
                          </div>
                        )}
                        {String(selectedComponent.type || '').toLowerCase() === 'accentline' && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Thickness px</div>
                              <input type="number" min="1" max="16" value={selectedComponent.style?.lineThickness || '5'} onChange={e => updateComponentStyle(selectedComponent.id, 'lineThickness', e.target.value)} style={{ ...fieldStyle, fontFamily: FONTS.mono }} />
                            </label>
                            <label>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Color</div>
                              <select value={selectedComponent.style?.color || 'accent'} onChange={e => updateComponentStyle(selectedComponent.id, 'color', e.target.value)} style={fieldStyle}>
                                <option value="accent">Accent</option>
                                <option value="primary">Primary</option>
                                <option value="secondary">Secondary</option>
                                <option value="text">Text</option>
                              </select>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ borderTop: `1px solid ${C.b1}`, marginTop: 12, paddingTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 9 }}>
                    <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, fontWeight: 800, textTransform: 'uppercase' }}>Elements</div>
                    <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{selectedElements.length} in block</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
                    {REPORT_ELEMENT_LIBRARY.map(item => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.type}
                          type="button"
                          onClick={() => addElement(item.type)}
                          style={{
                            border: `1px solid ${C.b1}`, background: '#fff', borderRadius: 7, padding: '7px 6px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                            color: C.t1, fontSize: 10, fontWeight: 700, fontFamily: FONTS.sans,
                          }}
                        >
                          <Icon size={12} /> {item.label}
                        </button>
                      );
                    })}
                  </div>
                  {selectedElements.map((element, index) => {
                    const selected = element.id === selectedElement?.id;
                    return (
                      <button
                        key={element.id}
                        type="button"
                        draggable
                        onDragStart={e => e.dataTransfer.setData('text/report-element-id', element.id)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                          e.preventDefault();
                          moveElementToIndex(e.dataTransfer.getData('text/report-element-id'), index);
                        }}
                        onClick={() => setSelectedElementId(element.id)}
                        style={{
                          width: '100%', display: 'grid', gridTemplateColumns: '14px minmax(0, 1fr) auto', gap: 7,
                          alignItems: 'center', textAlign: 'left', border: `1px solid ${selected ? C.sky : C.b1}`,
                          background: selected ? `${C.sky}10` : C.s2, borderRadius: 7, padding: '8px 9px',
                          marginBottom: 7, cursor: 'pointer',
                        }}
                      >
                        <GripVertical size={12} color={C.t3} />
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {element.label || element.content || element.type}
                          </span>
                          <span style={{ display: 'block', fontSize: 8, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', marginTop: 2 }}>{element.type}</span>
                        </span>
                        <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{index + 1}</span>
                      </button>
                    );
                  })}
                  {selectedElement && (
                    <div style={{ marginTop: 10, padding: 10, border: `1px solid ${C.b1}`, borderRadius: 7, background: '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans }}>Selected Element</div>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button type="button" onClick={() => moveElement(selectedElement.id, -1)} title="Move up" style={iconButtonStyle()}><ChevronUp size={13} /></button>
                          <button type="button" onClick={() => moveElement(selectedElement.id, 1)} title="Move down" style={iconButtonStyle()}><ChevronDown size={13} /></button>
                          <button type="button" onClick={() => duplicateElement(selectedElement.id)} title="Duplicate" style={iconButtonStyle()}><Copy size={13} /></button>
                          <button type="button" onClick={() => removeElement(selectedElement.id)} title="Delete" style={{ ...iconButtonStyle(), color: C.red }}><Trash2 size={13} /></button>
                        </div>
                      </div>
                      <label style={{ display: 'block', marginBottom: 8 }}>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Type</div>
                        <select value={selectedElement.type || 'paragraph'} onChange={e => updateElement(selectedElement.id, { type: e.target.value })} style={fieldStyle}>
                          {REPORT_ELEMENT_LIBRARY.map(item => <option key={item.type} value={item.type}>{item.label}</option>)}
                        </select>
                      </label>
                      {!['heading', 'paragraph', 'divider'].includes(String(selectedElement.type || '').toLowerCase()) && (
                        <label style={{ display: 'block', marginBottom: 8 }}>
                          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Label</div>
                          <input value={selectedElement.label || ''} onChange={e => updateElement(selectedElement.id, { label: e.target.value })} style={fieldStyle} />
                        </label>
                      )}
                      <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, marginBottom: 8, background: C.s2 }}>
                        <div style={{ fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 8 }}>Element Layout</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          {[
                            ['X Offset %', 'x', 0, 95],
                            ['Top Gap px', 'y', 0, 700],
                            ['Width %', 'width', 10, 100],
                            ['Min Height', 'height', 1, 100],
                          ].map(([label, key, min, max]) => (
                            <label key={key} style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{label}</div>
                              <input
                                type="number"
                                min={min}
                                max={max}
                                value={Math.round(selectedElementLayout[key] ?? 0)}
                                onChange={e => updateElementLayout(selectedElement.id, key, clampNumber(e.target.value, min, max))}
                                style={{ ...fieldStyle, fontFamily: FONTS.mono }}
                              />
                            </label>
                          ))}
                        </div>
                        <label style={{ display: 'block', marginTop: 8 }}>
                          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Alignment</div>
                          <select value={selectedElementLayout.align || 'left'} onChange={e => updateElementLayout(selectedElement.id, 'align', e.target.value)} style={fieldStyle}>
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </label>
                      </div>
                      {['findings', 'findingrepeater', 'tablerepeater'].includes(selectedElementType) && (
                        <label style={{ display: 'block', marginBottom: 8 }}>
                          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Finding Filter</div>
                          <input value={selectedElement.query || ''} onChange={e => updateElement(selectedElement.id, { query: e.target.value })} placeholder="critical cloud" style={{ ...fieldStyle, fontFamily: FONTS.mono }} />
                        </label>
                      )}
                      {['findingrepeater', 'tablerepeater'].includes(selectedElementType) && (
                        <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, marginBottom: 8, background: C.s2 }}>
                          <div style={{ fontSize: 10, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 8 }}>Repeater Controls</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Max Items</div>
                              <input type="number" min="1" max="50" value={selectedElement.style?.limit || '6'} onChange={e => updateElementStyle(selectedElement.id, 'limit', clampNumber(e.target.value, 1, 50))} style={{ ...fieldStyle, fontFamily: FONTS.mono }} />
                            </label>
                            <label>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Layout</div>
                              <select value={selectedElement.style?.layout || (selectedElementType === 'tablerepeater' ? 'table' : 'cards')} onChange={e => updateElementStyle(selectedElement.id, 'layout', e.target.value)} style={fieldStyle}>
                                <option value="cards">Cards</option>
                                <option value="table">Table</option>
                                <option value="compact">Compact</option>
                              </select>
                            </label>
                          </div>
                        </div>
                      )}
                      {['chart'].includes(selectedElementType) && (
                        <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, marginBottom: 8, background: C.s2 }}>
                          <label style={{ display: 'block', marginBottom: 8 }}>
                            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Chart Format</div>
                            <select value={selectedElement.chartType || 'severity'} onChange={e => updateElement(selectedElement.id, { chartType: e.target.value })} style={fieldStyle}>
                              {REPORT_CHART_TYPES.map(chart => <option key={chart.value} value={chart.value}>{chart.label}</option>)}
                            </select>
                          </label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>X Axis Title</div>
                              <input value={selectedElement.style?.axisTitleX || chartAxisDefaults(selectedElement.chartType)[0]} onChange={e => updateElementStyle(selectedElement.id, 'axisTitleX', e.target.value)} style={fieldStyle} />
                            </label>
                            <label>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Y Axis Title</div>
                              <input value={selectedElement.style?.axisTitleY || chartAxisDefaults(selectedElement.chartType)[1]} onChange={e => updateElementStyle(selectedElement.id, 'axisTitleY', e.target.value)} style={fieldStyle} />
                            </label>
                          </div>
                          {String(selectedElement.chartType || '').toLowerCase() === 'dualline' && (
                            <label style={{ display: 'block', marginTop: 8 }}>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Secondary Y Axis Title</div>
                              <input value={selectedElement.style?.axisTitleY2 || chartAxisDefaults(selectedElement.chartType)[2]} onChange={e => updateElementStyle(selectedElement.id, 'axisTitleY2', e.target.value)} style={fieldStyle} />
                            </label>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                            {REPORT_CHART_COLOR_FIELDS.map(([label, key], index) => (
                              <label key={key} style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{label}</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 6 }}>
                                  <input
                                    type="color"
                                    value={colorInputValue(selectedElement.style?.[key], [theme.primaryColor, theme.secondaryColor, theme.accentColor, '#f97316'][index])}
                                    onChange={e => updateElementStyle(selectedElement.id, key, e.target.value)}
                                    style={{ width: 34, height: 32, padding: 0, border: `1px solid ${C.b1}`, borderRadius: 6, background: '#fff' }}
                                  />
                                  <input value={selectedElement.style?.[key] || ''} onChange={e => updateElementStyle(selectedElement.id, key, e.target.value)} placeholder={['primary', 'secondary', 'accent', '#f97316'][index]} style={{ ...fieldStyle, minWidth: 0, fontFamily: FONTS.mono }} />
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {['metric'].includes(selectedElementType) && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Metric Presets</div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {REPORT_KPI_PRESETS.map(preset => (
                              <button
                                key={preset.token}
                                type="button"
                                onClick={() => updateElement(selectedElement.id, { label: preset.label, content: preset.token, style: { color: preset.color } })}
                                style={{ border: `1px solid ${C.b1}`, background: C.s2, borderRadius: 6, padding: '5px 6px', fontSize: 9, color: C.t1, fontFamily: FONTS.mono, cursor: 'pointer' }}
                              >
                                {preset.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {['image'].includes(selectedElementType) && (
                        <>
                          <label style={{ display: 'block', marginBottom: 8 }}>
                            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Image URL</div>
                            <input value={selectedElement.imageUrl || ''} onChange={e => updateElement(selectedElement.id, { imageUrl: e.target.value })} style={fieldStyle} />
                            {renderImageUploadInput((imageUrl, fileName) => updateElement(selectedElement.id, {
                              imageUrl,
                              altText: selectedElement.altText || fileName,
                            }))}
                          </label>
                          <label style={{ display: 'block', marginBottom: 8 }}>
                            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Alt Text</div>
                            <input value={selectedElement.altText || ''} onChange={e => updateElement(selectedElement.id, { altText: e.target.value })} style={fieldStyle} />
                          </label>
                        </>
                      )}
                      {!['chart', 'findings', 'divider'].includes(String(selectedElement.type || '').toLowerCase()) && (
                        <label style={{ display: 'block' }}>
                          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Content</div>
                          <textarea value={selectedElement.content || ''} onChange={e => updateElement(selectedElement.id, { content: e.target.value })} style={{ ...textAreaStyle, minHeight: 86 }} />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ padding: 14, borderRadius: 7, background: C.s2, color: C.t2, fontSize: 10, fontFamily: FONTS.mono }}>No block selected.</div>
            )}
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, fontWeight: 700, marginBottom: 9, textTransform: 'uppercase' }}>Tokens</div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {REPORT_TOKEN_LIBRARY.map(token => (
                <button
                  key={token}
                  type="button"
                  onClick={() => appendToken(token)}
                  style={{
                    border: `1px solid ${C.b1}`, background: C.s2, color: C.t1, borderRadius: 6,
                    padding: '6px 7px', fontSize: 9, fontFamily: FONTS.mono, cursor: 'pointer',
                  }}
                >
                  {token}
                </button>
              ))}
            </div>
          </Card>

          <Card style={{ padding: 14 }}>
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Cover Title</div>
              <input value={definition.coverTitle || ''} onChange={e => updateDefinition('coverTitle', e.target.value)} style={fieldStyle} />
            </label>
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Executive Heading</div>
              <input value={definition.executiveSummaryHeading || ''} onChange={e => updateDefinition('executiveSummaryHeading', e.target.value)} style={fieldStyle} />
            </label>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Closing Note</div>
              <textarea value={definition.closingNote || ''} onChange={e => updateDefinition('closingNote', e.target.value)} style={{ ...textAreaStyle, minHeight: 72 }} />
            </label>
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="ghost" onClick={handleArchiveActive} disabled={saving || loading || !activeTemplate} style={{ color: activeTemplate ? C.red : C.t3 }}>
                <Archive size={13} style={{ marginRight: 6 }} /> Revert to Default
              </Button>
            </div>
          </Card>

          {supportsImportWizard && (
            <Card style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Sparkles size={15} color={C.sky} />
                <div style={{ fontSize: 12, fontWeight: 800, fontFamily: FONTS.sans, color: C.t1 }}>Import Wizard</div>
              </div>
              <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.55, marginBottom: 10 }}>
                Inspect a reference DOCX and generate an editable managed draft with theme, sections, tables, charts, finding repeaters, and page structure.
              </div>
              <input
                ref={importFileRef}
                type="file"
                accept=".docx"
                onChange={e => {
                  setImportFile(e.target.files?.[0] || null);
                  setImportPreview(null);
                }}
                style={{ ...getInputStyle(), padding: 7, cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="sec" onClick={handleImportPreview} disabled={saving || loading || importing || !importFile}>
                  <Sparkles size={13} style={{ marginRight: 6 }} /> {importing ? 'Inspecting...' : 'Inspect DOCX'}
                </Button>
                <Button variant="pri" onClick={applyImportPreview} disabled={!importPreview?.definition || saving || loading}>
                  Apply Draft
                </Button>
              </div>
              {importPreview && (
                <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 7 }}>
                    {[
                      ['Pages', importPreview.pages || 0],
                      ['Tables', importPreview.structure?.tables || 0],
                      ['Charts', importPreview.structure?.charts || 0],
                      ['Images', importPreview.structure?.media || 0],
                    ].map(([label, value]) => (
                      <div key={label} style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: '8px 6px', background: C.s2, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: C.t1, fontFamily: FONTS.mono, fontWeight: 900, lineHeight: 1 }}>{value}</div>
                        <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', marginTop: 4 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, background: '#fff' }}>
                    <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, fontWeight: 800, textTransform: 'uppercase', marginBottom: 7 }}>Detected Theme</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                      {[
                        ['Body', importPreview.theme?.bodyFont],
                        ['Heading', importPreview.theme?.headingFont],
                        ['Primary', importPreview.theme?.primaryColor],
                        ['Accent', importPreview.theme?.accentColor],
                      ].map(([label, value]) => (
                        <div key={label} style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <strong style={{ color: C.t1 }}>{label}:</strong> {value || 'detected'}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, background: C.s2 }}>
                    <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, fontWeight: 800, textTransform: 'uppercase', marginBottom: 7 }}>Suggested Blocks</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(importPreview.definition?.blocks || []).slice(0, 12).map(block => (
                        <span key={block.id || `${block.type}-${block.title}`} style={{ border: `1px solid ${C.b1}`, background: '#fff', borderRadius: 999, padding: '4px 7px', fontSize: 9, color: C.t1, fontFamily: FONTS.mono }}>
                          {block.type}
                        </span>
                      ))}
                    </div>
                  </div>
                  {(importPreview.recommendations || []).slice(0, 3).map(item => (
                    <div key={item} style={{ fontSize: 9, color: C.grn, fontFamily: FONTS.mono, lineHeight: 1.45 }}>
                      <CheckCircle2 size={11} style={{ marginRight: 5, verticalAlign: 'text-bottom' }} />{item}
                    </div>
                  ))}
                  {(importPreview.warnings || []).slice(0, 3).map(item => (
                    <div key={item} style={{ fontSize: 9, color: C.amb, fontFamily: FONTS.mono, lineHeight: 1.45 }}>
                      <AlertTriangle size={11} style={{ marginRight: 5, verticalAlign: 'text-bottom' }} />{item}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {isWordOutput && (
            <Card style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <FileText size={15} color={C.sky} />
                <div style={{ fontSize: 12, fontWeight: 800, fontFamily: FONTS.sans, color: C.t1 }}>Word Template Mapping</div>
              </div>
              <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.55, marginBottom: 10 }}>
                Map uploaded DOCX content controls or tags to live report fields.
              </div>
              {Object.entries(DEFAULT_TEMPLATE_FIELD_MAPPINGS).map(([field]) => (
                <label key={field} style={{ display: 'block', marginBottom: 8 }}>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{field}</div>
                  <input
                    value={fieldMappings[field] || ''}
                    onChange={e => updateFieldMapping(field, e.target.value)}
                    style={{ ...fieldStyle, fontFamily: FONTS.mono }}
                  />
                </label>
              ))}
            </Card>
          )}

          {supportsUpload && (
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 10, color: C.t1 }}>Office Package Upload</div>
              <input
                ref={fileRef}
                type="file"
                accept={String(selectedSlot.outputFormat).toLowerCase() === 'docx' ? '.docx' : '.pptx'}
                onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                style={{ ...getInputStyle(), padding: 7, cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="sec" onClick={handleUpload} disabled={saving || loading || !selectedFile}>
                  <UploadCloud size={13} style={{ marginRight: 6 }} /> Upload to Draft
                </Button>
                {editableTemplate?.fileName && (
                  <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
                    {editableTemplate.fileName} ({Math.round((editableTemplate.sizeBytes || 0) / 1024)} KB)
                  </span>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function iconButtonStyle() {
  return {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: `1px solid ${C.b1}`,
    background: '#fff',
    color: C.t2,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function hexToRgba(hex, alpha) {
  const safe = /^#[0-9a-f]{6}$/i.test(hex || '') ? hex : '#0f4c81';
  const value = parseInt(safe.slice(1), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function ReportChartPreview({ item, theme, compact = false, onEditTitle }) {
  const chartType = String(item?.chartType || 'severity').toLowerCase();
  const colors = chartColors(item, theme);
  const defaults = chartAxisDefaults(chartType);
  const xTitle = axisTitle(item, 'axisTitleX', defaults[0]);
  const yTitle = axisTitle(item, 'axisTitleY', defaults[1]);
  const y2Title = axisTitle(item, 'axisTitleY2', defaults[2]);
  const frameStyle = {
    border: `1px solid ${C.b1}`,
    borderRadius: 7,
    padding: compact ? 10 : 14,
    background: hexToRgba(theme.backgroundColor, 0.65),
  };
  const titleStyle = { fontSize: compact ? 10 : 11, color: theme.textColor, fontWeight: 850, marginBottom: 10 };
  const axisStyle = { fontSize: 8, fill: hexToRgba(theme.textColor, 0.68), fontFamily: FONTS.mono };
  const editableChartTitle = fallback => (
    <InlineEditableText
      value={item?.title || item?.label || ''}
      fallback={fallback}
      onCommit={value => onEditTitle?.(value)}
    />
  );
  const severityRows = REPORT_DESIGNER_SAMPLE.severity.map((row, index) => ({
    ...row,
    color: colors[index % colors.length],
  }));

  if (chartType === 'pie' || chartType === 'donut' || chartType === 'riskmix') {
    const total = severityRows.reduce((sum, row) => sum + row.value, 0);
    let start = 0;
    const stops = severityRows.map(row => {
      const end = start + (row.value / total) * 100;
      const segment = `${row.color} ${start}% ${end}%`;
      start = end;
      return segment;
    }).join(', ');
    return (
      <div style={frameStyle}>
        <div style={titleStyle}>{editableChartTitle('Risk Mix')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '150px minmax(0, 1fr)', gap: 14, alignItems: 'center' }}>
          <div style={{
            width: compact ? 128 : 146,
            height: compact ? 128 : 146,
            borderRadius: '50%',
            background: `conic-gradient(${stops})`,
            justifySelf: compact ? 'center' : 'start',
            position: 'relative',
          }}>
            {chartType === 'donut' && <div style={{ position: 'absolute', inset: '26%', borderRadius: '50%', background: theme.surfaceColor, border: `1px solid ${C.b1}` }} />}
          </div>
          <div style={{ display: 'grid', gap: 7 }}>
            {severityRows.map(row => (
              <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '12px minmax(0, 1fr) auto', gap: 7, alignItems: 'center' }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: row.color }} />
                <span style={{ fontSize: 10, color: theme.textColor, fontWeight: 800 }}>{row.label}</span>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (chartType === 'line' || chartType === 'scoretrend' || chartType === 'dualline') {
    const scores = REPORT_DESIGNER_SAMPLE.scoreTrend;
    const open = [34, 32, 30, 29, 28, 27];
    const toPoint = (value, idx, min, max) => {
      const x = 42 + idx * 64;
      const y = 136 - ((value - min) / Math.max(1, max - min)) * 96;
      return `${x},${Math.max(28, Math.min(136, y))}`;
    };
    const scorePoints = scores.map((score, idx) => toPoint(score, idx, 60, 90)).join(' ');
    const openPoints = open.map((value, idx) => toPoint(value, idx, 20, 40)).join(' ');
    return (
      <div style={frameStyle}>
        <div style={titleStyle}>{editableChartTitle('Score Trend')}</div>
        <svg viewBox="0 0 430 178" width="100%" height={compact ? 148 : 178} role="img" aria-label={`${chartTitleFor(item, 'Chart')} with ${xTitle} and ${yTitle} axes`}>
          <line x1="42" y1="136" x2="404" y2="136" stroke={hexToRgba(theme.textColor, 0.22)} strokeWidth="1" />
          <line x1="42" y1="24" x2="42" y2="136" stroke={hexToRgba(theme.textColor, 0.22)} strokeWidth="1" />
          <text x="222" y="168" textAnchor="middle" style={axisStyle}>{xTitle}</text>
          <text x="12" y="84" textAnchor="middle" transform="rotate(-90 12 84)" style={axisStyle}>{yTitle}</text>
          {chartType === 'dualline' && <text x="418" y="84" textAnchor="middle" transform="rotate(90 418 84)" style={axisStyle}>{y2Title || 'Open Findings'}</text>}
          <polyline points={scorePoints} fill="none" stroke={colors[0]} strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
          {chartType === 'dualline' && <polyline points={openPoints} fill="none" stroke={colors[1]} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="6 5" />}
          {scores.map((score, idx) => {
            const [x, y] = toPoint(score, idx, 60, 90).split(',');
            return <circle key={`${score}-${idx}`} cx={x} cy={y} r="4" fill={colors[0]} />;
          })}
          {chartType === 'dualline' && open.map((value, idx) => {
            const [x, y] = toPoint(value, idx, 20, 40).split(',');
            return <circle key={`${value}-${idx}`} cx={x} cy={y} r="3" fill={colors[1]} />;
          })}
        </svg>
      </div>
    );
  }

  if (chartType === 'stackedbar') {
    const rows = [
      ['Cloud', [4, 6, 2, 1]],
      ['DAST', [2, 3, 4, 0]],
      ['SDD', [1, 4, 2, 1]],
      ['Vuln', [3, 8, 5, 2]],
    ];
    return (
      <div style={frameStyle}>
        <div style={titleStyle}>{editableChartTitle('Risk Category Stack')}</div>
        <div style={{ display: 'grid', gap: 9 }}>
          {rows.map(([label, values]) => {
            const total = values.reduce((sum, value) => sum + value, 0);
            return (
              <div key={label} style={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr) 32px', gap: 10, alignItems: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: theme.textColor }}>{label}</div>
                <div style={{ height: 15, borderRadius: 7, background: C.s2, overflow: 'hidden', display: 'flex' }}>
                  {values.map((value, index) => (
                    <span key={`${label}-${index}`} style={{ width: `${(value / total) * 100}%`, background: colors[index % colors.length] }} />
                  ))}
                </div>
                <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textAlign: 'right' }}>{total}</div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', color: C.t3, fontSize: 8, fontFamily: FONTS.mono }}>
          <span>{xTitle}</span>
          <span>{yTitle}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={frameStyle}>
      <div style={titleStyle}>{editableChartTitle('Finding Severity Distribution')}</div>
      <div style={{ display: 'grid', gap: 10 }}>
        {severityRows.map(row => (
          <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '74px minmax(0, 1fr) 34px', gap: 10, alignItems: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: theme.textColor }}>{row.label}</div>
            <div style={{ height: 14, borderRadius: 7, background: hexToRgba(row.color, 0.12), overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(12, row.value * 7)}%`, height: '100%', background: row.color, borderRadius: 7 }} />
            </div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textAlign: 'right' }}>{row.value}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', color: C.t3, fontSize: 8, fontFamily: FONTS.mono }}>
        <span>{xTitle}</span>
        <span>{yTitle}</span>
      </div>
    </div>
  );
}

function elementCanvasHeight(elements = []) {
  return Math.max(
    260,
    ...elements.map(element => {
      const layout = normalizeLayout(element.layout, defaultElementLayout(element.type));
      return Math.max(0, Number(layout.y) || 0) + Math.max(50, Number(layout.height || 8) * 6) + 28;
    }),
  );
}

function ReportPageBreak({ pageNumber, theme }) {
  return (
    <div
      data-report-page-break="true"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
        alignItems: 'center',
        gap: 12,
        padding: '2px 8px',
        color: C.t3,
        userSelect: 'none',
      }}
      aria-label={`Page break before page ${pageNumber}`}
    >
      <span style={{ borderTop: `1px dashed ${hexToRgba(theme.textColor, 0.28)}` }} />
      <span
        style={{
          border: `1px solid ${hexToRgba(theme.accentColor, 0.34)}`,
          background: hexToRgba(theme.surfaceColor, 0.94),
          color: hexToRgba(theme.textColor, 0.62),
          borderRadius: 999,
          padding: '5px 10px',
          fontSize: 9,
          fontFamily: FONTS.mono,
          fontWeight: 900,
          textTransform: 'uppercase',
          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
          whiteSpace: 'nowrap',
        }}
      >
        Page break · Page {pageNumber}
      </span>
      <span style={{ borderTop: `1px dashed ${hexToRgba(theme.textColor, 0.28)}` }} />
    </div>
  );
}

function ReportPreviewBlock({
  block,
  index,
  blockCount,
  theme,
  selected,
  selectedElementId,
  selectedComponentId,
  onSelect,
  onSelectElement,
  onSelectComponent,
  onMoveElement,
  onMoveElementLayout,
  onMoveComponent,
  onMoveBlock,
  onDropBlock,
  onEditBlockField,
  onEditComponentField,
  onEditElementField,
}) {
  const type = String(block.type || 'text').toLowerCase();
  const elements = Array.isArray(block.elements) ? block.elements : [];
  const components = Array.isArray(block.components) ? block.components : [];
  const layout = normalizeLayout(block.layout, defaultFrameLayout());
  const blockWidth = Math.min(100, Math.max(10, Number(layout.width) || 100));
  const blockX = Math.min(Math.max(0, Number(layout.x) || 0), Math.max(0, 100 - blockWidth));
  const blockChrome = {
    position: 'relative',
    padding: type === 'cover' ? 'clamp(28px, 6%, 52px) clamp(32px, 7%, 58px) clamp(26px, 5%, 46px)' : '24px 52px',
    width: `${blockWidth}%`,
    marginLeft: `${blockX}%`,
    marginTop: `${Math.max(0, Number(layout.y) || 0)}px`,
    minHeight: type === 'cover' ? undefined : Math.max(0, Number(layout.height) || 0) * 6,
    boxSizing: 'border-box',
    textAlign: layout.align || 'left',
    borderLeft: selected ? `4px solid ${theme.accentColor}` : '4px solid transparent',
    outline: selected ? `1px solid ${hexToRgba(theme.accentColor, 0.35)}` : 'none',
    cursor: 'pointer',
  };
  const headingStyle = {
    color: theme.textColor,
    fontFamily: theme.headingFontFamily,
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 850,
    margin: '0 0 12px',
    letterSpacing: 0,
  };
  const bodyStyle = {
    color: hexToRgba(theme.textColor, 0.82),
    fontSize: 12,
    lineHeight: 1.65,
    margin: 0,
  };
  const blockDragProps = {
    draggable: false,
    onDragStart: event => {
      if (event.target.closest?.('[data-report-component-id], [data-report-element-id]')) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.setData('text/report-block-id', block.id);
      event.dataTransfer.effectAllowed = 'move';
    },
    onDragOver: event => {
      const types = Array.from(event.dataTransfer.types || []);
      if (types.includes('text/report-block-id')) event.preventDefault();
    },
    onDrop: event => {
      const draggedBlockId = event.dataTransfer.getData('text/report-block-id');
      if (!draggedBlockId) return;
      event.preventDefault();
      onDropBlock?.(draggedBlockId, index);
    },
  };
  const showBlockMoveControls = selected && !(type === 'cover' && selectedComponentId);
  const blockMoveControls = showBlockMoveControls ? (
    <div
      style={{
        position: 'absolute', top: 8, left: 10, zIndex: 5, display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 5px', borderRadius: 7, border: `1px solid ${hexToRgba(theme.accentColor, 0.35)}`,
        background: hexToRgba(theme.surfaceColor, 0.94), boxShadow: '0 8px 22px rgba(15, 23, 42, 0.14)',
      }}
      title="Drag block to reorder"
    >
      <GripVertical size={13} color={theme.accentColor} />
      <button
        type="button"
        onClick={event => { event.stopPropagation(); onMoveBlock?.(Math.max(0, index - 1)); }}
        disabled={index <= 0}
        title="Move block up"
        style={{ ...iconButtonStyle(), width: 24, height: 24, opacity: index <= 0 ? 0.45 : 1 }}
      ><ChevronUp size={12} /></button>
      <button
        type="button"
        onClick={event => { event.stopPropagation(); onMoveBlock?.(Math.min((blockCount || 1) - 1, index + 1)); }}
        disabled={index >= (blockCount || 1) - 1}
        title="Move block down"
        style={{ ...iconButtonStyle(), width: 24, height: 24, opacity: index >= (blockCount || 1) - 1 ? 0.45 : 1 }}
      ><ChevronDown size={12} /></button>
    </div>
  ) : null;
  const editableBlockTitle = fallback => (
    <InlineEditableText
      value={block.title}
      fallback={fallback}
      onCommit={value => onEditBlockField?.('title', value)}
    />
  );
  const editableBlockContent = (fallback, style, multiline = false) => (
    <InlineEditableText
      value={block.content}
      fallback={fallback}
      multiline={multiline}
      onCommit={value => onEditBlockField?.('content', value)}
      style={style}
    />
  );

  if (elements.length > 0 && type !== 'cover') {
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Report Section')}</h2>
        <div data-report-element-canvas="true" style={{ position: 'relative', minHeight: elementCanvasHeight(elements) }}>
          {elements.map((element, elementIndex) => (
            <ReportPreviewElement
              key={element.id}
              element={element}
              index={elementIndex}
              theme={theme}
              selected={element.id === selectedElementId}
              onSelect={event => {
                event.stopPropagation();
                onSelectElement?.(element.id);
              }}
              onMove={targetIndex => onMoveElement?.(element.id, targetIndex)}
              onMoveLayout={point => onMoveElementLayout?.(element.id, point)}
              onDropElement={draggedElementId => onMoveElement?.(draggedElementId, elementIndex)}
              onEditField={(field, value) => onEditElementField?.(element.id, field, value)}
            />
          ))}
        </div>
      </section>
    );
  }

  if (type === 'cover') {
    const coverComponents = components.length > 0 ? components : createCoverComponents(block);
    const coverDropProps = {
      ...blockDragProps,
      draggable: false,
      onDragOver: event => {
        const types = Array.from(event.dataTransfer.types || []);
        if (types.includes('text/report-component-id') || types.includes('text/report-block-id')) event.preventDefault();
      },
      onDrop: event => {
        const componentId = event.dataTransfer.getData('text/report-component-id');
        if (componentId) {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          onMoveComponent?.(componentId, {
            x: ((event.clientX - rect.left) / rect.width) * 100,
            y: ((event.clientY - rect.top) / rect.height) * 100,
          });
          return;
        }
        blockDragProps.onDrop(event);
      },
    };
    return (
      <section
        data-report-cover-frame="true"
        onClick={event => {
          if (event.target.closest?.('[data-report-component-id]')) return;
          onSelect?.(event);
        }}
        style={{
          ...blockChrome,
          aspectRatio: '8.5 / 11',
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          background: theme.coverStyle === 'minimal' ? theme.surfaceColor : `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
          color: '#fff',
        }}
        {...coverDropProps}
      >
        {blockMoveControls}
        {coverComponents.map(component => (
          <ReportCoverComponent
            key={component.id}
            component={component}
            block={block}
            theme={theme}
            selected={selected && component.id === selectedComponentId}
            onSelect={event => {
              event.stopPropagation();
              onSelectComponent?.(component.id);
            }}
            onMove={(componentId, point) => onMoveComponent?.(componentId, point)}
            onEditField={(field, value) => onEditComponentField?.(component.id, field, value)}
            onEditBlockField={onEditBlockField}
          />
        ))}
      </section>
    );
  }

  if (type === 'toc') {
    const sections = ['Executive Summary', 'Accounts', 'Risk Summary', 'Data at a Glance', 'Roadmap', 'Methodology', 'Appendix'];
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Table of Contents')}</h2>
        <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, overflow: 'hidden' }}>
          {sections.map((section, sectionIndex) => (
            <div key={section} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 36px', gap: 12, padding: '9px 12px', borderBottom: sectionIndex === sections.length - 1 ? 'none' : `1px solid ${C.b1}` }}>
              <span style={{ fontSize: 11, color: theme.textColor, fontWeight: 700 }}>{section}</span>
              <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textAlign: 'right' }}>{sectionIndex + 2}</span>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (type === 'pagebreak') {
    return (
      <section onClick={onSelect} style={{ ...blockChrome, paddingTop: 16, paddingBottom: 16 }} {...blockDragProps}>
        {blockMoveControls}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 10 }}>
          <span style={{ height: 1, background: C.b1 }} />
          <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, fontWeight: 800, textTransform: 'uppercase' }}>Page Break</span>
          <span style={{ height: 1, background: C.b1 }} />
        </div>
      </section>
    );
  }

  if (type === 'table') {
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Data at a Glance')}</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${C.b1}`, borderRadius: 7, overflow: 'hidden' }}>
          <tbody>
            {[
              ['Security score', REPORT_DESIGNER_SAMPLE.securityScore],
              ['Open findings', REPORT_DESIGNER_SAMPLE.totalOpen],
              ['Critical findings', REPORT_DESIGNER_SAMPLE.criticalCount],
              ['Resolved findings', REPORT_DESIGNER_SAMPLE.resolvedCount],
            ].map(([label, value]) => (
              <tr key={label} style={{ borderBottom: `1px solid ${C.b1}` }}>
                <td style={{ padding: '10px 12px', fontSize: 11, color: theme.textColor, fontWeight: 700 }}>{label}</td>
                <td style={{ padding: '10px 12px', fontSize: 11, color: C.t2, fontFamily: FONTS.mono, textAlign: 'right' }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    );
  }

  if (type === 'kpi') {
    const metrics = parseKpiMetrics(block.content, theme, 6);
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Security KPIs')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, metrics.length))}, minmax(0, 1fr))`, gap: 10 }}>
          {metrics.map(({ label, value, color }, metricIndex) => (
            <div key={label} style={{ border: `1px solid ${hexToRgba(color, 0.22)}`, borderRadius: 7, padding: 12, background: hexToRgba(color, 0.06), minHeight: 88 }}>
              <div style={{ color, fontSize: 24, fontWeight: 900, lineHeight: 1 }}>{value}</div>
              <div style={{ color: hexToRgba(theme.textColor, 0.72), fontSize: 10, fontFamily: FONTS.mono, marginTop: 11, textTransform: 'uppercase' }}>
                <InlineEditableText
                  value={label}
                  fallback="Metric"
                  onCommit={nextLabel => onEditBlockField?.('content', updateKpiMetricLabel(block.content || '', metricIndex, nextLabel))}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (type === 'chart') {
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <ReportChartPreview item={block} theme={theme} onEditTitle={value => onEditBlockField?.('title', value)} />
      </section>
    );
  }

  if (type === 'findingcard') {
    const finding = reportQueryFindings(block.query || 'critical cloud')[0];
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Priority Remediation Card')}</h2>
        {finding ? (
          <div style={{ border: `1px solid ${hexToRgba('#dc2626', 0.22)}`, borderRadius: 7, padding: 14, background: hexToRgba('#dc2626', 0.05) }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <span style={{ color: '#dc2626', fontSize: 10, fontWeight: 900, fontFamily: FONTS.mono, textTransform: 'uppercase' }}>{finding.severity}</span>
              <span style={{ color: C.t2, fontSize: 9, fontFamily: FONTS.mono }}>{finding.age} days open</span>
            </div>
            <div style={{ fontSize: 13, color: theme.textColor, fontWeight: 850, lineHeight: 1.35 }}>{finding.title}</div>
            <p style={{ ...bodyStyle, marginTop: 10 }}>{editableBlockContent('Recommended action: assign an owner, validate exposure, and confirm remediation evidence.', undefined, true)}</p>
          </div>
        ) : (
          <div style={{ padding: 14, color: C.t2, fontSize: 11, fontFamily: FONTS.mono }}>No matching finding.</div>
        )}
      </section>
    );
  }

  if (type === 'findings') {
    const findings = reportQueryFindings(block.query || 'critical cloud').slice(0, 5);
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Findings')}</h2>
        <div style={{ border: `1px solid ${C.b1}`, borderRadius: 7, overflow: 'hidden' }}>
          {findings.map(finding => (
            <div key={finding.title} style={{ display: 'grid', gridTemplateColumns: '82px minmax(0, 1fr) 54px', gap: 10, padding: '10px 12px', borderBottom: `1px solid ${C.b1}`, alignItems: 'center' }}>
              <span style={{ color: finding.severity === 'Critical' ? '#dc2626' : '#f97316', fontSize: 10, fontWeight: 900 }}>{finding.severity}</span>
              <span style={{ fontSize: 11, color: theme.textColor, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{finding.title}</span>
              <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textAlign: 'right' }}>{finding.age}d</span>
            </div>
          ))}
          {findings.length === 0 && <div style={{ padding: 14, color: C.t2, fontSize: 11, fontFamily: FONTS.mono }}>No matching findings.</div>}
        </div>
      </section>
    );
  }

  if (type === 'findingrepeater') {
    const findings = reportQueryFindings(block.query || 'critical high vulnerability').slice(0, repeaterLimit(block, 4));
    const template = block.content || '{{finding.title}}\nSeverity: {{finding.severity}}\nAge: {{finding.ageDays}} days';
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Detailed Findings')}</h2>
        <div style={{ display: 'grid', gap: 10 }}>
          {findings.map(finding => (
            <div key={finding.title} style={{ border: `1px solid ${hexToRgba(theme.accentColor, 0.22)}`, borderRadius: 7, padding: 13, background: hexToRgba(theme.accentColor, 0.05) }}>
              {applyFindingTokens(template, finding).split('\n').filter(Boolean).map((line, lineIndex) => (
                lineIndex === 0
                  ? <div key={line} style={{ fontSize: 12, color: theme.textColor, fontWeight: 900, lineHeight: 1.35 }}>{line}</div>
                  : <div key={`${line}-${lineIndex}`} style={{ marginTop: 6, fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.45 }}>{line}</div>
              ))}
            </div>
          ))}
          {findings.length === 0 && <div style={{ padding: 14, color: C.t2, fontSize: 11, fontFamily: FONTS.mono }}>{block.style?.emptyText || 'No matching findings.'}</div>}
        </div>
      </section>
    );
  }

  if (type === 'tablerepeater') {
    const findings = reportQueryFindings(block.query || 'critical high vulnerability').slice(0, repeaterLimit(block, 6));
    const template = parseRepeaterTableTemplate(block.content);
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Finding Summary Table')}</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${C.b1}`, borderRadius: 7, overflow: 'hidden' }}>
          <thead>
            <tr>
              {template.headers.map(header => (
                <th key={header} style={{ padding: '9px 10px', background: hexToRgba(theme.primaryColor, 0.10), color: theme.textColor, fontSize: 9, fontFamily: FONTS.mono, textTransform: 'uppercase', textAlign: 'left' }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {findings.map(finding => (
              <tr key={finding.title} style={{ borderTop: `1px solid ${C.b1}` }}>
                {template.cells.map((cell, cellIndex) => (
                  <td key={`${finding.title}-${cellIndex}`} style={{ padding: '9px 10px', fontSize: 10, color: cellIndex === 0 ? (finding.severity === 'Critical' ? '#dc2626' : '#f97316') : theme.textColor, fontWeight: cellIndex === 0 ? 900 : 650 }}>
                    {applyFindingTokens(cell, finding)}
                  </td>
                ))}
              </tr>
            ))}
            {findings.length === 0 && (
              <tr><td colSpan={template.headers.length} style={{ padding: 14, color: C.t2, fontSize: 11, fontFamily: FONTS.mono }}>{block.style?.emptyText || 'No matching findings.'}</td></tr>
            )}
          </tbody>
        </table>
      </section>
    );
  }

  if (type === 'appendix') {
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Appendix')}</h2>
        <p style={bodyStyle}>{editableBlockContent('Supporting evidence, scope notes, and detailed finding exports for {{report.period}}.', undefined, true)}</p>
        <div style={{ marginTop: 14, border: `1px solid ${C.b1}`, borderRadius: 7, padding: 12, background: hexToRgba(theme.backgroundColor, 0.62), fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
          Open external ports, affected assets, finding evidence, and methodology notes
        </div>
      </section>
    );
  }

  if (type === 'image' || type === 'infographic') {
    return (
      <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
        {blockMoveControls}
        <PreviewBlockKicker block={block} index={index} theme={theme} />
        <h2 style={headingStyle}>{editableBlockTitle('Program Infographic')}</h2>
        {block.imageUrl ? (
          <img src={block.imageUrl} alt={block.altText || block.title || 'Report image'} style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 7, border: `1px solid ${C.b1}` }} />
        ) : (
          <div style={{
            minHeight: 210, borderRadius: 7, border: `1px solid ${hexToRgba(theme.primaryColor, 0.22)}`,
            background: `linear-gradient(135deg, ${hexToRgba(theme.primaryColor, 0.10)}, ${hexToRgba(theme.accentColor, 0.18)})`,
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, padding: 22, alignItems: 'center',
          }}>
            <div>
              <div style={{ width: 42, height: 42, borderRadius: 8, background: theme.primaryColor, color: '#fff', display: 'grid', placeItems: 'center', marginBottom: 16 }}>
                <Image size={21} />
              </div>
              <p style={{ ...bodyStyle, fontWeight: 700, color: theme.textColor }}>{editableBlockContent('Risk posture context for {{report.period}}.', undefined, true)}</p>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {[78, 54, 88, 41].map((width, idx) => (
                <div key={width} style={{ height: 13, borderRadius: 7, background: idx % 2 ? theme.accentColor : theme.secondaryColor, width: `${width}%`, justifySelf: idx % 2 ? 'end' : 'start' }} />
              ))}
            </div>
          </div>
        )}
      </section>
    );
  }

  const rawLines = String(block.content || '').split('\n');
  const lines = rawLines.map((line, lineIndex) => ({ line, lineIndex })).filter(item => item.line.trim().length > 0);
  return (
    <section onClick={onSelect} style={blockChrome} {...blockDragProps}>
      {blockMoveControls}
      <PreviewBlockKicker block={block} index={index} theme={theme} />
      <h2 style={headingStyle}>{editableBlockTitle('Text Block')}</h2>
      {lines.length === 0 ? (
        <p style={bodyStyle}>Select this block to add report content.</p>
      ) : (
        lines.map(({ line, lineIndex }) => (
          <p key={`${block.id}-${lineIndex}`} style={{ ...bodyStyle, marginBottom: 9 }}>
            <InlineEditableText
              value={line}
              fallback={line}
              multiline
              onCommit={value => onEditBlockField?.('content', replaceLineAtIndex(block.content || '', lineIndex, value))}
            />
          </p>
        ))
      )}
    </section>
  );
}

function resolveComponentColor(value, theme, fallback = '#ffffff') {
  if (value === 'primary') return theme.primaryColor;
  if (value === 'secondary') return theme.secondaryColor;
  if (value === 'accent') return theme.accentColor;
  if (value === 'text') return theme.textColor;
  return /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;
}

function ReportCoverComponent({ component, block, theme, selected, onSelect, onMove, onEditField, onEditBlockField }) {
  const type = String(component.type || '').toLowerCase();
  const layout = normalizeLayout(component.layout, {});
  const style = normalizeStyle(component.style, {});
  const dragState = useRef(null);
  const isMinimal = theme.coverStyle === 'minimal';
  const color = isMinimal ? theme.textColor : '#ffffff';
  const selectedChrome = selected ? {
    outline: `2px solid ${theme.accentColor}`,
    outlineOffset: 2,
    background: type === 'accentline' ? undefined : hexToRgba(theme.accentColor, 0.08),
  } : {};
  const baseStyle = {
    position: 'absolute',
    left: `${layout.x}%`,
    top: `${layout.y}%`,
    width: `${layout.width}%`,
    minHeight: `${Math.max(8, layout.height)}px`,
    cursor: 'move',
    touchAction: 'none',
    userSelect: 'none',
    borderRadius: 5,
    ...selectedChrome,
  };
  const startPointerMove = event => {
    if (event.button !== 0) return;
    if (event.target.closest?.('[data-report-inline-edit]')) return;
    event.preventDefault();
    event.stopPropagation();
    onSelect?.(event);
    const cover = event.currentTarget.closest('[data-report-cover-frame="true"]');
    const rect = cover?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = {
      rect,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: layout.x,
      startY: layout.y,
      width: Number(layout.width) || 12,
      height: Number(layout.height) || 8,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const movePointer = event => {
    const state = dragState.current;
    if (!state) return;
    event.preventDefault();
    event.stopPropagation();
    onMove?.(component.id, {
      x: state.startX + state.width / 2 + ((event.clientX - state.startClientX) / state.rect.width) * 100,
      y: state.startY + state.height / 2 + ((event.clientY - state.startClientY) / state.rect.height) * 100,
    });
  };
  const stopPointerMove = event => {
    if (!dragState.current) return;
    event.stopPropagation();
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragState.current = null;
  };
  const dragProps = {
    'data-report-component-id': component.id,
    draggable: false,
    onClick: event => {
      event.stopPropagation();
      onSelect?.(event);
    },
    onPointerDown: startPointerMove,
    onPointerMove: movePointer,
    onPointerUp: stopPointerMove,
    onPointerCancel: stopPointerMove,
  };

  if (type === 'accentline') {
    return (
      <div
        {...dragProps}
        title="Accent line"
        style={{
          ...baseStyle,
          height: Number(style.lineThickness || 5),
          minHeight: Number(style.lineThickness || 5),
          background: resolveComponentColor(style.color, theme, theme.accentColor),
        }}
      />
    );
  }

  if (type === 'logo') {
    const size = Number(style.size || 92);
    return (
      <div {...dragProps} title="Logo" style={{ ...baseStyle, width: Math.max(40, size), height: Math.max(40, size), minHeight: Math.max(40, size) }}>
        {component.imageUrl ? (
          <img src={component.imageUrl} alt={component.altText || 'Report logo'} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }} />
        ) : (
          <div style={{
            width: '100%', height: '100%', borderRadius: style.shape === 'circle' ? '50%' : style.shape === 'square' ? 0 : 8,
            border: `1px solid ${isMinimal ? hexToRgba(theme.primaryColor, 0.28) : hexToRgba('#ffffff', 0.28)}`,
            background: isMinimal ? hexToRgba(theme.primaryColor, 0.08) : hexToRgba('#ffffff', 0.12),
            display: 'grid', placeItems: 'center',
          }}>
            <ShieldCheck size={Math.max(22, Math.round(size * 0.38))} color={isMinimal ? theme.primaryColor : '#fff'} />
          </div>
        )}
      </div>
    );
  }

  if (type === 'title') {
    const title = component.content || block.title || 'Monthly Security Report';
    return (
      <div {...dragProps} title="Title" style={baseStyle}>
        <h1 style={{
          margin: 0, fontFamily: theme.headingFontFamily, color, fontSize: Number(style.fontSize || 38),
          lineHeight: 1.02, fontWeight: Number(style.fontWeight || 900), letterSpacing: 0,
          textAlign: layout.align || 'left',
        }}>
          <InlineEditableText
            value={title}
            fallback="Monthly Security Report"
            onCommit={value => {
              if (component.content) {
                onEditField?.('content', value);
              } else {
                onEditBlockField?.('title', value);
              }
            }}
          />
        </h1>
      </div>
    );
  }

  if (type === 'subtitle') {
    const subtitle = component.content || block.content || 'Prepared for {{client.name}} for {{report.period}}.';
    return (
      <div {...dragProps} title="Subtitle" style={baseStyle}>
        <p style={{
          margin: 0, color: isMinimal ? hexToRgba(theme.textColor, 0.75) : hexToRgba('#ffffff', 0.82),
          fontSize: Number(style.fontSize || 14), lineHeight: 1.6, textAlign: layout.align || 'left',
        }}>
          <InlineEditableText
            value={subtitle}
            fallback="Prepared for {{client.name}} for {{report.period}}."
            multiline
            onCommit={value => {
              if (component.content) {
                onEditField?.('content', value);
              } else {
                onEditBlockField?.('content', value);
              }
            }}
          />
        </p>
      </div>
    );
  }

  if (type === 'metadata') {
    const direction = layout.direction === 'stack' ? 'stack' : 'row';
    const metaItems = [
      ['Client', REPORT_DESIGNER_SAMPLE.clientName],
      ['Period', REPORT_DESIGNER_SAMPLE.period],
      ['Score', REPORT_DESIGNER_SAMPLE.securityScore],
    ];
    return (
      <div
        {...dragProps}
        title="Client / Period / Score"
        style={{
          ...baseStyle,
          display: 'grid',
          gridTemplateColumns: direction === 'stack' ? '1fr' : 'repeat(3, minmax(0, 1fr))',
          gap: Number(style.gap || 10),
        }}
      >
        {metaItems.map(([label, value]) => (
          <div key={label} style={{
            borderTop: `${Number(style.lineThickness || 1)}px solid ${isMinimal ? hexToRgba(theme.primaryColor, 0.24) : hexToRgba('#ffffff', 0.28)}`,
            paddingTop: 9,
            minWidth: 0,
          }}>
            <div style={{ fontSize: 9, textTransform: 'uppercase', fontFamily: FONTS.mono, opacity: 0.7, color }}>{label}</div>
            <div style={{ fontSize: 12, fontWeight: 800, marginTop: 3, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
          </div>
        ))}
      </div>
    );
  }

  return null;
}

function ReportPreviewElement({ element, index, theme, selected, onSelect, onMove, onMoveLayout, onDropElement, onEditField }) {
  const type = String(element.type || 'paragraph').toLowerCase();
  const layout = normalizeLayout(element.layout, defaultFrameLayout());
  const elementWidth = Math.min(100, Math.max(10, Number(layout.width) || 100));
  const elementX = Math.min(Math.max(0, Number(layout.x) || 0), Math.max(0, 100 - elementWidth));
  const dragState = useRef(null);
  const metricColor = resolveThemeColor(element.style?.color, theme, theme.primaryColor);
  const chrome = {
    position: 'absolute',
    left: `${elementX}%`,
    top: `${Math.max(0, Number(layout.y) || 0)}px`,
    width: `${elementWidth}%`,
    minHeight: Math.max(0, Number(layout.height) || 0) * 5,
    boxSizing: 'border-box',
    textAlign: layout.align || 'left',
    border: `1px solid ${selected ? theme.accentColor : 'transparent'}`,
    borderRadius: 7,
    padding: selected ? 10 : '10px 0',
    background: selected ? hexToRgba(theme.accentColor, 0.06) : 'transparent',
    cursor: selected ? 'move' : 'pointer',
    touchAction: 'none',
    userSelect: 'none',
  };
  const handle = (
    <div style={{ position: 'absolute', top: 6, right: 7, display: selected ? 'flex' : 'none', gap: 4 }}>
      <button type="button" onClick={event => { event.stopPropagation(); onMove?.(Math.max(0, index - 1)); }} title="Move element up" style={{ ...iconButtonStyle(), width: 24, height: 24 }}><ChevronUp size={12} /></button>
      <button type="button" onClick={event => { event.stopPropagation(); onMove?.(index + 1); }} title="Move element down" style={{ ...iconButtonStyle(), width: 24, height: 24 }}><ChevronDown size={12} /></button>
    </div>
  );
  const dragProps = {
    'data-report-element-id': element.id,
    draggable: false,
    onPointerDown: event => {
      if (event.button !== 0 || event.target.closest?.('button')) return;
      if (event.target.closest?.('[data-report-inline-edit]')) return;
      event.preventDefault();
      event.stopPropagation();
      onSelect?.(event);
      const parent = event.currentTarget.parentElement;
      const rect = parent?.getBoundingClientRect();
      if (!rect) return;
      dragState.current = {
        rect,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startX: layout.x,
        startY: layout.y,
        width: layout.width,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    onPointerMove: event => {
      const state = dragState.current;
      if (!state) return;
      event.preventDefault();
      event.stopPropagation();
      onMoveLayout?.({
        x: state.startX + state.width / 2 + ((event.clientX - state.startClientX) / state.rect.width) * 100,
        y: state.startY + (event.clientY - state.startClientY),
      });
    },
    onPointerUp: event => {
      if (!dragState.current) return;
      event.stopPropagation();
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      dragState.current = null;
    },
    onPointerCancel: event => {
      if (!dragState.current) return;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      dragState.current = null;
    },
    onDragStart: event => {
      event.stopPropagation();
      event.dataTransfer.setData('text/report-element-id', element.id);
    },
    onDragOver: event => {
      event.stopPropagation();
      event.preventDefault();
    },
    onDrop: event => {
      event.stopPropagation();
      event.preventDefault();
      const draggedElementId = event.dataTransfer.getData('text/report-element-id');
      if (draggedElementId) onDropElement?.(draggedElementId);
    },
  };

  if (type === 'heading') {
    return (
      <div onClick={onSelect} style={chrome} {...dragProps}>
        {handle}
        <h3 style={{ margin: 0, paddingRight: 70, color: theme.textColor, fontFamily: theme.headingFontFamily, fontSize: 16, lineHeight: 1.25, fontWeight: 850 }}>
          <InlineEditableText
            value={element.content}
            fallback="Section heading"
            onCommit={value => onEditField?.('content', value)}
          />
        </h3>
      </div>
    );
  }

  if (type === 'metric') {
    return (
      <div onClick={onSelect} style={{ ...chrome, borderColor: selected ? theme.accentColor : hexToRgba(metricColor, 0.18), padding: 12, background: selected ? hexToRgba(theme.accentColor, 0.06) : hexToRgba(metricColor, 0.05) }} {...dragProps}>
        {handle}
        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', fontWeight: 800 }}>
          <InlineEditableText value={element.label} fallback="Metric" onCommit={value => onEditField?.('label', value)} />
        </div>
        <div style={{ marginTop: 7, fontSize: 24, color: metricColor, fontWeight: 900, lineHeight: 1 }}>
          <InlineEditableText value={element.content} fallback="{{security.score}}" onCommit={value => onEditField?.('content', value)} />
        </div>
      </div>
    );
  }

  if (type === 'chart') {
    return (
      <div onClick={onSelect} style={{ ...chrome, padding: 12, borderColor: selected ? theme.accentColor : C.b1 }} {...dragProps}>
        {handle}
        <ReportChartPreview item={element} theme={theme} compact onEditTitle={value => onEditField?.('label', value)} />
      </div>
    );
  }

  if (type === 'findings') {
    const findings = reportQueryFindings(element.query || 'critical cloud').slice(0, 4);
    return (
      <div onClick={onSelect} style={{ ...chrome, padding: 12, borderColor: selected ? theme.accentColor : C.b1 }} {...dragProps}>
        {handle}
        <div style={{ fontSize: 11, color: theme.textColor, fontWeight: 800, marginBottom: 10 }}>
          <InlineEditableText value={element.label} fallback="Findings" onCommit={value => onEditField?.('label', value)} />
        </div>
        {findings.map(finding => (
          <div key={finding.title} style={{ display: 'grid', gridTemplateColumns: '76px minmax(0, 1fr)', gap: 10, padding: '7px 0', borderTop: `1px solid ${C.b1}` }}>
            <span style={{ color: finding.severity === 'Critical' ? '#dc2626' : '#f97316', fontSize: 10, fontWeight: 900 }}>{finding.severity}</span>
            <span style={{ fontSize: 11, color: theme.textColor, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{finding.title}</span>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'findingrepeater') {
    const findings = reportQueryFindings(element.query || 'critical high vulnerability').slice(0, repeaterLimit(element, 3));
    const template = element.content || '{{finding.title}}\nSeverity: {{finding.severity}}\nAge: {{finding.ageDays}} days';
    return (
      <div onClick={onSelect} style={{ ...chrome, padding: 12, borderColor: selected ? theme.accentColor : C.b1 }} {...dragProps}>
        {handle}
        <div style={{ fontSize: 11, color: theme.textColor, fontWeight: 800, marginBottom: 10 }}>
          <InlineEditableText value={element.label} fallback="Finding Repeater" onCommit={value => onEditField?.('label', value)} />
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          {findings.map(finding => (
            <div key={finding.title} style={{ border: `1px solid ${hexToRgba(theme.accentColor, 0.22)}`, borderRadius: 7, padding: 9, background: hexToRgba(theme.accentColor, 0.05) }}>
              {applyFindingTokens(template, finding).split('\n').filter(Boolean).slice(0, 3).map((line, lineIndex) => (
                <div key={`${finding.title}-${lineIndex}`} style={{ fontSize: lineIndex === 0 ? 11 : 9, color: lineIndex === 0 ? theme.textColor : C.t2, fontWeight: lineIndex === 0 ? 850 : 650, fontFamily: lineIndex === 0 ? FONTS.sans : FONTS.mono, lineHeight: 1.4, marginTop: lineIndex === 0 ? 0 : 4 }}>{line}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'tablerepeater') {
    const findings = reportQueryFindings(element.query || 'critical high vulnerability').slice(0, repeaterLimit(element, 4));
    const template = parseRepeaterTableTemplate(element.content);
    return (
      <div onClick={onSelect} style={{ ...chrome, padding: 12, borderColor: selected ? theme.accentColor : C.b1 }} {...dragProps}>
        {handle}
        <div style={{ fontSize: 11, color: theme.textColor, fontWeight: 800, marginBottom: 8 }}>
          <InlineEditableText value={element.label} fallback="Table Repeater" onCommit={value => onEditField?.('label', value)} />
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {findings.map(finding => (
              <tr key={finding.title} style={{ borderTop: `1px solid ${C.b1}` }}>
                {template.cells.slice(0, 3).map((cell, cellIndex) => (
                  <td key={`${finding.title}-${cellIndex}`} style={{ padding: '7px 4px', fontSize: 9, color: cellIndex === 0 ? (finding.severity === 'Critical' ? '#dc2626' : '#f97316') : theme.textColor, fontWeight: cellIndex === 0 ? 900 : 650 }}>
                    {applyFindingTokens(cell, finding)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'image') {
    return (
      <div onClick={onSelect} style={{ ...chrome, padding: 12, borderColor: selected ? theme.accentColor : C.b1 }} {...dragProps}>
        {handle}
        {element.imageUrl ? (
          <img src={element.imageUrl} alt={element.altText || element.label || 'Report image'} style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 7, border: `1px solid ${C.b1}` }} />
        ) : (
          <div style={{ minHeight: 120, borderRadius: 7, border: `1px solid ${hexToRgba(theme.primaryColor, 0.20)}`, background: `linear-gradient(135deg, ${hexToRgba(theme.primaryColor, 0.10)}, ${hexToRgba(theme.accentColor, 0.18)})`, display: 'grid', placeItems: 'center', color: theme.primaryColor }}>
            <Image size={26} />
          </div>
        )}
        {element.content && (
          <p style={{ margin: '9px 0 0', fontSize: 11, lineHeight: 1.55, color: hexToRgba(theme.textColor, 0.76) }}>
            <InlineEditableText value={element.content} multiline onCommit={value => onEditField?.('content', value)} />
          </p>
        )}
      </div>
    );
  }

  if (type === 'table') {
    return (
      <div onClick={onSelect} style={{ ...chrome, padding: 12, borderColor: selected ? theme.accentColor : C.b1 }} {...dragProps}>
        {handle}
        <div style={{ fontSize: 11, color: theme.textColor, fontWeight: 800, marginBottom: 8 }}>
          <InlineEditableText value={element.label} fallback="Table" onCommit={value => onEditField?.('label', value)} />
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {[['Security score', REPORT_DESIGNER_SAMPLE.securityScore], ['Open findings', REPORT_DESIGNER_SAMPLE.totalOpen]].map(([label, value]) => (
              <tr key={label} style={{ borderTop: `1px solid ${C.b1}` }}>
                <td style={{ padding: '7px 0', fontSize: 11, color: theme.textColor }}>{label}</td>
                <td style={{ padding: '7px 0', fontSize: 11, color: C.t2, fontFamily: FONTS.mono, textAlign: 'right' }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'divider') {
    return (
      <div onClick={onSelect} style={{ ...chrome, padding: '13px 0' }} {...dragProps}>
        {handle}
        <div style={{ height: 1, background: C.b1 }} />
      </div>
    );
  }

  return (
    <div onClick={onSelect} style={chrome} {...dragProps}>
      {handle}
      <p style={{ margin: 0, paddingRight: 70, color: hexToRgba(theme.textColor, 0.82), fontSize: 12, lineHeight: 1.65 }}>
        <InlineEditableText value={element.content} fallback="Paragraph text" multiline onCommit={value => onEditField?.('content', value)} />
      </p>
    </div>
  );
}

function PreviewBlockKicker({ block, index, theme }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ width: 21, height: 21, borderRadius: 5, background: hexToRgba(theme.accentColor, 0.13), color: theme.accentColor, display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 900, fontFamily: FONTS.mono }}>{index + 1}</span>
      <span style={{ color: hexToRgba(theme.textColor, 0.52), fontSize: 9, fontFamily: FONTS.mono, fontWeight: 800, letterSpacing: 0, textTransform: 'uppercase' }}>{block.type || 'block'}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   GENERAL TAB (original Settings content)
   ═══════════════════════════════════════════════════════════════ */
function GeneralTab() {
  const { data: MSP_CLIENTS, loading: clientsLoading } = useMspClients();
  const [branding, setBranding] = useState({ company: '', email: '', color: '#ef4444' });
  const [portal, setPortal] = useState({ domain: '', custom: '' });
  const [toggles, setToggles] = useState({ whiteLabel: true, customDomain: true, removePoweredBy: false });

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 18 }}>Partner Branding</div>
          {[['Company Name', 'company'], ['Support Email', 'email'], ['Primary Color', 'color']].map(([l, k]) => (
            <div key={l} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{l}</div>
              <input value={branding[k]} onChange={e => setBranding(p => ({ ...p, [k]: e.target.value }))} style={getInputStyle()} />
            </div>
          ))}

          <div style={{ marginTop: 8, marginBottom: 14, padding: 14, background: C.s2, borderRadius: 7, border: `1px solid ${C.b1}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10 }}>Portal Domain & White-Label</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Portal Domain</div>
              <input value={portal.domain} onChange={e => setPortal(p => ({ ...p, domain: e.target.value }))} style={{ ...getInputStyle(), background: C.s1 }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Custom Domain (Supreme clients)</div>
              <input value={portal.custom} onChange={e => setPortal(p => ({ ...p, custom: e.target.value }))} style={{ ...getInputStyle(), background: C.s1 }} />
              <div style={{ fontSize: 9, color: C.t2, marginTop: 4 }}>CNAME must point to portal.southernsentinelsecurity.com</div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {[{ l: 'White-Label (Baseline+)', k: 'whiteLabel' }, { l: 'Custom Domain (Supreme)', k: 'customDomain' }, { l: 'Remove "Powered By"', k: 'removePoweredBy' }].map(t => (
                <div key={t.l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div onClick={() => setToggles(p => ({ ...p, [t.k]: !p[t.k] }))} style={{ width: 28, height: 14, borderRadius: 7, background: toggles[t.k] ? C.grn : C.t3, cursor: 'pointer', position: 'relative', transition: 'background 0.15s' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: toggles[t.k] ? 16 : 2, transition: 'left 0.15s' }} />
                  </div>
                  <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>{t.l}</span>
                </div>
              ))}
            </div>
          </div>

          <Button variant="pri" disabled style={{ opacity: 0.45 }} title="Use the Branding tab to save appearance changes">Save Branding</Button>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12 }}>Integrations</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6 }}>
              Configure PSA, RMM, and third-party integrations from the <strong style={{ color: C.t1 }}>Integrations</strong> tab.
            </div>
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12 }}>Billing & Plan</div>
            {clientsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[0, 1, 2].map(i => <Skeleton key={i} height={18} />)}
              </div>
            ) : (
              <>
                {[{ l: 'Active Clients', v: `${MSP_CLIENTS.length}` }].map(r => (
                  <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.b2}` }}>
                    <span style={{ fontSize: 11, color: C.t2 }}>{r.l}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.t1, fontFamily: FONTS.mono }}>{r.v}</span>
                  </div>
                ))}
                <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, marginTop: 10, lineHeight: 1.5 }}>
                  Contact your account manager for plan and billing details.
                </div>
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Client Service Levels */}
      <Card style={{ padding: 22, marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 16 }}>Client Service Levels</div>
        <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14 }}>Manage per-client tier assignments and compliance framework entitlements.</div>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
              {['Client', 'Current Tier', 'Industry', 'Compliance Frameworks', 'Actions'].map(h => (
                <th key={h} style={{ textAlign: 'left', fontSize: 9, color: C.t3, fontFamily: FONTS.mono, padding: '6px 10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MSP_CLIENTS.map(c => {
              const id = c.clientId ?? c.id ?? '';
              const name = c.name ?? '';
              const tier = c.tier || 'Baseline';
              const industry = c.industry ?? '';
              const tierColor = tier === 'Supreme' ? '#9333ea' : tier === 'Baseline+' ? '#ca8a04' : '#2563eb';
              const frameworks = tier === 'Supreme' ? 'NIST, CIS, SOC 2, HIPAA, PCI, CMMC' : tier === 'Baseline+' ? 'NIST, CIS, ' + (['Healthcare', 'Legal'].includes(industry) ? 'HIPAA' : 'PCI') : 'NIST';
              return (
                <tr key={id} style={{ borderBottom: `1px solid ${C.b2}` }}>
                  <td style={{ padding: '10px', fontSize: 11, fontWeight: 600, color: C.t1 }}>{name}</td>
                  <td style={{ padding: '10px' }}><span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 4, fontFamily: FONTS.mono, color: tierColor, background: `${tierColor}14`, border: `1px solid ${tierColor}33` }}>{tier}</span></td>
                  <td style={{ padding: '10px', fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{industry}</td>
                  <td style={{ padding: '10px', fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>{frameworks}</td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <Button variant="sec" disabled style={{ opacity: 0.45, fontSize: 9 }} title="Tier management coming soon">Change Tier</Button>
                      <Button variant="ghost" disabled style={{ opacity: 0.45, fontSize: 9 }} title="Framework management coming soon">Frameworks</Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CLOUD ACCESS TAB
   ═══════════════════════════════════════════════════════════════ */
const CLOUD_PROVIDERS = [
  {
    key: 'M365',
    label: 'Microsoft 365',
    sub: 'Graph tenant access',
    icon: Cloud,
  },
  {
    key: 'Azure',
    label: 'Azure CSPM',
    sub: 'ARM subscription scope',
    icon: ShieldCheck,
  },
  {
    key: 'AWS',
    label: 'AWS CSPM',
    sub: 'STS and regional APIs',
    icon: Server,
  },
];

function getClientId(client) {
  return client?.clientId ?? client?.id ?? '';
}

function getClientName(client) {
  return client?.name ?? client?.clientName ?? client?.displayName ?? getClientId(client);
}

function formatTestedAt(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function getCloudResultState(result) {
  if (!result) return { label: 'Not checked', color: C.t2, bg: C.gryBg, Icon: AlertTriangle };
  if (result.valid || result.scanReady) return { label: 'Scan-ready', color: C.grn, bg: C.grnBg, Icon: CheckCircle2 };
  if (result.authenticated) return { label: 'Auth only', color: C.amb, bg: C.ambBg, Icon: AlertTriangle };
  return { label: 'Failed', color: C.red, bg: C.redBg, Icon: XCircle };
}

function cloudResultRows(result) {
  if (!result) return [];
  const rows = [];

  if (result.tenantCount != null) rows.push(['Tenants', result.tenantCount]);
  if (result.accountId) rows.push(['Account', result.accountId]);
  if (result.expectedAccountId && result.expectedAccountId !== result.accountId) rows.push(['Expected', result.expectedAccountId]);
  if (result.authMode) rows.push(['Auth mode', result.authMode]);
  if (result.regionCount != null) rows.push(['Regions', result.regionCount]);

  const tenants = Array.isArray(result.tenants) ? result.tenants : [];
  const subscriptionTotal = tenants.reduce((sum, tenant) => sum + (Number(tenant.subscriptionCount) || 0), 0);
  const domainTotal = tenants.reduce((sum, tenant) => sum + (Number(tenant.domainCount) || 0), 0);
  if (subscriptionTotal > 0) rows.push(['Subscriptions', subscriptionTotal]);
  if (domainTotal > 0) rows.push(['Domains', domainTotal]);

  if (result.testedAt) rows.push(['Tested', formatTestedAt(result.testedAt)]);
  return rows;
}

function CloudAccessTab() {
  const api = useApiClient();
  const { data: clients, loading, error } = useMspClients();
  const [selectedClientId, setSelectedClientId] = useState('');
  const [results, setResults] = useState({});
  const [testing, setTesting] = useState({});

  useEffect(() => {
    if (!selectedClientId && clients.length > 0) {
      setSelectedClientId(getClientId(clients[0]));
    }
  }, [clients, selectedClientId]);

  const selectedClient = clients.find(client => getClientId(client) === selectedClientId);

  const verifyProvider = async provider => {
    if (!selectedClientId) return null;
    setTesting(prev => ({ ...prev, [provider]: true }));
    try {
      const result = await api.post(`/clients/${selectedClientId}/credentials/validate`, { provider });
      setResults(prev => ({
        ...prev,
        [selectedClientId]: {
          ...(prev[selectedClientId] || {}),
          [provider]: result,
        },
      }));
      return result;
    } catch (err) {
      const result = {
        provider,
        valid: false,
        scanReady: false,
        error: err?.message || 'Connection verification failed',
        testedAt: new Date().toISOString(),
      };
      setResults(prev => ({
        ...prev,
        [selectedClientId]: {
          ...(prev[selectedClientId] || {}),
          [provider]: result,
        },
      }));
      return result;
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const verifyAll = async () => {
    for (const provider of CLOUD_PROVIDERS) {
      await verifyProvider(provider.key);
    }
  };

  const clientResults = results[selectedClientId] || {};
  const anyTesting = Object.values(testing).some(Boolean);

  if (loading) {
    return (
      <Card style={{ padding: 22 }}>
        <Skeleton height={26} />
        <div style={{ height: 14 }} />
        <Skeleton height={130} />
      </Card>
    );
  }

  if (error) return <PageError error={error.message || String(error)} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card style={{ padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 280px', maxWidth: 520 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 6 }}>Cloud Connection Verification</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 10 }}>
              {selectedClient ? getClientName(selectedClient) : 'Select a client'}
            </div>
            <select
              value={selectedClientId}
              onChange={event => setSelectedClientId(event.target.value)}
              style={{ ...getInputStyle(), cursor: 'pointer' }}
            >
              {clients.map(client => {
                const id = getClientId(client);
                return <option key={id} value={id}>{getClientName(client)}</option>;
              })}
            </select>
          </div>
          <Button variant="sec" onClick={verifyAll} disabled={!selectedClientId || anyTesting}>
            <RefreshCw size={14} />
            {anyTesting ? 'Verifying...' : 'Verify All'}
          </Button>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
        {CLOUD_PROVIDERS.map(provider => {
          const result = clientResults[provider.key] || (provider.key === 'AWS' ? null : selectedClient ? seededCloudResult(provider.key) : null);
          const state = getCloudResultState(result);
          const ProviderIcon = provider.icon;
          const StateIcon = state.Icon;
          const rows = cloudResultRows(result);
          const isTesting = testing[provider.key];

          return (
            <div
              key={provider.key}
              style={{
                background: C.s1,
                border: `1px solid ${C.b1}`,
                borderRadius: 8,
                padding: 18,
                minHeight: 210,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 14,
              }}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: C.skyBg,
                      border: `1px solid ${C.b1}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: C.sky,
                    }}>
                      <ProviderIcon size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans }}>{provider.label}</div>
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 3 }}>{provider.sub}</div>
                    </div>
                  </div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: state.color,
                    background: state.bg,
                    border: `1px solid ${state.color}33`,
                    borderRadius: 999,
                    padding: '4px 8px',
                    fontSize: 9,
                    fontWeight: 800,
                    fontFamily: FONTS.mono,
                    whiteSpace: 'nowrap',
                  }}>
                    <StateIcon size={13} />
                    {state.label}
                  </div>
                </div>

                <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: rows.length ? '1fr 1fr' : '1fr', gap: 8 }}>
                  {rows.length ? rows.map(([label, value]) => (
                    <div key={`${provider.key}-${label}`} style={{ padding: '8px 10px', background: C.s2, border: `1px solid ${C.b2}`, borderRadius: 6, minWidth: 0 }}>
                      <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, overflowWrap: 'anywhere' }}>{String(value)}</div>
                    </div>
                  )) : (
                    <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6, padding: '8px 0' }}>
                      No verification run for this client.
                    </div>
                  )}
                </div>

                {result?.error && (
                  <div style={{ marginTop: 12, color: state.color, fontSize: 10, lineHeight: 1.5, fontFamily: FONTS.mono, overflowWrap: 'anywhere' }}>
                    {result.error}
                  </div>
                )}
              </div>

              <Button variant="sec" onClick={() => verifyProvider(provider.key)} disabled={!selectedClientId || isTesting}>
                <RefreshCw size={14} />
                {isTesting ? 'Verifying...' : `Verify ${provider.key}`}
              </Button>
            </div>
          );
        })}
      </div>

      {selectedClientId && (
        <AwsOnboardingPanel
          clientId={selectedClientId}
          compact
          onValidationResult={(result) => {
            setResults(prev => ({
              ...prev,
              [selectedClientId]: {
                ...(prev[selectedClientId] || {}),
                AWS: result,
              },
            }));
          }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   INTEGRATIONS TAB
   ═══════════════════════════════════════════════════════════════ */
function IntegrationsTab() {
  const api = useApiClient();

  const [intLoading, setIntLoading] = useState(true);
  const [catalog, setCatalog] = useState(FALLBACK_INTEGRATION_CATALOG);
  const [catalogSource, setCatalogSource] = useState('fallback');
  const [psaConfig, setPsaConfig] = useState({ configured: false });
  const [rmmConfig, setRmmConfig] = useState({ configured: false });
  const [psaHealth, setPsaHealth] = useState({ endpointAvailable: false });
  const [rmmHealth, setRmmHealth] = useState({ endpointAvailable: false });
  const [integrationGaps, setIntegrationGaps] = useState([]);

  // PSA state
  const [psaType, setPsaType] = useState('');
  const [psaEnabled, setPsaEnabled] = useState(false);
  const [psaCreds, setPsaCreds] = useState({});
  const [psaSettings, setPsaSettings] = useState({});
  const [psaStatus, setPsaStatus] = useState(null);
  const [psaSaving, setPsaSaving] = useState(false);
  const [psaTesting, setPsaTesting] = useState(false);
  const [psaMsg, setPsaMsg] = useState(null);
  const [psaOpen, setPsaOpen] = useState(true);

  // RMM state
  const [rmmType, setRmmType] = useState('');
  const [rmmEnabled, setRmmEnabled] = useState(false);
  const [rmmCreds, setRmmCreds] = useState({});
  const [rmmSettings, setRmmSettings] = useState({});
  const [rmmStatus, setRmmStatus] = useState(null);
  const [rmmSaving, setRmmSaving] = useState(false);
  const [rmmTesting, setRmmTesting] = useState(false);
  const [rmmMsg, setRmmMsg] = useState(null);
  const [rmmOpen, setRmmOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const gaps = [];

      const [catalogResult, psaConfigResult, rmmConfigResult, psaHealthResult, rmmHealthResult] = await Promise.allSettled([
        api.get('/msp/integrations/catalog'),
        api.get('/msp/integrations/psa/config'),
        api.get('/msp/integrations/rmm/config'),
        api.get('/msp/integrations/psa/health'),
        api.get('/msp/integrations/rmm/health'),
      ]);

      if (cancelled) return;

      if (catalogResult.status === 'fulfilled' && !isProblemPayload(catalogResult.value)) {
        setCatalog(normalizeIntegrationCatalog(catalogResult.value));
        setCatalogSource('api');
      } else {
        setCatalog(FALLBACK_INTEGRATION_CATALOG);
        setCatalogSource('fallback');
        gaps.push('Catalog endpoint is unavailable; showing the normalized backend contract from the portal fallback.');
      }

      if (psaConfigResult.status === 'fulfilled' && !isProblemPayload(psaConfigResult.value)) {
        const data = psaConfigResult.value || { configured: false };
        setPsaConfig(data);
        if (data?.configured) {
          setPsaType(data.psaTypeName || '');
          setPsaEnabled(data.isEnabled);
          setPsaSettings(data.settings || {});
          setPsaStatus('configured');
        } else {
          setPsaStatus('not_configured');
        }
      } else {
        setPsaConfig({ configured: false });
        setPsaStatus('not_configured');
        gaps.push('PSA config endpoint is unavailable.');
      }

      if (rmmConfigResult.status === 'fulfilled' && !isProblemPayload(rmmConfigResult.value)) {
        const data = rmmConfigResult.value || { configured: false };
        setRmmConfig(data);
        if (data?.configured) {
          setRmmType(data.rmmTypeName || '');
          setRmmEnabled(data.isEnabled);
          setRmmSettings(data.settings || {});
          setRmmStatus('configured');
        } else {
          setRmmStatus('not_configured');
        }
      } else {
        setRmmConfig({ configured: false });
        setRmmStatus('not_configured');
        gaps.push('RMM config endpoint is unavailable.');
      }

      if (psaHealthResult.status === 'fulfilled' && !isProblemPayload(psaHealthResult.value)) {
        setPsaHealth({ ...psaHealthResult.value, endpointAvailable: true });
      } else {
        setPsaHealth({ endpointAvailable: false });
        gaps.push('PSA health endpoint is unavailable.');
      }

      if (rmmHealthResult.status === 'fulfilled' && !isProblemPayload(rmmHealthResult.value)) {
        setRmmHealth({ ...rmmHealthResult.value, endpointAvailable: true });
      } else {
        setRmmHealth({ endpointAvailable: false });
        gaps.push('RMM health endpoint is unavailable.');
      }

      setIntegrationGaps(gaps);
      setIntLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [api]);

  const handlePsaTest = async () => {
    setPsaTesting(true); setPsaMsg(null);
    try {
      await api.post('/msp/integrations/psa/test', { psaType, credentials: psaCreds, settings: psaSettings });
      setPsaMsg({ ok: true, text: 'Connection successful' });
    } catch (e) {
      setPsaMsg({ ok: false, text: e?.message || 'Connection failed' });
    } finally { setPsaTesting(false); }
  };

  const handlePsaSave = async () => {
    setPsaSaving(true); setPsaMsg(null);
    try {
      await api.put('/msp/integrations/psa/config', { psaType, isEnabled: psaEnabled, credentials: psaCreds, settings: psaSettings });
      setPsaMsg({ ok: true, text: 'PSA configuration saved' });
      setPsaStatus('configured');
      setPsaConfig({ configured: true, psaTypeName: psaType, isEnabled: psaEnabled, settings: psaSettings, updatedAt: new Date().toISOString() });
    } catch (e) {
      setPsaMsg({ ok: false, text: e?.message || 'Save failed' });
    } finally { setPsaSaving(false); }
  };

  const handleRmmTest = async () => {
    setRmmTesting(true); setRmmMsg(null);
    try {
      await api.post('/msp/integrations/rmm/test', { rmmType, credentials: rmmCreds, settings: rmmSettings });
      setRmmMsg({ ok: true, text: 'Connection successful' });
    } catch (e) {
      setRmmMsg({ ok: false, text: e?.message || 'Connection failed' });
    } finally { setRmmTesting(false); }
  };

  const handleRmmSave = async () => {
    setRmmSaving(true); setRmmMsg(null);
    try {
      await api.put('/msp/integrations/rmm/config', { rmmType, isEnabled: rmmEnabled, credentials: rmmCreds, settings: rmmSettings });
      setRmmMsg({ ok: true, text: 'RMM configuration saved' });
      setRmmStatus('configured');
      setRmmConfig({ configured: true, rmmTypeName: rmmType, isEnabled: rmmEnabled, settings: rmmSettings, updatedAt: new Date().toISOString() });
    } catch (e) {
      setRmmMsg({ ok: false, text: e?.message || 'Save failed' });
    } finally { setRmmSaving(false); }
  };

  const toggleStyle = (on) => ({ width: 32, height: 16, borderRadius: 8, background: on ? C.grn : C.t3, cursor: 'pointer', position: 'relative', transition: 'background 0.15s', flexShrink: 0 });
  const toggleKnob = (on) => ({ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: on ? 18 : 2, transition: 'left 0.15s' });
  const sectionHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '12px 0' };
  const msgStyle = (ok) => ({ color: ok ? C.grn : C.red, fontSize: 10, fontFamily: FONTS.mono, marginTop: 8 });

  const renderFields = (fields, values, setValues, isConfigured) =>
    fields.map(f => (
      <div key={f.k} style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{f.l}</div>
        <input
          type={f.type || 'password'}
          placeholder={isConfigured ? '••••••••' : ''}
          value={values[f.k] || ''}
          onChange={e => setValues(prev => ({ ...prev, [f.k]: e.target.value }))}
          style={getInputStyle()}
        />
      </div>
    ));

  const renderSettingFields = (fields, values, setValues) =>
    fields.map(f => (
      <div key={f.k} style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{f.l}</div>
        <input
          type={f.type || 'text'}
          value={values[f.k] || ''}
          onChange={e => setValues(prev => ({ ...prev, [f.k]: e.target.value }))}
          style={getInputStyle()}
        />
      </div>
    ));

  const fmtDate = (value) => value ? new Date(value).toLocaleString() : 'Not reported';
  const statusColor = (row) => {
    if (!row.isConfigured) return C.t3;
    if (!row.endpointAvailable) return C.amb;
    if (row.hasProvider === false || row.lastSyncStatus === 'Failed' || row.lastSyncStatus === 'ProviderUnavailable') return C.red;
    if (!row.isEnabled || row.lastSyncStatus === 'Disabled') return C.amb;
    return C.grn;
  };
  const statusLabel = (row) => {
    if (!row.isConfigured) return 'Not configured';
    if (!row.isEnabled) return 'Configured, disabled';
    if (row.hasProvider === false) return 'Provider unavailable';
    if (!row.endpointAvailable) return 'Health pending';
    return 'Enabled';
  };

  const CapabilityTags = ({ row }) => (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {row.supportsCustomers && <Tag label="Customers" color={C.sky} />}
      {row.supportsTickets && <Tag label="Tickets" color={C.pur} />}
      {row.supportsDevices && <Tag label="Devices" color={C.grn} />}
    </div>
  );

  const HealthSummaryCard = ({ label, value, detail, tone }) => (
    <div style={{ padding: 14, borderRadius: 7, background: C.s2, border: `1px solid ${C.b1}` }}>
      <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, color: tone || C.t1, fontWeight: 800, fontFamily: FONTS.sans }}>{value}</div>
      <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 4 }}>{detail}</div>
    </div>
  );

  const CatalogTable = ({ title, rows }) => (
    <Card style={{ padding: 22, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans }}>{title}</div>
          <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>Catalog, config, provider, and sync readiness</div>
        </div>
        <Tag label={`${rows.length} providers`} color={C.sky} />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
              {['Provider', 'Capabilities', 'Config', 'Provider', 'Sync', 'Updated'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '7px 8px', fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key} style={{ borderBottom: `1px solid ${C.b2}` }}>
                <td style={{ padding: '9px 8px' }}>
                  <div style={{ fontSize: 12, color: C.t1, fontWeight: 700, fontFamily: FONTS.sans }}>{row.displayName}</div>
                  <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 2 }}>{row.key}</div>
                </td>
                <td style={{ padding: '9px 8px' }}><CapabilityTags row={row} /></td>
                <td style={{ padding: '9px 8px' }}><Tag label={statusLabel(row)} color={statusColor(row)} /></td>
                <td style={{ padding: '9px 8px', fontSize: 10, color: row.hasProvider === false ? C.red : C.t2, fontFamily: FONTS.mono }}>
                  {row.hasProvider === null ? 'Not reported' : row.hasProvider ? 'Available' : 'Missing'}
                </td>
                <td style={{ padding: '9px 8px', fontSize: 10, color: row.lastSyncStatus === 'Failed' ? C.red : C.t2, fontFamily: FONTS.mono }}>
                  {row.lastSyncStatus}
                  {row.lastSyncCompletedAt && <div style={{ color: C.t3, marginTop: 2 }}>{fmtDate(row.lastSyncCompletedAt)}</div>}
                  {row.lastSyncError && <div style={{ color: C.red, marginTop: 2 }}>{row.lastSyncError}</div>}
                </td>
                <td style={{ padding: '9px 8px', fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{fmtDate(row.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  const psaFields = useMemo(() => formDefinitionsForKind(INTEGRATION_KIND.PSA, catalog), [catalog]);
  const rmmFields = useMemo(() => formDefinitionsForKind(INTEGRATION_KIND.RMM, catalog), [catalog]);
  const integrationRows = useMemo(
    () => buildIntegrationRows(catalog, { psa: psaConfig, rmm: rmmConfig }, { psa: psaHealth, rmm: rmmHealth }),
    [catalog, psaConfig, rmmConfig, psaHealth, rmmHealth],
  );
  const psaRows = integrationRows.filter(row => row.kind === INTEGRATION_KIND.PSA);
  const rmmRows = integrationRows.filter(row => row.kind === INTEGRATION_KIND.RMM);
  const configuredCount = integrationRows.filter(row => row.isConfigured).length;
  const enabledCount = integrationRows.filter(row => row.isEnabled).length;
  const providerBackedCount = integrationRows.filter(row => row.hasProvider === true).length;
  const psaDef = psaFields[psaType];
  const rmmDef = rmmFields[rmmType];

  if (intLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {[0, 1].map(i => (
        <Card key={i} style={{ padding: 22 }}>
          <Skeleton width="30%" height={16} style={{ marginBottom: 10 }} />
          <Skeleton width="50%" height={12} style={{ marginBottom: 14 }} />
          <Skeleton height={36} />
        </Card>
      ))}
    </div>
  );

  return (
    <>
      <Card style={{ padding: 22, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans }}>Integration Health</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>
              {catalogSource === 'api' ? 'Loaded from backend catalog endpoint' : 'Backend catalog endpoint pending; using portal fallback contract'}
            </div>
          </div>
          <Tag label={catalogSource === 'api' ? 'Live catalog' : 'Graceful fallback'} color={catalogSource === 'api' ? C.grn : C.amb} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 10, marginBottom: integrationGaps.length ? 14 : 0 }}>
          <HealthSummaryCard label="Catalog" value={catalog.length} detail="Normalized providers" tone={C.sky} />
          <HealthSummaryCard label="Configured" value={configuredCount} detail="PSA/RMM active configs" tone={configuredCount ? C.grn : C.t2} />
          <HealthSummaryCard label="Enabled" value={enabledCount} detail="Ready for workflows" tone={enabledCount ? C.grn : C.t2} />
          <HealthSummaryCard label="Provider Health" value={providerBackedCount || 'N/A'} detail="Reported by health endpoints" tone={providerBackedCount ? C.grn : C.amb} />
        </div>

        {integrationGaps.length > 0 && (
          <div style={{ padding: '10px 12px', borderRadius: 7, background: `${C.amb}12`, border: `1px solid ${C.amb}33` }}>
            {integrationGaps.map(gap => (
              <div key={gap} style={{ fontSize: 10, color: C.amb, fontFamily: FONTS.mono, lineHeight: 1.6 }}>{gap}</div>
            ))}
          </div>
        )}
      </Card>

      <CatalogTable title="PSA Catalog" rows={psaRows} />
      <CatalogTable title="RMM Catalog" rows={rmmRows} />

      {/* PSA Section */}
      <Card style={{ padding: 22, marginBottom: 14 }}>
        <div style={sectionHead} onClick={() => setPsaOpen(!psaOpen)}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans }}>PSA Integration</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>
              {psaStatus === 'configured' ? `${providerLabel(psaType, catalog)} configured` : 'Not configured'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {psaStatus === 'configured' && <Tag label="Configured" color={C.grn} />}
            {psaStatus === 'not_configured' && <Tag label="Not Configured" color={C.t3} />}
            <span style={{ fontSize: 12, color: C.t2 }}>{psaOpen ? '▾' : '▸'}</span>
          </div>
        </div>

        {psaOpen && (
          <div style={{ paddingTop: 8 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>PSA Provider</div>
              <select value={psaType} onChange={e => { setPsaType(e.target.value); setPsaCreds({}); setPsaSettings({}); }} style={{ ...getInputStyle(), cursor: 'pointer' }}>
                <option value="">Select PSA provider...</option>
                {psaRows.map(row => (
                  <option key={row.key} value={providerValue(row)}>{row.displayName}</option>
                ))}
              </select>
            </div>

            {psaDef && (
              <>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, fontFamily: FONTS.mono, marginBottom: 8 }}>Settings</div>
                {renderSettingFields(psaDef.settings, psaSettings, setPsaSettings)}
                <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, fontFamily: FONTS.mono, marginBottom: 8, marginTop: 12 }}>Credentials</div>
                {renderFields(psaDef.creds, psaCreds, setPsaCreds, psaStatus === 'configured')}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, marginBottom: 12 }}>
                  <div onClick={() => setPsaEnabled(!psaEnabled)} style={toggleStyle(psaEnabled)}>
                    <div style={toggleKnob(psaEnabled)} />
                  </div>
                  <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Enable Integration</span>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="sec" style={{ fontSize: 10 }} onClick={handlePsaTest} disabled={psaTesting}>
                    {psaTesting ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button variant="pri" style={{ fontSize: 10 }} onClick={handlePsaSave} disabled={psaSaving}>
                    {psaSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
                {psaMsg && <div style={msgStyle(psaMsg.ok)}>{psaMsg.text}</div>}
              </>
            )}
          </div>
        )}
      </Card>

      {/* RMM Section */}
      <Card style={{ padding: 22 }}>
        <div style={sectionHead} onClick={() => setRmmOpen(!rmmOpen)}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans }}>RMM Integration</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>
              {rmmStatus === 'configured' ? `${providerLabel(rmmType, catalog)} configured` : 'Not configured'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {rmmStatus === 'configured' && <Tag label="Configured" color={C.grn} />}
            {rmmStatus === 'not_configured' && <Tag label="Not Configured" color={C.t3} />}
            <span style={{ fontSize: 12, color: C.t2 }}>{rmmOpen ? '▾' : '▸'}</span>
          </div>
        </div>

        {rmmOpen && (
          <div style={{ paddingTop: 8 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>RMM Provider</div>
              <select value={rmmType} onChange={e => { setRmmType(e.target.value); setRmmCreds({}); setRmmSettings({}); }} style={{ ...getInputStyle(), cursor: 'pointer' }}>
                <option value="">Select RMM provider...</option>
                {rmmRows.map(row => (
                  <option key={row.key} value={providerValue(row)}>{row.displayName}</option>
                ))}
              </select>
              <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: C.s2, border: `1px solid ${C.b1}`, fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
                Sync status is read-only here. Use manual sync actions only when backend support is complete.
              </div>
            </div>

            {rmmDef && (
              <>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, fontFamily: FONTS.mono, marginBottom: 8 }}>Settings</div>
                {renderSettingFields(rmmDef.settings, rmmSettings, setRmmSettings)}
                <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, fontFamily: FONTS.mono, marginBottom: 8, marginTop: 12 }}>Credentials</div>
                {renderFields(rmmDef.creds, rmmCreds, setRmmCreds, rmmStatus === 'configured')}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, marginBottom: 12 }}>
                  <div onClick={() => setRmmEnabled(!rmmEnabled)} style={toggleStyle(rmmEnabled)}>
                    <div style={toggleKnob(rmmEnabled)} />
                  </div>
                  <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Enable Integration</span>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="sec" style={{ fontSize: 10 }} onClick={handleRmmTest} disabled={rmmTesting}>
                    {rmmTesting ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button variant="pri" style={{ fontSize: 10 }} onClick={handleRmmSave} disabled={rmmSaving}>
                    {rmmSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
                {rmmMsg && <div style={msgStyle(rmmMsg.ok)}>{rmmMsg.text}</div>}
              </>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   NOTIFICATIONS TAB
   ═══════════════════════════════════════════════════════════════ */
function NotificationsTab() {
  const { profile } = useUser();
  const api = useApiClient();
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  // Notification events
  const [criticalFinding, setCriticalFinding] = useState(true);
  const [scoreDrop, setScoreDrop] = useState(true);
  const [scoreThreshold, setScoreThreshold] = useState(70);
  const [scanCompleted, setScanCompleted] = useState(false);
  const [scanFailed, setScanFailed] = useState(true);
  const [newClientOnboarded, setNewClientOnboarded] = useState(false);

  // Delivery method
  const [email, setEmail] = useState(profile?.email ?? '');
  const [notifyAdmin, setNotifyAdmin] = useState(true);
  const [notifyClientContacts, setNotifyClientContacts] = useState(false);

  const Toggle = ({ value, onChange }) => (
    <div onClick={() => onChange(!value)} style={{
      width: 32, height: 16, borderRadius: 8, background: value ? C.grn : C.t3,
      cursor: 'pointer', position: 'relative', transition: 'background 0.15s', flexShrink: 0,
    }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: value ? 18 : 2, transition: 'left 0.15s' }} />
    </div>
  );

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      await api.put('/msp/notifications/preferences', {
        events: {
          criticalFinding, scoreDrop, scoreThreshold,
          scanCompleted, scanFailed, newClientOnboarded,
        },
        delivery: { email, notifyAdmin, notifyClientContacts },
      });
      setSaveResult({ ok: true, msg: 'Preferences saved.' });
    } catch (err) {
      if (err.message?.includes('404')) {
        setSaveResult({ ok: true, msg: 'Preferences saved. Email notification delivery will be enabled in a future update.' });
      } else {
        setSaveResult({ ok: false, msg: err.message });
      }
    } finally {
      setSaving(false);
    }
  };

  const events = [
    { l: 'Critical finding discovered', v: criticalFinding, s: setCriticalFinding },
    { l: 'Score drops below threshold', v: scoreDrop, s: setScoreDrop, extra: true },
    { l: 'Scan completed', v: scanCompleted, s: setScanCompleted },
    { l: 'Scan failed', v: scanFailed, s: setScanFailed },
    { l: 'New client onboarded', v: newClientOnboarded, s: setNewClientOnboarded },
  ];

  return (
    <>
      <Card style={{ padding: 22, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 4 }}>Alert Triggers</div>
        <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14 }}>Choose which events generate alerts for your MSP</div>

        {events.map(e => (
          <div key={e.l}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.b2}` }}>
              <Toggle value={e.v} onChange={e.s} />
              <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.sans }}>{e.l}</span>
            </div>
            {e.extra && e.v && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0 8px 42px' }}>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Threshold:</span>
                <input type="number" value={scoreThreshold} onChange={ev => setScoreThreshold(Number(ev.target.value))}
                  style={{ ...getInputStyle(), width: 60, padding: '4px 8px' }} min={0} max={100} />
              </div>
            )}
          </div>
        ))}
      </Card>

      <Card style={{ padding: 22, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 4 }}>Delivery Channels</div>
        <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14 }}>Destinations where triggered alerts are sent</div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>EMAIL ADDRESS</div>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder={profile?.email || 'your@email.com'} style={getInputStyle()} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.b2}` }}>
          <Toggle value={notifyAdmin} onChange={setNotifyAdmin} />
          <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.sans }}>Notify MSP admin</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
          <Toggle value={notifyClientContacts} onChange={setNotifyClientContacts} />
          <div>
            <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.sans }}>Notify client contacts</span>
            <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 2 }}>Requires client contact email on file</div>
          </div>
        </div>
      </Card>

      {saveResult && (
        <div style={{
          marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono,
          background: saveResult.ok ? `${C.grn}12` : C.redBg,
          border: `1px solid ${saveResult.ok ? C.grn : C.red}33`,
          color: saveResult.ok ? C.grn : C.red,
        }}>{saveResult.msg}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="pri" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   DOMAIN & SSO TAB
   ═══════════════════════════════════════════════════════════════ */
function DomainSsoTab() {
  const api = useApiClient();
  const [customDomain, setCustomDomain] = useState('');
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [oidcTenantId, setOidcTenantId] = useState('');
  const [oidcAuthority, setOidcAuthority] = useState('');
  const [oidcClientId, setOidcClientId] = useState('');
  const [oidcScopes, setOidcScopes] = useState('openid profile email');
  const [oidcAllowedDomains, setOidcAllowedDomains] = useState('');
  const [oidcAllowJitProvisioning, setOidcAllowJitProvisioning] = useState(false);
  const [oidcDefaultRole, setOidcDefaultRole] = useState('MspReader');
  const [oidcClientSecret, setOidcClientSecret] = useState('');
  const [oidcClientSecretConfigured, setOidcClientSecretConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get('/msp/branding');
        if (cancelled) return;
        if (data) {
          setCustomDomain(data.customDomain || '');
          setOidcEnabled(Boolean(data.oidcEnabled));
          setOidcTenantId(data.oidcTenantId || '');
          setOidcAuthority(data.oidcAuthority || '');
          setOidcClientId(data.oidcClientId || '');
          setOidcScopes(data.oidcScopes || 'openid profile email');
          setOidcAllowedDomains(data.oidcAllowedDomains || '');
          setOidcAllowJitProvisioning(Boolean(data.oidcAllowJitProvisioning));
          setOidcDefaultRole(data.oidcDefaultRole || 'MspReader');
          setOidcClientSecretConfigured(Boolean(data.oidcClientSecretConfigured));
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || 'Failed to load domain settings');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSaveDomain = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const normalizedDomain = customDomain.trim().toLowerCase();
      await api.put('/msp/branding/domain', { customDomain: normalizedDomain });
      setCustomDomain(normalizedDomain);
      setSaveMsg({ ok: true, text: 'Custom domain saved.' });
    } catch (err) { setSaveMsg({ ok: false, text: err.message }); }
    finally { setSaving(false); }
  };

  const validateSso = () => {
    if (!oidcEnabled) return null;
    if (!oidcTenantId.trim()) return 'Tenant ID is required before enabling SSO.';
    if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(oidcTenantId.trim())) {
      return 'Tenant ID must be an Entra tenant GUID.';
    }
    if (!oidcAuthority.trim().startsWith('https://')) return 'Authority URL must start with https://.';
    if (!oidcClientId.trim()) return 'Client ID is required before enabling SSO.';
    return null;
  };

  const handleSaveSso = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const validationError = validateSso();
      if (validationError) {
        setSaveMsg({ ok: false, text: validationError });
        return;
      }
      const payload = {
        oidcEnabled,
        oidcTenantId: oidcTenantId.trim(),
        oidcAuthority: oidcAuthority.trim(),
        oidcClientId: oidcClientId.trim(),
        oidcScopes: oidcScopes.trim() || 'openid profile email',
        oidcAllowedDomains: oidcAllowedDomains.trim().toLowerCase(),
        oidcAllowJitProvisioning,
        oidcDefaultRole,
      };
      if (oidcClientSecret.trim()) payload.oidcClientSecret = oidcClientSecret.trim();
      const saved = await api.put('/msp/branding/oidc', payload);
      setOidcClientSecret('');
      setOidcClientSecretConfigured(Boolean(saved?.oidcClientSecretConfigured || payload.oidcClientSecret));
      setOidcEnabled(Boolean(saved?.oidcEnabled ?? oidcEnabled));
      setSaveMsg({ ok: true, text: 'SSO settings saved.' });
    } catch (err) { setSaveMsg({ ok: false, text: err.message }); }
    finally { setSaving(false); }
  };

  const fieldLabelStyle = { fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 };
  const helperStyle = { fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 4, lineHeight: 1.5 };
  const checkboxStyle = { width: 16, height: 16, accentColor: C.sky, flexShrink: 0 };

  if (loading) return (
    <Card style={{ padding: 22 }}>
      <Skeleton width="40%" height={16} style={{ marginBottom: 14 }} />
      <Skeleton height={14} style={{ marginBottom: 10 }} />
      <Skeleton width="70%" height={36} style={{ marginBottom: 14 }} />
      <Skeleton height={14} style={{ marginBottom: 10 }} />
      <Skeleton width="70%" height={36} />
    </Card>
  );

  return (
    <>
      {loadError && <PageError error={loadError} />}
      {saveMsg && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: saveMsg.ok ? `${C.grn}12` : `${C.red}12`, border: `1px solid ${saveMsg.ok ? C.grn : C.red}33`, color: saveMsg.ok ? C.grn : C.red }}>
          {saveMsg.text}
        </div>
      )}
      <Card style={{ padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 16 }}>Portal Domain</div>
        <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14 }}>Configure your portal's custom domain for white-label access.</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Custom Domain</div>
          <input value={customDomain} onChange={e => setCustomDomain(e.target.value)} placeholder="portal.yourcompany.com" style={getInputStyle()} />
          <div style={{ fontSize: 9, color: C.t2, marginTop: 4 }}>Set a CNAME record pointing to portal.southernsentinelsecurity.com</div>
        </div>
        <Button variant="pri" onClick={handleSaveDomain} disabled={saving}>{saving ? 'Saving...' : 'Save Domain'}</Button>
      </Card>

      <Card style={{ padding: 22, marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 4 }}>SSO Configuration (OIDC)</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Each MSP can bind this portal to its own Microsoft Entra tenant.</div>
          </div>
          <Tag label={oidcEnabled ? 'Enabled' : 'Disabled'} color={oidcEnabled ? C.grn : C.t3} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={oidcEnabled} onChange={e => setOidcEnabled(e.target.checked)} style={checkboxStyle} />
          <span style={{ fontSize: 12, color: C.t1, fontFamily: FONTS.sans, fontWeight: 700 }}>Enable SSO for this MSP</span>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={fieldLabelStyle}>Entra Tenant ID</div>
            <input value={oidcTenantId} onChange={e => setOidcTenantId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" style={getInputStyle()} />
          </div>
          <div>
            <div style={fieldLabelStyle}>OIDC Client ID</div>
            <input value={oidcClientId} onChange={e => setOidcClientId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" style={getInputStyle()} />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={fieldLabelStyle}>OIDC Authority URL</div>
          <input value={oidcAuthority} onChange={e => setOidcAuthority(e.target.value)} placeholder="https://login.microsoftonline.com/{tenant-id}/v2.0" style={getInputStyle()} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={fieldLabelStyle}>OIDC Scopes</div>
            <input value={oidcScopes} onChange={e => setOidcScopes(e.target.value)} placeholder="openid profile email" style={getInputStyle()} />
          </div>
          <div>
            <div style={fieldLabelStyle}>Allowed Email Domains</div>
            <input value={oidcAllowedDomains} onChange={e => setOidcAllowedDomains(e.target.value)} placeholder="example.com, example.org" style={getInputStyle()} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={fieldLabelStyle}>OIDC Client Secret</div>
            <input
              value={oidcClientSecret}
              onChange={e => setOidcClientSecret(e.target.value)}
              placeholder={oidcClientSecretConfigured ? 'Secret already stored' : 'Optional client secret'}
              type="password"
              autoComplete="new-password"
              style={getInputStyle()}
            />
            <div style={helperStyle}>
              {oidcClientSecretConfigured ? 'Leave blank to keep the stored secret.' : 'Required when the Entra app is configured as a confidential web app.'}
            </div>
          </div>
          <div>
            <div style={fieldLabelStyle}>JIT Default Role</div>
            <select value={oidcDefaultRole} onChange={e => setOidcDefaultRole(e.target.value)} style={getInputStyle()}>
              <option value="MspReader">MspReader</option>
              <option value="MspAdmin">MspAdmin</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={oidcAllowJitProvisioning} onChange={e => setOidcAllowJitProvisioning(e.target.checked)} style={checkboxStyle} />
              <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Create users on first SSO sign-in</span>
            </label>
          </div>
        </div>

        <div style={{
          padding: '10px 12px', borderRadius: 7, background: C.s2, border: `1px solid ${C.b1}`,
          fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6, marginBottom: 14,
        }}>
          Register the backend callback URL in Entra: /auth/sso/callback. Existing users are matched by email unless JIT provisioning is enabled.
        </div>

        <Button variant="pri" onClick={handleSaveSso} disabled={saving}>{saving ? 'Saving...' : 'Save SSO Settings'}</Button>
      </Card>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   API KEYS TAB
   ═══════════════════════════════════════════════════════════════ */
function ApiKeysTab() {
  return <ApiKeysPanel />;
}

/* ═══════════════════════════════════════════════════════════════
   MAIN SETTINGS COMPONENT WITH TABS
   ═══════════════════════════════════════════════════════════════ */
export default function MspSettings() {
  const [activeTab, setActiveTab] = useState('Branding');

  return (
    <Shell title="MSP Settings" sub="Partner configuration & branding">
      {/* Tab navigation */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 20,
        borderBottom: `1px solid ${C.b1}`,
      }}>
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              fontSize: 12, fontWeight: 600, fontFamily: FONTS.sans,
              color: activeTab === tab ? C.t1 : C.t2,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === tab ? `2px solid ${C.sky}` : '2px solid transparent',
              transition: 'color 0.15s, border-color 0.15s',
              marginBottom: -1,
            }}
          >{tab}</button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'Branding' && <BrandingTab />}
      {activeTab === 'Report Templates' && <ReportTemplatesTab />}
      {activeTab === 'Domain & SSO' && <DomainSsoTab />}
      {activeTab === 'Cloud Access' && <CloudAccessTab />}
      {activeTab === 'Integrations' && <IntegrationsTab />}
      {activeTab === 'Notifications' && <NotificationsTab />}
      {activeTab === 'API Keys' && <ApiKeysTab />}
    </Shell>
  );
}
